<template>
  <b-modal
    id="create-campaign-modal"
    ref="modal"
    :title="modalTitle"
    size="xl"
    centered
    no-close-on-backdrop
    @hidden="resetForm"
    @shown="onModalShown"
    hide-footer
    modal-class="create-campaign-modal"
  >
    <!-- Loading Overlay (save in progress, or loading a campaign's full data for edit) -->
    <CommonOverlay
      :show="savingDraft || loadingDetails"
      :title="overlayTitle"
      :message="overlayMessage"
    />

    <!-- Queued Confirmation (shown after Send Now completes) -->
    <div v-if="campaignSent" class="sent-confirmation text-center py-4">
      <div style="font-size:3rem;">✅</div>
      <h4 class="mt-3">Your campaign has been queued!</h4>
      <p class="text-muted">
        We're delivering your campaign to
        <strong>{{ sentRecipientCount | formatNumber }}</strong>
        recipient<span v-if="sentRecipientCount !== 1">s</span> in the background.
      </p>
      <p v-if="sentSkippedCount > 0" class="text-muted small">
        <i class="fa fa-info-circle mr-1"></i>
        Skipped <strong>{{ sentSkippedCount | formatNumber }}</strong>
        disconnected number<span v-if="sentSkippedCount !== 1">s</span> that can't receive texts —
        so you weren't charged for them.
      </p>
      <p class="text-muted small">
        You can safely close this window — live delivery status will update on the Campaigns page.
      </p>
      <div class="mt-4">
        <b-button variant="primary" @click="hide">Close</b-button>
      </div>
    </div>

    <!-- Progress Steps -->
    <div v-if="!campaignSent" class="wizard-progress">
      <div 
        v-for="(step, index) in steps" 
        :key="step.key"
        class="progress-step"
        :class="{ 
          'active': currentStep === index,
          'completed': currentStep > index,
          'clickable': currentStep > index
        }"
        @click="goToStep(index)"
      >
        <div class="step-indicator">
          <i v-if="currentStep > index" class="fa fa-check"></i>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <span class="step-label">{{ step.label }}</span>
      </div>
    </div>

    <!-- Step Content -->
    <div v-if="!campaignSent" class="wizard-content">
      <!-- Step 1: Campaign Details -->
      <div v-show="currentStep === 0" class="step-content">
        <h4 class="step-title">Campaign Details</h4>
        <p class="step-subtitle">Give your campaign a name and choose how you want to reach your patients</p>
        
        <!-- Warning if editing campaign with disabled type -->
        <b-alert 
          v-if="isEditing && campaign && !isCampaignTypeValid" 
          variant="warning" 
          show 
          class="mb-3"
        >
          <i class="fa fa-exclamation-triangle mr-2"></i>
          <strong>Warning:</strong> This campaign uses a messaging type that is no longer enabled for your site. 
          You may need to change the campaign type before sending.
        </b-alert>
        
        <b-form @submit.prevent>
          <b-row>
            <b-col md="12">
              <b-form-group label="Campaign Name" label-for="campaign-name">
                <b-form-input
                  id="campaign-name"
                  v-model="form.name"
                  placeholder="e.g., Spring Teeth Whitening Promotion"
                  :state="validateField('name')"
                  class="form-control-lg"
                />
                <b-form-invalid-feedback>Campaign name is required</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <b-form-group label="Description (Optional)" label-for="campaign-description">
                <b-form-textarea
                  id="campaign-description"
                  v-model="form.description"
                  placeholder="Describe your campaign goals and target audience..."
                  rows="3"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <b-form-group label="Campaign Type">
                <div class="campaign-type-selector">
                  <div 
                    class="type-option"
                    :class="{ 
                      'selected': form.type === 'SMS',
                      'disabled': !isSmsEnabled
                    }"
                    @click="isSmsEnabled && (form.type = 'SMS')"
                  >
                    <div class="type-icon sms-icon" :class="{ 'disabled': !isSmsEnabled }">
                      <i class="fa fa-comment"></i>
                    </div>
                    <div class="type-info">
                      <h6>SMS Only</h6>
                      <p>Send text messages to patients' mobile phones</p>
                      <small v-if="!isSmsEnabled" class="text-muted">
                        <i class="fa fa-lock mr-1"></i>{{ smsOutboundOff ? 'Texting is turned off for this office' : 'SMS is not enabled for your site' }}
                      </small>
                      <small v-else-if="formattedSmsNumber" style="color: #15803d; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 20px; padding: 2px 10px; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;">
                        <i class="fa fa-phone" style="font-size: 0.7rem;"></i> {{ formattedSmsNumber }}
                      </small>
                    </div>
                    <div class="type-check" v-if="form.type === 'SMS' && isSmsEnabled">
                      <i class="fa fa-check-circle text-success"></i>
                    </div>
                  </div>

                  <div 
                    class="type-option"
                    :class="{ 
                      'selected': form.type === 'Email',
                      'disabled': !isEmailEnabled
                    }"
                    @click="isEmailEnabled && (form.type = 'Email')"
                  >
                    <div class="type-icon email-icon" :class="{ 'disabled': !isEmailEnabled }">
                      <i class="fa fa-envelope"></i>
                    </div>
                    <div class="type-info">
                      <h6>Email Only</h6>
                      <p>Send emails to patients' inbox</p>
                      <small v-if="!isEmailEnabled" class="text-muted">
                        <i class="fa fa-lock mr-1"></i>Email is not enabled for your site
                      </small>
                    </div>
                    <div class="type-check" v-if="form.type === 'Email' && isEmailEnabled">
                      <i class="fa fa-check-circle text-success"></i>
                    </div>
                  </div>

                  <div 
                    class="type-option"
                    :class="{ 
                      'selected': form.type === 'Both',
                      'disabled': !isSmsEnabled || !isEmailEnabled
                    }"
                    @click="(isSmsEnabled && isEmailEnabled) && (form.type = 'Both')"
                  >
                    <div class="type-icon both-icon" :class="{ 'disabled': !isSmsEnabled || !isEmailEnabled }">
                      <i class="fa fa-comments"></i>
                    </div>
                    <div class="type-info">
                      <h6>SMS + Email</h6>
                      <p>Reach patients through both channels</p>
                      <small v-if="!isSmsEnabled || !isEmailEnabled" class="text-muted">
                        <i class="fa fa-lock mr-1"></i>Requires both SMS and Email to be enabled
                      </small>
                    </div>
                    <div class="type-check" v-if="form.type === 'Both' && isSmsEnabled && isEmailEnabled">
                      <i class="fa fa-check-circle text-success"></i>
                    </div>
                  </div>

                  <div
                    class="type-option"
                    :class="{
                      'selected': form.type === 'ai_call',
                      'disabled': !isAiCallEnabled
                    }"
                    @click="isAiCallEnabled && (form.type = 'ai_call')"
                  >
                    <div class="type-icon ai-call-icon" :class="{ 'disabled': !isAiCallEnabled }">
                      <i class="fa fa-phone-volume"></i>
                    </div>
                    <div class="type-info">
                      <h6>AI Call</h6>
                      <p>An AI voice agent calls patients to book &amp; follow up</p>
                      <small v-if="!isAiCallEnabled" class="text-muted" data-test="ai-call-coming-soon">
                        <i class="fa fa-clock-o mr-1"></i>Coming soon
                      </small>
                    </div>
                    <div class="type-check" v-if="form.type === 'ai_call' && isAiCallEnabled">
                      <i class="fa fa-check-circle text-success"></i>
                    </div>
                  </div>
                </div>
              </b-form-group>

              <!-- AI Call follow-up list type sub-choice -->
              <b-form-group
                v-if="form.type === 'ai_call'"
                label="Follow-up List Type"
                label-for="ai-call-followup-type"
                class="mt-3"
              >
                <b-form-select
                  id="ai-call-followup-type"
                  v-model="form.callFollowupTaskTypeID"
                  :options="aiCallFollowupTypeOptions"
                />
                <small class="form-text text-muted">
                  AI calls count as a real follow-up for the chosen list type.
                </small>
              </b-form-group>

              <!-- AI Call: prioritize which patients get called first -->
              <b-form-group
                v-if="form.type === 'ai_call' && isAiCallEnabled"
                label="Prioritize by"
                label-for="ai-call-priority-preset"
                class="mt-3"
              >
                <b-form-select
                  id="ai-call-priority-preset"
                  v-model="form.priorityPreset"
                  :options="aiCallPriorityPresetOptions"
                />
                <small class="form-text text-muted">
                  Standard dials in default order. Other options rank by who's most worth calling first.
                </small>
              </b-form-group>

              <!-- AI Call: place a REAL test call to a number you choose (never dials patients) -->
              <b-form-group v-if="form.type === 'ai_call'" class="mt-3">
                <label class="d-block mb-1"><strong>Test this call</strong></label>
                <small class="form-text text-muted mb-2 d-block">
                  Places a real AI call to a number you enter (e.g. your own) using a selected
                  patient's script. It does not dial real patients and does not affect the campaign.
                </small>

                <div v-for="(t, i) in testTargets" :key="'tt' + i" class="d-flex align-items-center mb-2" style="gap:.5rem;">
                  <b-form-select v-model="t.patNum" size="sm" :options="testPatientOptions" style="max-width:240px;" />
                  <b-form-input v-model="t.phone" size="sm" type="tel" placeholder="Number to call, e.g. +1 845 702 6176" style="max-width:230px;" />
                  <b-button v-if="testTargets.length > 1" variant="link" size="sm" class="text-danger p-0" @click="removeTestTarget(i)" title="Remove">
                    <i class="fa fa-times"></i>
                  </b-button>
                </div>

                <div class="d-flex align-items-center flex-wrap mt-1" style="gap:.75rem;">
                  <b-button variant="outline-secondary" size="sm" :disabled="testTargets.length >= testCallMax" @click="addTestTarget">
                    <i class="fa fa-plus mr-1"></i>Add number
                  </b-button>
                  <b-button variant="warning" size="sm" :disabled="placingTestCalls || !hasTestTargets" @click="placeTestCalls">
                    <b-spinner v-if="placingTestCalls" small class="mr-1" />
                    <i v-else class="fa fa-phone-volume mr-1"></i>{{ placingTestCalls ? 'Calling…' : 'Place test call' }}
                  </b-button>
                  <small class="text-muted">Up to {{ testCallMax }} per test.</small>
                </div>

                <div v-if="testCallResults.length" class="mt-2">
                  <small v-for="(r, i) in testCallResults" :key="'tr' + i" class="d-block" :class="r.success ? 'text-success' : 'text-danger'">
                    <i :class="r.success ? 'fa fa-check' : 'fa fa-exclamation-triangle'" class="mr-1"></i>{{ r.phone }} — {{ r.message }}
                  </small>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </div>

      <!-- Step 2: Patient List -->
      <div v-show="currentStep === 1" class="step-content">

        <!-- Phase 1: Choose audience method -->
        <template v-if="recipientSubStep === 0">
          <h4 class="step-title">Select Recipients</h4>
          <p class="step-subtitle">How would you like to choose your audience?</p>

          <div class="audience-method-selector">
            <div class="method-card" @click="selectAudienceMethod('customFilters')">
              <div class="method-icon" style="background: linear-gradient(135deg, #547fff 0%, #3d5dff 100%);">
                <i class="fa fa-filter"></i>
              </div>
              <div class="method-info">
                <h6>Custom Filters</h6>
                <p>Build filters using procedures, appointments & more</p>
              </div>
              <div class="method-arrow">
                <i class="fa fa-chevron-right"></i>
              </div>
            </div>

            <div class="method-card" @click="selectAudienceMethod('scheduledPatients')">
              <div class="method-icon" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
                <i class="fa fa-calendar-check-o"></i>
              </div>
              <div class="method-info">
                <h6>Scheduled Patients</h6>
                <p>Send quick messages to all your patients on schedule today, tomorrow, or a custom time frame.</p>
              </div>
              <div class="method-arrow">
                <i class="fa fa-chevron-right"></i>
              </div>
            </div>

            <div class="method-card" @click="selectAudienceMethod('list')">
              <div class="method-icon" style="background: linear-gradient(135deg, #3abf94 0%, #2d9474 100%);">
                <i class="fa fa-list-ul"></i>
              </div>
              <div class="method-info">
                <h6>Saved Patient List</h6>
                <p>Use your patient lists you've created before (Recall, Unscheduled), or patient lists you've built using Custom Filters previously.</p>
              </div>
              <div class="method-arrow">
                <i class="fa fa-chevron-right"></i>
              </div>
            </div>

            <div class="method-card" @click="selectAudienceMethod('all')">
              <div class="method-icon" style="background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);">
                <i class="fa fa-users"></i>
              </div>
              <div class="method-info">
                <h6>All Active Patients</h6>
                <p>Send to every active patient in your practice</p>
              </div>
              <div class="method-arrow">
                <i class="fa fa-chevron-right"></i>
              </div>
            </div>
          </div>
        </template>

        <!-- Phase 2: Configure audience -->
        <template v-else>
          <div class="audience-back-link mb-3">
            <a href="#" @click.prevent="recipientSubStep = 0">
              <i class="fa fa-arrow-left mr-1"></i> Change audience method
            </a>
          </div>

          <!-- Custom Filter Builder -->
          <div v-if="form.recipientSource === 'customFilters'">
            <h4 class="step-title">Build Custom Filters</h4>
            <p class="step-subtitle">Add procedure and appointment filters to find your target audience</p>

            <div class="custom-filters-section">
              <CustomFilterBuilder
                v-model="form.customFilters"
                :proc-code-options="procCodeOptions"
                :zip-code-options="zipCodeOptions"
                :insurance-options="insuranceOptions"
                :provider-options="providerOptions"
                :recall-type-options="recallTypeOptions"
                :preview-loading="previewLoading"
                @preview="handleFilterPreview"
                @valid="onCustomFiltersValid"
              />
              <b-alert v-if="previewError" variant="danger" show class="mt-2" dismissible @dismissed="previewError = ''">
                <i class="fa fa-exclamation-circle mr-1"></i>{{ previewError }}
              </b-alert>
            </div>
          </div>

          <!-- Saved Lists -->
          <div v-if="form.recipientSource === 'list'">
            <h4 class="step-title">Choose a Saved Patient List</h4>
            <p class="step-subtitle">Reuse recipients from a previous campaign or saved patient list</p>

            <!-- Section 1: Campaign Drafts & Saved Lists -->
            <div v-if="savedListsLoading && savedLists.length === 0" class="text-center py-3">
              <b-spinner small variant="success"></b-spinner>
              <span class="ml-2 text-muted">Loading saved lists...</span>
            </div>

            <div v-if="savedLists.length > 0" class="mb-4">
              <h6 @click="campaignListsCollapsed = !campaignListsCollapsed" style="font-weight: 600; color: #334155; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.5px; margin-bottom: 10px; cursor: pointer; user-select: none;">
                <i class="fa fa-bullhorn mr-1"></i> From Your Campaigns
                <i :class="campaignListsCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'" class="ml-1" style="font-size: 0.65rem;"></i>
              </h6>
              <div v-if="!campaignListsCollapsed" class="saved-lists-browse">
                <div v-for="item in savedLists" :key="item.CampaignID" class="saved-list-card mb-2 p-3" style="background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;">
                  <div class="d-flex justify-content-between align-items-start">
                    <div style="flex: 1;">
                      <h6 class="mb-1" style="font-weight: 600;">{{ item.Name }}</h6>
                      <div class="mb-1" style="font-size: 0.8rem; color: #64748b;">
                        <span v-if="item.Type" class="mr-3"><i class="fa fa-tag mr-1"></i>{{ item.Type }}</span>
                        <span v-if="item.TotalRecipients" class="mr-3"><i class="fa fa-users mr-1"></i>{{ item.TotalRecipients }} recipients</span>
                        <span><i class="fa fa-calendar-o mr-1"></i>{{ item.SentAt || item.CreatedAt | formatListDate }}</span>
                      </div>
                      <div v-if="item.FilterSummary" style="font-size: 0.78rem; color: #94a3b8; font-style: italic;" class="mt-1">
                        {{ item.FilterSummary.length > 120 ? item.FilterSummary.substring(0, 120) + '...' : item.FilterSummary }}
                      </div>
                    </div>
                    <div class="d-flex flex-column" style="gap: 4px; min-width: 130px;">
                      <b-btn v-if="item.FilterConfig" size="sm" variant="outline-success" @click="rerunSavedFilters(item)" style="font-size: 0.75rem;">
                        <i class="fa fa-refresh mr-1"></i>Re-run Filters
                      </b-btn>
                      <b-btn v-if="item.PatientList" size="sm" variant="outline-primary" @click="useExactList(item)" :disabled="loadingCampaignID !== null" style="font-size: 0.75rem;">
                        <span v-if="loadingCampaignID === item.CampaignID"><b-spinner small class="mr-1" />Loading...</span>
                        <span v-else><i class="fa fa-list mr-1"></i>Use Exact List</span>
                      </b-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section 2: Overdue Recall & Unscheduled Treatment Lists -->
            <div v-if="siteListsLoading" class="text-center py-3">
              <b-spinner small variant="success"></b-spinner>
              <span class="ml-2 text-muted">Loading your lists...</span>
            </div>

            <div v-if="!siteListsLoading && (recallLists.length > 0 || unscheduledLists.length > 0)">
              <h6 @click="siteListsCollapsed = !siteListsCollapsed" style="font-weight: 600; color: #334155; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.5px; margin-bottom: 10px; cursor: pointer; user-select: none;">
                <i class="fa fa-list-alt mr-1"></i> From Your Lists
                <i :class="siteListsCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'" class="ml-1" style="font-size: 0.65rem;"></i>
              </h6>
              <div v-if="!siteListsCollapsed" class="d-flex" style="gap: 16px;">

                <!-- Overdue Recall Lists -->
                <div style="flex: 1; min-width: 0;">
                  <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px;">
                    <div class="d-flex align-items-center mb-2" style="gap: 6px;">
                      <span style="background: #fee2e2; color: #dc2626; width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 0.8rem;">
                        <i class="fa fa-clock-o"></i>
                      </span>
                      <span style="font-weight: 600; font-size: 0.85rem; color: #1e293b;">Overdue Recall Lists</span>
                    </div>

                    <div v-if="recallLists.length === 0" class="text-center py-3">
                      <p class="text-muted mb-0" style="font-size: 0.8rem;">No recall lists found</p>
                    </div>

                    <div v-else style="max-height: 320px; overflow-y: auto;">
                      <div
                        v-for="list in paginatedRecallLists"
                        :key="list.ListID"
                        style="background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; transition: border-color 0.15s;"
                        @mouseenter="$event.currentTarget.style.borderColor='#10b981'"
                        @mouseleave="$event.currentTarget.style.borderColor='#e2e8f0'"
                      >
                        <div class="d-flex justify-content-between align-items-start">
                          <div style="flex: 1; min-width: 0;">
                            <div style="font-weight: 600; font-size: 0.85rem; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ list.Name }}</div>
                            <div v-if="list.Description" style="font-size: 0.75rem; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px;">{{ list.Description }}</div>
                            <div style="font-size: 0.72rem; color: #94a3b8; margin-top: 3px;">
                              <span><i class="fa fa-calendar-o mr-1"></i>Created: {{ list.CreatedAt | formatListDate }}</span>
                              <span v-if="list.MemberCount" class="ml-2"><i class="fa fa-users mr-1"></i>{{ list.MemberCount }}</span>
                            </div>
                          </div>
                          <b-btn size="sm" variant="outline-success" @click="useRecallOrUnscheduledList(list)" :disabled="loadingListID !== null" style="font-size: 0.72rem; padding: 2px 8px; white-space: nowrap; margin-left: 8px;">
                            <span v-if="loadingListID === list.ListID"><b-spinner small class="mr-1" />Loading...</span>
                            <span v-else>Use List</span>
                          </b-btn>
                        </div>
                      </div>

                      <!-- Pagination -->
                      <div v-if="recallLists.length > listsPerPage" class="d-flex justify-content-between align-items-center mt-2" style="font-size: 0.75rem; color: #64748b;">
                        <span>{{ recallListPage }} of {{ Math.ceil(recallLists.length / listsPerPage) }}</span>
                        <div style="gap: 4px; display: flex;">
                          <b-btn size="sm" variant="outline-secondary" :disabled="recallListPage <= 1" @click="recallListPage--" style="padding: 1px 6px; font-size: 0.7rem;">
                            <i class="fa fa-chevron-left"></i>
                          </b-btn>
                          <b-btn size="sm" variant="outline-secondary" :disabled="recallListPage >= Math.ceil(recallLists.length / listsPerPage)" @click="recallListPage++" style="padding: 1px 6px; font-size: 0.7rem;">
                            <i class="fa fa-chevron-right"></i>
                          </b-btn>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Unscheduled Treatment Lists -->
                <div style="flex: 1; min-width: 0;">
                  <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px;">
                    <div class="d-flex align-items-center mb-2" style="gap: 6px;">
                      <span style="background: #dbeafe; color: #2563eb; width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 0.8rem;">
                        <i class="fa fa-calendar-check-o"></i>
                      </span>
                      <span style="font-weight: 600; font-size: 0.85rem; color: #1e293b;">Unscheduled Tx Lists</span>
                    </div>

                    <div v-if="unscheduledLists.length === 0" class="text-center py-3">
                      <p class="text-muted mb-0" style="font-size: 0.8rem;">No unscheduled treatment lists found</p>
                    </div>

                    <div v-else style="max-height: 320px; overflow-y: auto;">
                      <div
                        v-for="list in paginatedUnscheduledLists"
                        :key="list.ListID"
                        style="background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; transition: border-color 0.15s;"
                        @mouseenter="$event.currentTarget.style.borderColor='#3b82f6'"
                        @mouseleave="$event.currentTarget.style.borderColor='#e2e8f0'"
                      >
                        <div class="d-flex justify-content-between align-items-start">
                          <div style="flex: 1; min-width: 0;">
                            <div style="font-weight: 600; font-size: 0.85rem; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ list.Name }}</div>
                            <div v-if="list.Description" style="font-size: 0.75rem; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px;">{{ list.Description }}</div>
                            <div style="font-size: 0.72rem; color: #94a3b8; margin-top: 3px;">
                              <span><i class="fa fa-calendar-o mr-1"></i>Created: {{ list.CreatedAt | formatListDate }}</span>
                              <span v-if="list.MemberCount" class="ml-2"><i class="fa fa-users mr-1"></i>{{ list.MemberCount }}</span>
                            </div>
                          </div>
                          <b-btn size="sm" variant="outline-primary" @click="useRecallOrUnscheduledList(list)" :disabled="loadingListID !== null" style="font-size: 0.72rem; padding: 2px 8px; white-space: nowrap; margin-left: 8px;">
                            <span v-if="loadingListID === list.ListID"><b-spinner small class="mr-1" />Loading...</span>
                            <span v-else>Use List</span>
                          </b-btn>
                        </div>
                      </div>

                      <!-- Pagination -->
                      <div v-if="unscheduledLists.length > listsPerPage" class="d-flex justify-content-between align-items-center mt-2" style="font-size: 0.75rem; color: #64748b;">
                        <span>{{ unscheduledListPage }} of {{ Math.ceil(unscheduledLists.length / listsPerPage) }}</span>
                        <div style="gap: 4px; display: flex;">
                          <b-btn size="sm" variant="outline-secondary" :disabled="unscheduledListPage <= 1" @click="unscheduledListPage--" style="padding: 1px 6px; font-size: 0.7rem;">
                            <i class="fa fa-chevron-left"></i>
                          </b-btn>
                          <b-btn size="sm" variant="outline-secondary" :disabled="unscheduledListPage >= Math.ceil(unscheduledLists.length / listsPerPage)" @click="unscheduledListPage++" style="padding: 1px 6px; font-size: 0.7rem;">
                            <i class="fa fa-chevron-right"></i>
                          </b-btn>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <!-- Empty state when no lists at all -->
            <div v-if="!savedListsLoading && !siteListsLoading && savedLists.length === 0 && recallLists.length === 0 && unscheduledLists.length === 0" class="text-center py-4">
              <div class="mb-2" style="color: #94a3b8;"><i class="fa fa-list-ul fa-2x"></i></div>
              <p class="text-muted mb-0">No saved patient lists found. Save a campaign, or create lists from Overdue Recall or Unscheduled Treatment pages.</p>
            </div>
          </div>

          <!-- All Active Patients -->
          <div v-if="form.recipientSource === 'all'">
            <h4 class="step-title">All Active Patients</h4>
            <p style="font-weight: 600; font-size: 0.95rem; color: #334155; line-height: 1.6; margin-bottom: 1rem;">
              An active patient is someone who's had at least one procedure completed in the last 18 months.<br>
              These procedures will not include the following: <strong>D9986</strong>, <strong>D9987</strong>
            </p>

            <!-- Active Patients Timeframe -->
            <div class="mb-3">
              <label style="font-size: 0.75rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.025em; margin-bottom: 0.375rem; display: block;">Active Patients</label>
              <div class="mb-2" style="display: flex; gap: 16px;">
                <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 0.875rem; color: #374151; margin-bottom: 0;">
                  <input type="radio" value="relative" v-model="allActiveTimeframeMode" style="width: 16px; height: 16px; margin: 0; cursor: pointer; accent-color: #10b981;" />
                  <span>In the last</span>
                </label>
                <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 0.875rem; color: #374151; margin-bottom: 0;">
                  <input type="radio" value="custom" v-model="allActiveTimeframeMode" style="width: 16px; height: 16px; margin: 0; cursor: pointer; accent-color: #10b981;" />
                  <span>Custom range</span>
                </label>
              </div>

              <!-- Relative mode -->
              <div v-if="allActiveTimeframeMode === 'relative'" class="d-flex align-items-center" style="gap: 0.5rem;">
                <span style="font-size: 0.8125rem; color: #64748b; white-space: nowrap;">In the last</span>
                <input
                  v-model.number="allActiveTimeframeValue"
                  type="number"
                  min="1"
                  class="form-control form-control-sm"
                  style="width: 60px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.8125rem;"
                />
                <select
                  v-model="allActiveTimeframeUnit"
                  class="form-control form-control-sm"
                  style="width: 100px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.8125rem;"
                >
                  <option value="months">Months</option>
                  <option value="days">Days</option>
                  <option value="years">Years</option>
                </select>
              </div>

              <!-- Custom range mode -->
              <div v-else class="d-flex align-items-center" style="gap: 0.5rem;">
                <label class="mb-0" style="font-weight: 500; font-size: 0.875rem;">From</label>
                <input
                  type="date"
                  v-model="allActiveDateFrom"
                  :max="allActiveDateTo || undefined"
                  class="form-control form-control-sm"
                  style="width: 160px;"
                />
                <label class="mb-0" style="font-weight: 500; font-size: 0.875rem;">To</label>
                <input
                  type="date"
                  v-model="allActiveDateTo"
                  :min="allActiveDateFrom || undefined"
                  class="form-control form-control-sm"
                  style="width: 160px;"
                />
              </div>
            </div>

            <!-- Provider(s) -->
            <div class="mb-3">
              <label style="font-size: 0.75rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.025em; margin-bottom: 0.375rem; display: block;">Provider(s)</label>
              <multiselect
                v-model="allActiveProviders"
                :options="providerOptions"
                :multiple="true"
                :searchable="true"
                :close-on-select="false"
                :show-labels="false"
                label="text"
                track-by="value"
                placeholder="All providers (optional)"
                class="filter-multiselect"
              />
            </div>

            <!-- Exclude Procedure Codes -->
            <div class="mb-3">
              <label style="font-size: 0.8125rem; font-weight: 600; color: #64748b; margin-bottom: 0.375rem; display: block;">
                Choose additional one or more procedure codes you'd like to exclude in addition to D9986, D9987
              </label>
              <multiselect
                v-model="allActiveExcludedProcCodes"
                :options="procCodeOptions"
                :multiple="true"
                :searchable="true"
                :close-on-select="false"
                :show-labels="false"
                label="text"
                track-by="value"
                placeholder="Search procedure codes..."
                class="filter-multiselect"
              />
            </div>

            <!-- Preview Button -->
            <b-button
              variant="primary"
              size="sm"
              :disabled="previewLoading || !canPreviewAllActive"
              @click="handleAllActivePatientsPreview"
            >
              <span v-if="previewLoading"><b-spinner small class="mr-1" />Loading...</span>
              <span v-else><i class="fa fa-search mr-1"></i>Preview Patients</span>
            </b-button>

            <b-alert v-if="previewError" variant="danger" show class="mt-2" dismissible @dismissed="previewError = ''">
              <i class="fa fa-exclamation-circle mr-1"></i>{{ previewError }}
            </b-alert>
          </div>

          <!-- Scheduled Patients -->
          <div v-if="form.recipientSource === 'scheduledPatients'">
            <h4 class="step-title">Scheduled Patients</h4>
            <p class="step-subtitle">Choose a time frame to find patients with scheduled appointments</p>

            <div class="scheduled-timeframe-section">
              <b-form-group label="Quick Select" class="mb-3">
                <b-form-radio-group
                  v-model="scheduledTimeframe"
                  :options="scheduledTimeframeOptions"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  @change="onScheduledTimeframeChange"
                />
              </b-form-group>

              <div v-if="scheduledTimeframe === 'custom'" class="d-flex align-items-center mb-3" style="gap: 0.5rem;">
                <label class="mb-0" style="font-weight: 500; font-size: 0.875rem;">From</label>
                <input
                  type="date"
                  v-model="scheduledDateFrom"
                  :max="scheduledDateTo || undefined"
                  class="form-control form-control-sm"
                  style="width: 160px;"
                />
                <label class="mb-0" style="font-weight: 500; font-size: 0.875rem;">To</label>
                <input
                  type="date"
                  v-model="scheduledDateTo"
                  :min="scheduledDateFrom || undefined"
                  class="form-control form-control-sm"
                  style="width: 160px;"
                />
              </div>

              <div class="mb-3">
                <label style="font-weight: 500; font-size: 0.875rem;">Provider(s)</label>
                <multiselect
                  v-model="scheduledProviders"
                  :options="providerOptions"
                  :multiple="true"
                  :searchable="true"
                  :close-on-select="false"
                  :show-labels="false"
                  label="text"
                  track-by="value"
                  placeholder="All providers (default)"
                  class="filter-multiselect"
                />
              </div>

              <b-button
                variant="primary"
                size="sm"
                :disabled="!canPreviewScheduled || previewLoading"
                @click="handleScheduledPreview"
              >
                <span v-if="previewLoading"><b-spinner small class="mr-1" />Loading...</span>
                <span v-else><i class="fa fa-search mr-1"></i>Preview Patients</span>
              </b-button>

              <b-alert v-if="previewError" variant="danger" show class="mt-2" dismissible @dismissed="previewError = ''">
                <i class="fa fa-exclamation-circle mr-1"></i>{{ previewError }}
              </b-alert>
            </div>
          </div>

          <!-- Missing Contact Info Warnings -->
          <div v-if="recipientCount > 0 && (missingPhoneCount > 0 || missingEmailCount > 0)" class="mt-3">
            <div v-if="(form.type === 'SMS' || form.type === 'Both') && missingPhoneCount > 0" class="mb-2 py-2 px-3" style="background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 6px; color: #334155; font-size: 0.85rem;">
              <i class="fa fa-exclamation-triangle mr-1" style="color: #64748b;"></i>
              <strong>{{ missingPhoneCount }}</strong> patient{{ missingPhoneCount > 1 ? 's' : '' }} {{ missingPhoneCount > 1 ? 'have' : 'has' }} no mobile phone number and will not receive SMS messages.
            </div>
            <div v-if="(form.type === 'Email' || form.type === 'Both') && missingEmailCount > 0" class="mb-2 py-2 px-3" style="background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 6px; color: #334155; font-size: 0.85rem;">
              <i class="fa fa-exclamation-triangle mr-1" style="color: #64748b;"></i>
              <strong>{{ missingEmailCount }}</strong> patient{{ missingEmailCount > 1 ? 's' : '' }} {{ missingEmailCount > 1 ? 'have' : 'has' }} no email address and will not receive emails.
            </div>
            <div class="mb-2 py-2 px-3" style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; color: #1e40af; font-size: 0.82rem;">
              <i class="fa fa-info-circle mr-1"></i>
              If you add missing contact info in Open Dental, you will need to come back and re-preview for changes to reflect.
            </div>
          </div>

        <!-- Filter Summary (shows after any preview attempt, even with 0 results) -->
        <div v-if="filterSummaryLines.length > 0 && (recipientCount > 0 || previewError)" class="mt-3 py-3 px-3" style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;">
          <h5 class="mb-2" style="font-weight: 700;">Criteria Summary</h5>
          <div v-for="(item, idx) in filterSummaryLines" :key="idx" style="font-size: 0.82rem; color: #475569;">
            <div class="mb-1"><strong>{{ item.text }}</strong></div>
            <div v-if="item.connector" class="text-center mb-1" style="font-size: 0.7rem; font-weight: 700; color: #94a3b8; letter-spacing: 0.05em;">{{ item.connector }}</div>
          </div>
        </div>

        <!-- Recipient Preview -->
        <div class="recipient-preview" ref="recipientPreview" v-if="recipientCount > 0">
          <div class="preview-card">
            <div class="preview-icon">
              <i class="fa fa-users"></i>
            </div>
            <div class="preview-content">
              <h5>{{ effectiveRecipientCount | formatNumber }} Recipients</h5>
              <p class="mb-0">
                <span v-if="form.type === 'SMS' || form.type === 'Both'">
                  {{ smsEligibleCount | formatNumber }} with valid mobile numbers
                </span>
                <span v-if="form.type === 'Both'"> • </span>
                <span v-if="form.type === 'Email' || form.type === 'Both'">
                  {{ emailEligibleCount | formatNumber }} with valid emails
                </span>
              </p>
            </div>
          </div>

          <!-- Disconnected-number skip (Phase 2): show BEFORE send how many
               recipients will be left out, with a per-send "send anyway" escape. -->
          <div v-if="(form.type === 'SMS' || form.type === 'Both') && smsSuppressedCount > 0" class="dead-number-notice mt-2">
            <div class="dnn-head">
              <i class="fa fa-ban mr-2" style="color:#b45309;"></i>
              <strong>{{ smsSuppressedCount | formatNumber }}</strong>
              recipient<span v-if="smsSuppressedCount !== 1">s</span> will be skipped — disconnected numbers
            </div>
            <div class="dnn-body">
              These numbers have repeatedly failed to receive texts, so we leave them out of
              campaigns and you're not charged for them. They're added back automatically once
              a text reaches them again.
            </div>
            <b-form-checkbox v-model="form.includeSuppressed" class="dnn-override">
              Send to these disconnected numbers anyway (this campaign only)
            </b-form-checkbox>
          </div>

          <!-- Patient List Table -->
          <div v-if="previewPatients.length > 0" class="patient-table-section mt-3">
            <!-- Header Row: count + removal toggle -->
            <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap" style="gap: 6px;">
              <small class="text-muted">
                <i class="fa fa-table mr-1"></i>
                Showing {{ filteredPreviewPatients.length }} of {{ effectiveRecipientCount | formatNumber }} patients
                <span v-if="activePreviewPatients.length < previewPatients.length" class="text-danger ml-2">
                  ({{ previewPatients.length - activePreviewPatients.length }} removed)
                </span>
              </small>
              <div class="d-flex align-items-center" style="gap: 8px;">
                <template v-if="removalMode">
                  <small v-if="selectedPatNums.length > 0" class="font-weight-bold" style="color: #64748b;">
                    {{ selectedPatNums.length }} selected
                  </small>
                  <b-btn size="sm" variant="outline-danger" :disabled="selectedPatNums.length === 0" @click="showRemoveConfirm = true" style="padding: 2px 8px; font-size: 0.75rem;">
                    <i class="fa fa-trash mr-1"></i>Remove
                  </b-btn>
                  <b-btn size="sm" variant="outline-secondary" @click="exitRemovalMode" style="padding: 2px 8px; font-size: 0.75rem;">
                    Cancel
                  </b-btn>
                </template>
                <template v-else>
                  <b-btn size="sm" variant="outline-success" @click="saveListOnly" style="padding: 2px 8px; font-size: 0.75rem;">
                    <i class="fa fa-floppy-o mr-1"></i>Save List
                  </b-btn>
                  <b-btn size="sm" variant="outline-secondary" @click="enterRemovalMode" style="padding: 2px 8px; font-size: 0.75rem;">
                    <i class="fa fa-user-times mr-1"></i>Remove Patients
                  </b-btn>
                </template>
              </div>
            </div>
            <!-- Search Box -->
            <b-input-group size="sm" class="mb-2">
              <b-input-group-prepend is-text>
                <i class="fa fa-search"></i>
              </b-input-group-prepend>
              <b-form-input
                v-model="patientSearch"
                placeholder="Search by patient name..."
                debounce="200"
              />
              <b-input-group-append v-if="patientSearch">
                <b-btn variant="outline-secondary" @click="patientSearch = ''">
                  <i class="fa fa-times"></i>
                </b-btn>
              </b-input-group-append>
            </b-input-group>
            <b-table
              :items="filteredPreviewPatients"
              :fields="patientTableFields"
              :per-page="patientPerPage"
              :current-page="patientCurrentPage"
              striped
              hover
              small
              responsive
              class="patient-preview-table mb-0"
            >
              <template #head(select)>
                <input
                  type="checkbox"
                  :checked="allVisibleSelected"
                  :indeterminate.prop="selectedPatNums.length > 0 && !allVisibleSelected"
                  @change="toggleSelectAll($event.target.checked)"
                  style="cursor: pointer; width: 16px; height: 16px;"
                />
              </template>
              <template #cell(select)="data">
                <input
                  type="checkbox"
                  :checked="selectedPatNums.includes(data.item.PatNum)"
                  @change="togglePatientSelection(data.item.PatNum)"
                  style="cursor: pointer; width: 16px; height: 16px;"
                />
              </template>
              <template #cell(name)="data">
                {{ data.item.LName }}, {{ data.item.FName }}
              </template>
              <template #cell(WirelessPhone)="data">
                <span v-if="data.value" class="text-success">
                  <i class="fa fa-phone mr-1" style="font-size: 0.7rem;"></i>{{ data.value }}
                </span>
                <span v-else-if="form.type === 'SMS' || form.type === 'Both'" style="background: #f1f5f9; color: #64748b; padding: 2px 6px; border-radius: 4px; font-size: 0.75rem; font-weight: 600;">
                  <i class="fa fa-exclamation-triangle mr-1" style="font-size: 0.65rem;"></i>Missing
                </span>
                <span v-else class="text-muted">—</span>
              </template>
              <template #cell(Email)="data">
                <span v-if="data.value">{{ data.value }}</span>
                <span v-else-if="form.type === 'Email' || form.type === 'Both'" style="background: #f1f5f9; color: #64748b; padding: 2px 6px; border-radius: 4px; font-size: 0.75rem; font-weight: 600;">
                  <i class="fa fa-exclamation-triangle mr-1" style="font-size: 0.65rem;"></i>Missing
                </span>
                <span v-else class="text-muted">—</span>
              </template>
            </b-table>
            <div v-if="filteredPreviewPatients.length > patientPerPage" class="d-flex justify-content-between align-items-center mt-2">
              <small class="text-muted">
                Page {{ patientCurrentPage }} of {{ Math.ceil(filteredPreviewPatients.length / patientPerPage) }}
              </small>
              <b-pagination
                v-model="patientCurrentPage"
                :total-rows="filteredPreviewPatients.length"
                :per-page="patientPerPage"
                size="sm"
                class="mb-0"
              />
            </div>
          </div>

          <!-- Remove Patients Confirmation Modal -->
          <b-modal
            v-model="showRemoveConfirm"
            title="Remove Patients"
            size="sm"
            centered
            ok-title="Remove"
            ok-variant="danger"
            @ok="confirmRemovePatients"
            @cancel="showRemoveConfirm = false"
          >
            <p>Remove <strong>{{ selectedPatNums.length }}</strong> patient{{ selectedPatNums.length > 1 ? 's' : '' }} from this campaign?</p>
            <p class="text-muted mb-0"><small>These patients will not receive this campaign message. This cannot be undone.</small></p>
          </b-modal>

          <!-- Keep Removals Prompt Modal -->
          <b-modal
            v-model="showKeepRemovalsPrompt"
            title="Previous Removals Found"
            size="sm"
            centered
            no-close-on-backdrop
            no-close-on-esc
            hide-header-close
          >
            <p>You previously removed <strong>{{ removedPatNums.length }}</strong> patient{{ removedPatNums.length > 1 ? 's' : '' }}. Would you like to keep those removals or start fresh?</p>
            <template #modal-footer>
              <b-btn variant="outline-secondary" @click="handleKeepRemovalsChoice(false)">Start Fresh</b-btn>
              <b-btn variant="primary" @click="handleKeepRemovalsChoice(true)">Keep Removals</b-btn>
            </template>
          </b-modal>
        </div>

        <!-- AI Call: editable ranked recall list (Phase D.2b / D.2c) ----------- -->
        <!-- Standard preset: no list, just a note (audience>0). Other presets: the editable
             ranked list + hand-add picker, reachable even with an empty audience so the user
             can hand-add the FIRST patient (Phase D.2c). -->
        <div
          v-if="form.type === 'ai_call' && isAiCallEnabled"
          class="ai-ranked-section mt-4"
        >
          <div
            v-if="form.priorityPreset === 'standard'"
            v-show="aiCallSelectedPatNums.length > 0"
            class="py-2 px-3"
            style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; color: #475569; font-size: 0.85rem;"
          >
            <i class="fa fa-info-circle mr-1"></i>
            Standard — patients are dialed in default order.
          </div>

          <template v-else>
            <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap" style="gap: 6px;">
              <h6 class="mb-0" style="font-weight: 700; color: #1e293b;">
                <i class="fa fa-sort-amount-desc mr-1" style="color: #6366f1;"></i>
                Who would be called first
              </h6>
              <small v-if="!rankingLoading && !rankingError && rankedPatients.length > 0" class="text-muted">
                Will call <strong>{{ rankedEffectiveCount | formatNumber }}</strong>
                of {{ rankedPatients.length | formatNumber }}
              </small>
            </div>

            <!-- Empty union (no audience + no hand-adds): friendly prompt so the user knows
                 they can start the call list right here (Phase D.2c). -->
            <div
              v-if="aiCallSelectedPatNums.length === 0"
              class="py-2 px-3 mb-3"
              style="background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; color: #075985; font-size: 0.85rem;"
            >
              <i class="fa fa-info-circle mr-1"></i>
              No patients selected yet — add specific patients to call below, or pick an audience above.
            </div>

            <!-- Hand-add specific patients (Phase D.2c) — add patients who aren't in the
                 recall audience to the call list. They rank like everyone else. -->
            <div class="ai-handadd mb-3">
              <label class="d-block mb-1" style="font-weight: 600; color: #1e293b; font-size: 0.82rem;">
                <i class="fa fa-user-plus mr-1" style="color: #0ea5e9;"></i>
                Add a specific patient to this call list
              </label>
              <multiselect
                v-model="handAddPatientSelected"
                :options="availableHandAddPatients"
                :multiple="false"
                :custom-label="handAddPatientLabel"
                :taggable="false"
                :show-labels="false"
                :disabled="!patients || patients.length === 0"
                track-by="PatNum"
                :placeholder="!patients || patients.length === 0 ? 'Patient list is still loading…' : 'Search for a patient to add…'"
                class="filter-multiselect"
                @select="onHandAddPatient"
                :append-to-body="true"
              >
                <template slot="option" slot-scope="props">
                  <div class="d-flex align-items-center">
                    <span class="font-weight-medium">{{ handAddPatientLabel(props.option) }}</span>
                    <span v-if="handAddPatientDOB(props.option)" class="text-muted ml-2 small">{{ handAddPatientDOB(props.option) }}</span>
                  </div>
                </template>
                <template slot="noOptions">Start typing to search patients</template>
              </multiselect>
              <small v-if="!patients || patients.length === 0" class="text-muted d-block mt-1">
                Patients couldn't be loaded — try reopening this window.
              </small>
              <small v-else class="text-muted d-block mt-1">
                Added patients are dialed alongside the recall list. Patients who opted out of
                reminders or have no mobile number are skipped automatically.
              </small>
            </div>

            <!-- Loading -->
            <div v-if="rankingLoading" class="text-center py-3">
              <b-spinner small variant="primary"></b-spinner>
              <span class="ml-2 text-muted">Ranking your patients…</span>
            </div>

            <!-- Error -->
            <b-alert v-else-if="rankingError" variant="warning" show class="mb-0">
              <i class="fa fa-exclamation-triangle mr-1"></i>{{ rankingError }}
            </b-alert>

            <!-- Ranked table -->
            <template v-else-if="rankedPatients.length > 0">
              <p class="text-muted small mb-2">
                Pin patients to call first, or exclude patients you don't want called. Pinned float to the top;
                excluded won't be dialed.
              </p>
              <b-table
                :items="displayRankedPatients"
                :fields="rankedFields"
                striped
                hover
                small
                responsive
                class="ai-ranked-table mb-0"
              >
                <template #cell(_rank)="data">
                  <span v-if="!data.item._excluded" class="font-weight-bold">{{ data.item._rank }}</span>
                  <span v-else class="text-muted">—</span>
                </template>
                <template #cell(name)="data">
                  <span :class="{ 'ai-ranked-excluded': data.item._excluded }">
                    {{ rankedNameFor(data.item) }}
                    <b-badge v-if="data.item._pinned" variant="primary" class="ml-1">Pinned</b-badge>
                    <b-badge v-if="data.item._excluded" variant="secondary" class="ml-1">Excluded</b-badge>
                    <b-badge v-if="isHandAdded(data.item.patNum)" variant="info" class="ml-1">Added</b-badge>
                  </span>
                </template>
                <template #cell(why)="data">
                  <span :class="{ 'ai-ranked-excluded': data.item._excluded }" style="font-size: 0.8rem; color: #475569;">
                    {{ rankedWhyFor(data.item) || '—' }}
                  </span>
                </template>
                <template #cell(actions)="data">
                  <div class="d-flex" style="gap: 4px;">
                    <b-btn
                      size="sm"
                      :variant="isPinned(data.item.patNum) ? 'primary' : 'outline-primary'"
                      style="padding: 1px 8px; font-size: 0.72rem;"
                      :disabled="data.item._excluded"
                      @click="togglePin(data.item.patNum)"
                    >
                      <i class="fa fa-thumb-tack mr-1"></i>{{ isPinned(data.item.patNum) ? 'Pinned' : 'Pin' }}
                    </b-btn>
                    <b-btn
                      size="sm"
                      :variant="isExcluded(data.item.patNum) ? 'secondary' : 'outline-secondary'"
                      style="padding: 1px 8px; font-size: 0.72rem;"
                      @click="toggleExclude(data.item.patNum)"
                    >
                      <i class="fa fa-ban mr-1"></i>{{ isExcluded(data.item.patNum) ? 'Excluded' : 'Exclude' }}
                    </b-btn>
                    <b-btn
                      v-if="isHandAdded(data.item.patNum)"
                      size="sm"
                      variant="outline-danger"
                      style="padding: 1px 8px; font-size: 0.72rem;"
                      title="Remove this hand-added patient from the call list"
                      @click="removeHandPatient(data.item.patNum)"
                    >
                      <i class="fa fa-times mr-1"></i>Remove
                    </b-btn>
                  </div>
                </template>
              </b-table>
            </template>

            <!-- Empty (audience present but nothing came back). Suppressed when the union is
                 empty — the empty-state prompt above already covers that case. -->
            <div v-else-if="aiCallSelectedPatNums.length > 0" class="text-muted small py-2">
              No ranked patients to show.
            </div>
          </template>
        </div>
        </template>
      </div>

      <!-- Step 3: Message Content -->
      <div v-show="currentStep === 2" class="step-content">
        <h4 class="step-title">Compose Your Message</h4>
        <p class="step-subtitle">Create the content for your campaign</p>

        <!-- AI Call: no message to compose — the built-in voice agent runs its own script -->
        <b-alert v-if="form.type === 'ai_call'" variant="info" show class="mb-0">
          <i class="fa fa-robot mr-2"></i>
          AI Call campaigns use the built-in voice agent script — no message to compose.
          Patients on your selected list will be called and the outcome recorded as a follow-up.
        </b-alert>

        <template v-if="form.type !== 'ai_call'">
        <!-- SMS Content -->
        <div v-if="form.type === 'SMS' || form.type === 'Both'" class="message-section mb-4">
          <h6 class="section-label">
            <i class="fa fa-comment text-success mr-2"></i>SMS Message
            <small v-if="formattedSmsNumber" class="ml-2" style="color: #15803d; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 20px; padding: 2px 10px; font-weight: 600; font-size: 0.75rem;">
              <i class="fa fa-phone" style="font-size: 0.65rem; margin-right: 3px;"></i>
              {{ user && user.SiteName ? user.SiteName : 'Your practice' }}'s outgoing number: {{ formattedSmsNumber }}
            </small>
          </h6>
          
          <!-- Textarea + Insert button -->
          <div class="message-editor">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <small class="text-muted">Type your message or select a template below</small>
              <b-dropdown ref="smsInsertDropdown" size="sm" variant="outline-secondary" right no-caret>
                <template #button-content>
                  <i class="fa fa-plus mr-1"></i> Insert
                </template>
                <div class="px-3 py-2" style="min-width: 210px;">
                  <div class="text-muted small font-weight-bold mb-1">Variables</div>
                  <div
                    v-for="v in smsInsertVariables"
                    :key="v.key"
                    class="sms-insert-option"
                    @click="insertSmsVariable(v.template)"
                  >
                    <span>{{ v.name }}</span>
                    <small class="text-muted ml-1">{{ v.template }}</small>
                  </div>
                  <div class="dropdown-divider my-2"></div>
                  <div class="text-muted small font-weight-bold mb-1">Emojis</div>
                  <div class="text-muted mb-1" style="font-size: 0.7rem;">
                    Heads up: emoji switch texts to Unicode — 67 characters per credit instead of 153
                  </div>
                  <div class="emoji-picker-grid">
                    <span
                      v-for="emoji in commonEmojis"
                      :key="emoji"
                      class="emoji-item"
                      @click="insertSmsEmoji(emoji)"
                    >{{ emoji }}</span>
                  </div>
                </div>
              </b-dropdown>
            </div>
            <b-form-textarea
              v-model="form.smsContent"
              placeholder="Type your SMS message here..."
              rows="4"
              class="message-textarea"
              @input="updateSmsPreview"
            />
            <!-- Live segment/character counter — made prominent 2026-07-03 (the
                 old counter was tiny grey text staff never noticed). Colour-coded
                 by credits used, with the exact "chars until the next credit". -->
            <div class="sms-counter-live" :class="'tone-' + smsSegmentTone">
              <div class="scl-top">
                <span class="scl-pill">
                  <i class="fa" :class="smsSegments > 1 ? 'fa-clone' : 'fa-comment'"></i>
                  {{ smsSegments }} credit{{ smsSegments === 1 ? '' : 's' }} per text
                </span>
                <span class="scl-read">
                  <span class="scl-count">{{ smsCharCount.toLocaleString() }}</span>
                  <span class="scl-unit">{{ smsSegments === 1 ? '/ ' + smsMaxCharsForOneSegment + ' characters' : 'characters' }}</span>
                </span>
              </div>
              <div class="scl-track"><div class="scl-fill" :style="{ width: smsCounterTrackPct + '%' }"></div></div>
              <div class="scl-foot">
                <span class="scl-hint">
                  <b>{{ smsCharsUntilNextSegment.toLocaleString() }}</b>
                  more character{{ smsCharsUntilNextSegment === 1 ? '' : 's' }} until this uses
                  {{ (smsSegments + 1).toLocaleString() }} credits
                </span>
                <span v-if="smsActiveAnalysis.encoding === 'UCS-2'" class="scl-uni">
                  <i class="fa fa-exclamation-triangle mr-1"></i>Unicode — a special character cut each credit to 67 characters
                </span>
              </div>
            </div>

            <!-- CARRIER SIZE HARD STOP (Twilio error 30019). Above the cap, carriers
                 reject the text AND still bill for it — 978 of Allay's Invisalign
                 messages failed exactly this way on 2026-07-02. Block, don't warn. -->
            <div v-if="smsCarrierBlocked" class="sms-carrier-block">
              <div class="scb-head">
                <i class="fa fa-ban mr-2"></i>
                This message is too long to send
              </div>
              <div class="scb-body">
                At <strong>{{ smsSegments }} credits per text</strong>, mobile carriers
                (AT&amp;T, T-Mobile, Verizon…) will reject it — messages larger than
                {{ smsCarrierMaxSegments }} credits are not delivered, and you'd still be
                charged for the attempt. Please shorten it to
                {{ smsCarrierMaxSegments }} credits or fewer.
                <span v-if="smsOptimizeWouldComply">
                  Switching to <strong>Optimize formatting</strong> above would bring it
                  to {{ smsOptimized.segments }} credits — under the limit.
                </span>
              </div>
            </div>

            <!-- Plain-English money-saving coach (2026-07-03). Concepts only — no
                 channel alternatives; every figure at Teamio's message-credit rate. -->
            <details v-if="form.smsContent" class="sms-tips" open>
              <summary class="sms-tips-head">
                <span class="sms-tips-bulb">💡</span>
                <span class="sms-tips-title">How texting costs work — and how to spend less</span>
                <i class="fa fa-chevron-right sms-tips-chev"></i>
              </summary>
              <div class="sms-tips-body">
                <div class="sms-tip">
                  <span class="sms-tip-num">1</span>
                  <div class="sms-tip-text">
                    <strong>Texts are billed in “message credits.”</strong>
                    A text up to 160 characters uses 1 credit. Longer texts are split into parts of about
                    <strong>153 characters</strong>, and each part uses another credit.
                    <template v-if="smsCharCount > 0"> This message is
                    <strong>{{ smsCharCount.toLocaleString() }} characters → {{ smsSegments }} credit{{ smsSegments === 1 ? '' : 's' }}</strong> per recipient.</template>
                  </div>
                </div>
                <div class="sms-tip">
                  <span class="sms-tip-num">2</span>
                  <div class="sms-tip-text">
                    <strong>Every ~153 characters you trim saves a whole credit.</strong>
                    <template v-if="smsTrimSavingsUsd > 0">
                      Shortening this campaign by one credit per text would save about
                      <span class="sms-tip-chip">${{ smsTrimSavingsUsd.toFixed(2) }}</span>
                    </template>
                    <template v-else-if="smsSegments > 1">
                      This campaign is within your monthly plan, so trimming won't lower a charge — but it uses
                      fewer of your included credits.
                    </template>
                    <template v-else>
                      This message already fits in a single credit — nice and lean.
                    </template>
                  </div>
                </div>
                <div class="sms-tip">
                  <span class="sms-tip-num">3</span>
                  <div class="sms-tip-text">
                    <strong>Avoid special characters.</strong>
                    A single <code>®</code>, <code>™</code>, emoji, “curly quote,” or long dash <code>—</code>
                    switches a text to “Unicode,” which cuts each credit from 153 to just
                    <strong>67 characters</strong> — so the same message needs more than twice as many credits.
                    Keeping <strong>“Optimize formatting”</strong> on fixes the common ones for you.
                  </div>
                </div>
                <div class="sms-tip">
                  <span class="sms-tip-num">4</span>
                  <div class="sms-tip-text">
                    <strong>Disconnected numbers are skipped automatically.</strong>
                    If a patient's number keeps failing to receive texts (disconnected, or a phone that
                    no longer exists), we leave it out of campaigns so you don't pay for messages that
                    can't be delivered. The moment that number successfully receives a text again, it's
                    included automatically — nothing is deleted, and you can always choose to send to it anyway.
                  </div>
                </div>
                <div class="sms-tip">
                  <span class="sms-tip-num">5</span>
                  <div class="sms-tip-text">
                    <strong>Very long texts can't be sent.</strong>
                    Mobile carriers reject texts longer than <strong>10 credits</strong> (about 1,500
                    characters) — they won't deliver, but you'd still be charged. If a message is that long
                    we'll stop you before sending and ask you to shorten it.
                  </div>
                </div>
                <div class="sms-tip">
                  <span class="sms-tip-num">6</span>
                  <div class="sms-tip-text">
                    <strong>Heavy emoji or exclamation marks can get a text filtered.</strong>
                    Beyond the extra cost, carriers watch for spam-like patterns — lots of emoji,
                    exclamation marks, or ALL CAPS — and may silently block a message that looks like
                    it's dodging spam filters. It'll still be billed even if this happens. Keeping emoji
                    light helps a text actually reach your patients, not just cost less.
                  </div>
                </div>
              </div>
              <div class="sms-tips-foot">Tips 1–3 only affect cost — your patients always see the same, correctly-formatted message. Tips 4–5 protect you from paying for texts that can't be delivered. Tip 6 is about staying clear of carrier spam filters, which we can't detect or block in advance.</div>
            </details>

            <!-- Cost card — "receipt" treatment (mockup A, chosen 2026-07-03).
                 One ® once turned a 5-segment promo into a 12-segment one (2x cost)
                 with no warning; now the practice picks between priced options, sees
                 a usage meter against the plan allowance, and gets an itemized bill
                 with a single hero total before sending. -->
            <div v-if="form.smsContent && (smsHasFormattingChoice || smsRecipientsForEstimate > 0)" class="sms-cost-card mt-2">

              <!-- formatting choice as selectable priced cards -->
              <div v-if="smsHasFormattingChoice" class="sms-choice-row">
                <label class="sms-choice" :class="{ 'is-selected': form.smsFormatting === 'optimized' }">
                  <input type="radio" v-model="form.smsFormatting" value="optimized" class="sms-choice-input">
                  <span class="sms-choice-head">
                    <span class="sms-choice-title">
                      Optimize formatting <span class="sms-choice-badge">Recommended</span>
                    </span>
                    <span v-if="smsRecipientsForEstimate" class="sms-choice-price">
                      <template v-if="smsCostForSegments(smsOptimized.segments) > 0">${{ smsCostForSegments(smsOptimized.segments).toFixed(2) }}</template>
                      <template v-else>No added cost</template>
                    </span>
                  </span>
                  <span class="sms-choice-sub">
                    {{ smsOptimized.segments }} credit{{ smsOptimized.segments === 1 ? '' : 's' }} per text — replaces
                    <template v-for="(r, i) in smsNormalization.replacements">
                      <code :key="'r' + i">{{ r.from }}</code><span :key="'a' + i"> → </span><code :key="'t' + i">{{ r.to === '' ? '(removed)' : r.to }}</code><span v-if="i < smsNormalization.replacements.length - 1" :key="'c' + i">, </span>
                    </template>
                  </span>
                </label>
                <label class="sms-choice sms-choice--costly" :class="{ 'is-selected': form.smsFormatting === 'original' }">
                  <input type="radio" v-model="form.smsFormatting" value="original" class="sms-choice-input">
                  <span class="sms-choice-head">
                    <span class="sms-choice-title">Keep exactly as typed</span>
                    <span v-if="smsRecipientsForEstimate" class="sms-choice-price">
                      <template v-if="smsCostForSegments(smsAsTyped.segments) > 0">${{ smsCostForSegments(smsAsTyped.segments).toFixed(2) }}</template>
                      <template v-else>No added cost</template>
                    </span>
                  </span>
                  <span class="sms-choice-sub">
                    {{ smsAsTyped.segments }} credit{{ smsAsTyped.segments === 1 ? '' : 's' }} per text —
                    special characters switch texts to Unicode (67 characters per credit instead of 153)
                  </span>
                </label>
              </div>

              <!-- monthly usage meter against the plan allowance -->
              <div v-if="smsUsageMeter" class="sms-meter-block">
                <div class="sms-meter-head">
                  <span>
                    Monthly message credits — <strong>{{ smsUsageBreakdown.projected.toLocaleString() }}</strong>
                    projected of <strong>{{ smsUsageBreakdown.allowance.toLocaleString() }}</strong> included
                  </span>
                  <span v-if="smsUsageBreakdown.overage > 0" class="sms-meter-over">
                    {{ smsUsageBreakdown.overage.toLocaleString() }} over
                  </span>
                  <span v-else class="sms-meter-ok">
                    {{ (smsUsageBreakdown.allowance - smsUsageBreakdown.projected).toLocaleString() }} remaining
                  </span>
                </div>
                <div class="sms-meter">
                  <div class="sms-meter-used" :style="{ width: smsUsageMeter.usedPct + '%' }"></div>
                  <div class="sms-meter-campaign" :style="{ width: smsUsageMeter.campaignPct + '%' }"></div>
                  <div class="sms-meter-overbar" :style="{ width: smsUsageMeter.overPct + '%' }"></div>
                  <div class="sms-meter-cap" :style="{ left: smsUsageMeter.capPct + '%' }"></div>
                </div>
                <div class="sms-meter-legend">
                  <span><span class="sms-meter-dot" style="background:#94a3b8"></span>Used so far <strong>{{ smsUsageBreakdown.used.toLocaleString() }}</strong></span>
                  <span><span class="sms-meter-dot" style="background:#22c55e"></span>This campaign (included) <strong>{{ smsUsageMeter.included.toLocaleString() }}</strong></span>
                  <span v-if="smsUsageBreakdown.chargeableSegments > 0"><span class="sms-meter-dot" style="background:#f59e0b"></span>Beyond free credits <strong>{{ smsUsageBreakdown.chargeableSegments.toLocaleString() }}</strong></span>
                </div>
              </div>

              <!-- itemized receipt — under the credit wallet this shows how many message
                   credits the campaign uses and how they're covered: free monthly credits
                   first, then a draw from the practice's PREPAID credit balance (never a
                   per-campaign card charge). A fully-covered campaign shows "no cost". If the
                   balance can't cover the draw, a BULK credit top-up is added at send (the
                   practice approves it), and only what's needed is used — the rest rolls
                   forward. (Reframed 2026-07-10 for the wallet model.) -->
              <div v-if="smsRecipientsForEstimate" class="sms-lines">
                <template v-if="smsUsageBreakdown">
                  <!-- fully covered by the free monthly credits: nothing drawn from the balance -->
                  <div v-if="smsUsageBreakdown.overageCostUsd === 0" class="sms-line sms-line--total">
                    <span class="sms-line-label">
                      Credits this campaign uses
                      <small>{{ smsUsageBreakdown.campaignSegments.toLocaleString() }} message credits — all within your monthly free credits</small>
                    </span>
                    <span class="sms-line-val sms-line-val--zero">No cost</span>
                  </div>
                  <!-- beyond the free monthly credits: the extra credits draw from the prepaid balance -->
                  <template v-else>
                    <div class="sms-line">
                      <span class="sms-line-label">
                        Campaign cost
                        <small>{{ smsCampaignTotalSegments.toLocaleString() }} credits × ${{ smsOverageRatePerSegment.toFixed(2) }} ({{ smsSegments }}/text × {{ smsRecipientsForEstimate.toLocaleString() }} recipients)</small>
                      </span>
                      <span class="sms-line-val">${{ smsCampaignValueUsd.toFixed(2) }}</span>
                    </div>
                    <div class="sms-line">
                      <span class="sms-line-label">
                        Covered by your free monthly credits
                        <small>{{ smsUsageMeter.included.toLocaleString() }} of {{ smsUsageBreakdown.campaignSegments.toLocaleString() }} credits included</small>
                      </span>
                      <span class="sms-line-val sms-line-val--covered">− ${{ smsCoveredValueUsd.toFixed(2) }}</span>
                    </div>
                    <div class="sms-line sms-line--total">
                      <span class="sms-line-label">
                        Drawn from your credit balance
                        <small>{{ smsUsageBreakdown.chargeableSegments.toLocaleString() }} credit{{ smsUsageBreakdown.chargeableSegments === 1 ? '' : 's' }} beyond your free monthly credits × ${{ smsOverageRatePerSegment.toFixed(2) }} each</small>
                      </span>
                      <span class="sms-line-val sms-line-val--due">${{ smsUsageBreakdown.overageCostUsd.toFixed(2) }}</span>
                    </div>
                  </template>
                </template>
                <div v-else class="sms-line-note">
                  <i class="fa fa-info-circle mr-1"></i>
                  Message credits beyond your free monthly allowance draw from your prepaid credit
                  balance (${{ smsOverageRatePerSegment.toFixed(2) }} each). If your balance runs low,
                  it's topped up in bulk — you approve it before anything sends.
                </div>
              </div>

              <!-- every lever that lowers the cost, each priced -->
              <div v-if="smsSavingsSuggestions.length" class="sms-advisor">
                <div class="sms-advisor-title">Ways to lower this cost</div>
                <div v-for="s in smsSavingsSuggestions" :key="s.key" class="sms-sugg">
                  <span class="sms-sugg-label">
                    <i class="fa mr-1" :class="s.icon"></i>
                    {{ s.label }}
                    <small>{{ s.detail }}</small>
                  </span>
                  <span class="sms-sugg-price">
                    ${{ s.costUsd.toFixed(2) }}
                    <small>save ${{ s.saveUsd.toFixed(2) }}</small>
                  </span>
                </div>
                <div class="sms-advisor-lastresort">
                  As a last resort, a smaller audience also reduces cost — but reach
                  usually matters more than credit savings.
                </div>
              </div>

              <!-- TEAMIO-INTERNAL economics — dev hosts + super admin ONLY -->
              <div v-if="smsInternalCostVisible" class="sms-internal">
                <div class="sms-internal-title">
                  <i class="fa fa-lock mr-1"></i>
                  Teamio internal — dev + super admin only
                </div>
                <div class="sms-line">
                  <span class="sms-line-label">Cost to Teamio ({{ smsCampaignTotalSegments.toLocaleString() }} segs × ${{ smsInternalCost.ratePerSegment }} Twilio + carrier)</span>
                  <span class="sms-line-val">${{ smsInternalCost.costUsd.toFixed(2) }}</span>
                </div>
                <div class="sms-line">
                  <span class="sms-line-label">Customer would be charged (overage only)</span>
                  <span class="sms-line-val">${{ smsInternalCost.chargeUsd.toFixed(2) }}</span>
                </div>
                <div class="sms-line">
                  <span class="sms-line-label">Net to Teamio</span>
                  <span class="sms-line-val" :class="smsInternalCost.netNegative ? 'text-danger' : 'text-success'">{{ smsInternalCost.netDisplay }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Inline SMS template list -->
          <div class="mt-3">
            <div v-if="smsTemplatesLoading" class="text-muted small py-2">
              <i class="fa fa-spinner fa-spin mr-1"></i> Loading templates...
            </div>
            <template v-else-if="smsTemplateList.length > 0">
              <p class="text-muted small mb-2">
                <i class="fa fa-mouse-pointer mr-1"></i>Click a template below to prefill your message
              </p>
              <b-form-input
                v-model="smsTemplateSearch"
                placeholder="Filter by name..."
                size="sm"
                class="mb-2"
                @input="smsTemplatePage = 1"
              />
              <div style="max-height: 520px; overflow-y: auto; display: flex; flex-direction: column; gap: 8px; padding: 4px;">
                <div
                  v-for="t in paginatedSmsTemplates"
                  :key="t.WikiPageID"
                  class="wiki-template-card"
                  @click="applySmsTemplate(t)"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <span class="wiki-template-card-name">{{ t.Name }}</span>
                    <div style="display: flex; flex-wrap: wrap; gap: 4px; flex-shrink: 0; margin-left: 8px;">
                      <span
                        v-for="tag in (t.Tags || '').split(',')"
                        :key="tag"
                        class="wiki-tag-badge"
                      >{{ tag.trim() }}</span>
                    </div>
                  </div>
                  <p style="color: #64748b; font-size: 0.8rem; margin: 4px 0 0 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ parsedTemplatePreview(t) }}</p>
                  <div class="wiki-template-card-hint">
                    <i class="fa fa-check-circle mr-1"></i>Click to use this template
                  </div>
                </div>
              </div>
              <div v-if="smsTemplateTotalPages > 1" class="d-flex justify-content-between align-items-center mt-2" style="padding: 4px 0;">
                <small class="text-muted">
                  Page {{ smsTemplatePage }} of {{ smsTemplateTotalPages }}
                  ({{ filteredSmsTemplates.length }} templates)
                </small>
                <div>
                  <b-button size="sm" variant="outline-secondary" :disabled="smsTemplatePage <= 1" @click="smsTemplatePage--">
                    <i class="fa fa-chevron-left"></i>
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" class="ml-1" :disabled="smsTemplatePage >= smsTemplateTotalPages" @click="smsTemplatePage++">
                    <i class="fa fa-chevron-right"></i>
                  </b-button>
                </div>
              </div>
            </template>
            <div v-else class="text-muted small py-2">
              <i class="fa fa-info-circle mr-1"></i>
              No SMS templates found. Create wiki pages tagged <strong>sms-template</strong> to use them here.
            </div>
          </div>

          <!-- Phone Mockup Preview (similar to wiki) -->
          <div class="sms-preview-section mt-4">
            <label class="preview-label mb-3">
              <i class="fa fa-mobile mr-2"></i>Preview
            </label>
            <div class="phone-preview-container">
              <div class="phone-mockup-campaign">
                <div class="phone-frame-campaign">
                  <div class="phone-notch-campaign"></div>
                  <div class="phone-screen-campaign">
                    <div class="phone-header-campaign">
                      <span class="phone-time-campaign">{{ currentTime }}</span>
                      <span class="phone-carrier-campaign">TeamIO</span>
                    </div>
                    <div class="phone-content-campaign">
                      <!-- Show split messages if applicable -->
                      <template v-if="smsSplitPreview && smsSplitPreview.length > 1">
                        <div 
                          v-for="(segment, idx) in smsSplitPreview" 
                          :key="'seg-'+idx" 
                          class="message-bubble-campaign outgoing"
                          :class="{'split-bubble': true}"
                        >
                          <div class="split-indicator-campaign">{{ idx + 1 }}/{{ smsSplitPreview.length }}</div>
                          <div class="message-text-campaign">{{ segment }}</div>
                          <div class="message-time-campaign">{{ idx === 0 ? 'Just now' : '' }}</div>
                        </div>
                      </template>
                      <!-- Single message -->
                      <template v-else>
                        <div class="message-bubble-campaign outgoing">
                          <div class="message-text-campaign">{{ smsPreviewText || 'Your message will appear here...' }}</div>
                          <div class="message-time-campaign">Just now</div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="phone-label-campaign">📱 SMS Preview</div>
              </div>
              
              <!-- SMS Character Counter -->
              <div class="sms-counter-campaign" :class="smsCounterClass">
                <div class="counter-main-campaign">
                  <span class="char-count-campaign">{{ smsCharCount }}</span>
                  <span class="char-limit-campaign">/ {{ smsMaxCharsForOneSegment }} characters</span>
                </div>
                <div class="segment-info-campaign">
                  <span class="segment-badge-campaign" :class="{'multi-segment': smsSegments > 1}">
                    <i class="fa" :class="smsSegments > 1 ? 'fa-clone' : 'fa-commenting-o'"></i>
                    {{ smsSegments }} {{ smsSegments === 1 ? 'message' : 'messages' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Email Content -->
        <div v-if="form.type === 'Email' || form.type === 'Both'" class="message-section">
          <h6 class="section-label">
            <i class="fa fa-envelope text-primary mr-2"></i>Email Message
          </h6>

          <!-- When template IS selected: show read-only subject + preview + remove button -->
          <div v-if="selectedEmailTemplate">
            <div class="email-subject-display mb-3" style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px;">
              <small class="text-muted text-uppercase" style="font-size: 0.7rem; font-weight: 600; letter-spacing: 0.03em;">Subject Line</small>
              <div style="font-size: 0.95rem; color: #334155; margin-top: 2px;">{{ form.emailSubject }}</div>
            </div>

            <div class="email-template-preview mb-2">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <strong class="small text-muted">
                  <i class="fa fa-file-text mr-1"></i>{{ selectedEmailTemplate.Name }}
                </strong>
                <b-button size="sm" variant="outline-danger" @click="clearEmailTemplate">
                  <i class="fa fa-times mr-1"></i>Remove Template
                </b-button>
              </div>
              <div class="email-html-preview" v-html="form.emailContent"></div>
            </div>
          </div>

          <!-- When NO template selected: show only the template list -->
          <div v-else>
            <div class="text-center py-3 mb-3" style="background: #f0f9ff; border: 1px dashed #93c5fd; border-radius: 8px;">
              <i class="fa fa-envelope-open-o text-primary" style="font-size: 1.5rem;"></i>
              <p class="mb-0 mt-2" style="color: #1e40af; font-size: 0.9rem; font-weight: 500;">Select an email template below</p>
              <small class="text-muted">Choose from your wiki templates tagged with "email-template"</small>
            </div>
          </div>

          <!-- Inline email template list (always visible when no template selected) -->
          <div v-if="!selectedEmailTemplate" class="mt-2">
            <div v-if="emailTemplatesLoading" class="text-muted small py-2">
              <i class="fa fa-spinner fa-spin mr-1"></i> Loading templates...
            </div>
            <template v-else-if="emailTemplateList.length > 0">
              <p class="text-muted small mb-2">
                <i class="fa fa-mouse-pointer mr-1"></i>Click a template to use it for your email campaign
              </p>
              <b-form-input
                v-model="emailTemplateSearch"
                placeholder="Filter by name..."
                size="sm"
                class="mb-2"
                @input="emailTemplatePage = 1"
              />
              <div style="max-height: 520px; overflow-y: auto; display: flex; flex-direction: column; gap: 8px; padding: 4px;">
                <div
                  v-for="t in paginatedEmailTemplates"
                  :key="t.WikiPageID"
                  class="wiki-template-card"
                  @click="applyEmailTemplate(t)"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <span class="wiki-template-card-name">{{ t.Name }}</span>
                    <div style="display: flex; flex-wrap: wrap; gap: 4px; flex-shrink: 0; margin-left: 8px;">
                      <span
                        v-for="tag in (t.Tags || '').split(',')"
                        :key="tag"
                        class="wiki-tag-badge"
                      >{{ tag.trim() }}</span>
                    </div>
                  </div>
                  <p style="color: #64748b; font-size: 0.8rem; margin: 4px 0 0 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ parsedTemplatePreview(t) }}</p>
                  <div class="wiki-template-card-hint">
                    <i class="fa fa-check-circle mr-1"></i>Click to use this template
                  </div>
                </div>
              </div>
              <div v-if="emailTemplateTotalPages > 1" class="d-flex justify-content-between align-items-center mt-2" style="padding: 4px 0;">
                <small class="text-muted">
                  Page {{ emailTemplatePage }} of {{ emailTemplateTotalPages }}
                  ({{ filteredEmailTemplates.length }} templates)
                </small>
                <div>
                  <b-button size="sm" variant="outline-secondary" :disabled="emailTemplatePage <= 1" @click="emailTemplatePage--">
                    <i class="fa fa-chevron-left"></i>
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" class="ml-1" :disabled="emailTemplatePage >= emailTemplateTotalPages" @click="emailTemplatePage++">
                    <i class="fa fa-chevron-right"></i>
                  </b-button>
                </div>
              </div>
            </template>
            <div v-else class="text-muted small py-2">
              <i class="fa fa-info-circle mr-1"></i>
              No email templates found. Create wiki pages tagged <strong>email-template</strong> to use them here.
            </div>
          </div>

          <!-- Tracking Options Section -->
          <div class="tracking-section mt-4">
            <div
              class="tracking-header"
              @click="showTrackingOptions = !showTrackingOptions"
              style="cursor: pointer;"
            >
              <i class="fa" :class="showTrackingOptions ? 'fa-chevron-down' : 'fa-chevron-right'" style="width: 16px;"></i>
              <span class="ml-1"><i class="fa fa-bar-chart mr-1"></i> Tracking options</span>
            </div>
            <b-collapse v-model="showTrackingOptions" class="mt-3">
              <b-card class="tracking-options-card">
                <div class="tracking-option">
                  <b-form-checkbox v-model="form.trackClicks" switch>
                    <strong>Click tracking</strong>
                    <p class="text-muted mb-0 small">Track when recipients click links in your email</p>
                  </b-form-checkbox>
                </div>
              </b-card>
            </b-collapse>
          </div>
        </div>
        </template>
      </div>

      <!-- Step 4: Schedule & Review -->
      <div v-show="currentStep === 3" class="step-content">
        <h4 class="step-title">Schedule & Review</h4>
        <p class="step-subtitle">Review your campaign and choose when to send</p>

        <!-- Schedule Options -->
        <div class="schedule-section mb-4">
          <h6 class="section-label mb-3">
            <i class="fa fa-clock-o mr-2"></i>When to Send
          </h6>
          
          <div class="schedule-options">
            <div 
              class="schedule-option"
              :class="{ 'selected': form.scheduleType === 'now' }"
              @click="form.scheduleType = 'now'"
            >
              <i class="fa fa-bolt option-icon"></i>
              <div>
                <strong>Send Immediately</strong>
                <p class="mb-0">Campaign will start sending right away</p>
              </div>
            </div>
            <div 
              class="schedule-option"
              :class="{ 'selected': form.scheduleType === 'scheduled' }"
              @click="form.scheduleType = 'scheduled'"
            >
              <i class="fa fa-calendar option-icon"></i>
              <div>
                <strong>Schedule for Later</strong>
                <p class="mb-0">Pick a specific date and time</p>
              </div>
            </div>
            <div 
              class="schedule-option"
              :class="{ 'selected': form.scheduleType === 'draft' }"
              @click="form.scheduleType = 'draft'"
            >
              <i class="fa fa-save option-icon"></i>
              <div>
                <strong>Save as Draft</strong>
                <p class="mb-0">Save and send later</p>
              </div>
            </div>
          </div>

          <div v-if="form.scheduleType === 'scheduled'" class="schedule-datetime mt-3">
            <div class="d-flex align-items-center" style="gap: 1rem;">
              <div>
                <label class="small text-muted mb-1">Date</label>
                <input type="date" class="form-control form-control-sm" v-model="form.scheduledDate" :min="minScheduleDate" style="width: 170px;" />
              </div>
              <div>
                <label class="small text-muted mb-1">Time</label>
                <div class="d-flex align-items-center" style="gap: 0.35rem;">
                  <select class="form-control form-control-sm" v-model="scheduledHour" style="width: 70px;">
                    <option value="" disabled>Hr</option>
                    <option v-for="h in 12" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <span style="font-weight: 600;">:</span>
                  <select class="form-control form-control-sm" v-model="scheduledMinute" style="width: 70px;">
                    <option value="00">00</option>
                    <option value="15">15</option>
                    <option value="30">30</option>
                    <option value="45">45</option>
                  </select>
                  <select class="form-control form-control-sm" v-model="scheduledAmPm" style="width: 70px;">
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Credit outlook for a LATER send. A scheduled campaign fires unattended: if the
                 wallet runs dry mid-send it simply stops, delivering to some patients and not
                 others, with nobody told. Advisory only — it never blocks scheduling. Hidden
                 entirely when auto-reload is on (that is consent) or when we cannot tell. -->
            <div v-if="scheduleCreditWarning.warn" class="schedule-credit-warning mt-3" data-test="schedule-credit-warning">
              <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
              <div>
                <div class="scw-title">This campaign may not reach everyone</div>
                <div class="scw-body">
                  <span v-for="s in scheduleCreditWarning.shortfalls" :key="s.product" class="scw-line">
                    <strong>{{ s.product === 'email' ? 'Email' : 'SMS' }}:</strong>
                    needs ~{{ s.creditsNeeded.toLocaleString() }} credits,
                    {{ s.creditsAvailable.toLocaleString() }} available
                    <em>(short {{ s.shortfall.toLocaleString() }})</em>
                  </span>
                </div>
                <div class="scw-action">
                  Auto-reload is off, so sending stops when credits run out. Top up before
                  {{ formatScheduleDate }}, or turn on auto-reload in My&nbsp;Account&nbsp;&rsaquo;&nbsp;Credits.
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Send Test Message -->
        <div class="send-test-section mb-3">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="openSendTestModal"
          >
            <i class="fa fa-paper-plane mr-1"></i> Send Test Message
          </b-button>
          <small class="text-muted ml-2">Send a test before launching your campaign</small>
        </div>

        <!-- Review Summary -->
        <div class="review-summary">
          <h6 class="section-label mb-3">
            <i class="fa fa-check-square-o mr-2"></i>Campaign Summary
          </h6>
          
          <b-card class="summary-card">
            <b-row>
              <b-col md="6">
                <div class="summary-item">
                  <label>Campaign Name</label>
                  <p>{{ form.name || '-' }}</p>
                </div>
              </b-col>
              <b-col md="6">
                <div class="summary-item">
                  <label>Type</label>
                  <p>
                    <b-badge :variant="getTypeVariant(form.type)">{{ form.type }}</b-badge>
                  </p>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <div class="summary-item">
                  <label>Recipients</label>
                  <p>{{ effectiveRecipientCount | formatNumber }} patients</p>
                </div>
              </b-col>
              <b-col md="6">
                <div class="summary-item">
                  <label>Schedule</label>
                  <p>
                    <span v-if="form.scheduleType === 'now'">Send Immediately</span>
                    <span v-else-if="form.scheduleType === 'scheduled'">
                      {{ formatScheduleDate }}
                    </span>
                    <span v-else>Save as Draft</span>
                  </p>
                </div>
              </b-col>
            </b-row>
            
            <hr>
            
            <div v-if="form.type === 'SMS' || form.type === 'Both'" class="summary-item">
              <label>SMS Content</label>
              <div class="content-preview">
                {{ form.smsContent || 'No content' }}
              </div>
            </div>
            
            <div v-if="form.type === 'Email' || form.type === 'Both'" class="summary-item mt-3">
              <label>Email Subject</label>
              <p>{{ form.emailSubject || 'No subject' }}</p>
              <label>Email Content</label>
              <div class="email-html-preview review-email-preview" v-html="form.emailContent"></div>
            </div>
          </b-card>

        </div>
      </div>
    </div>

    <!-- Send Test Modal -->
    <b-modal
      id="send-test-modal"
      ref="sendTestModal"
      title="Send Test Message"
      size="md"
      centered
      @ok="sendTestMessage"
      :ok-title="sendingTest ? 'Sending...' : 'Send Test'"
      :ok-disabled="!canSendTest || sendingTest"
    >
      <p class="text-muted mb-3">Enter the email or phone number to send a test message to.</p>

      <div v-if="form.type === 'Email' || form.type === 'Both'" class="mb-3">
        <b-form-group label="Test Email Address">
          <b-form-input
            v-model="testEmail"
            type="email"
            placeholder="Enter email address..."
          />
        </b-form-group>
      </div>

      <div v-if="form.type === 'SMS' || form.type === 'Both'">
        <b-form-group label="Test Phone Number">
          <b-form-input
            v-model="testPhone"
            type="tel"
            placeholder="Enter phone number..."
          />
        </b-form-group>
      </div>

      <!-- Info alert about variable resolution -->
      <div v-if="form.type === 'SMS' || form.type === 'Both'" class="mt-3" style="background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; border-radius: 6px; padding: 10px 14px; font-size: 0.85rem;">
        <i class="fa fa-info-circle mr-1"></i>
        <strong>Site variables</strong> (e.g., <code v-pre>{{site_name}}</code>, <code v-pre>{{site_officephone}}</code>) will use your real practice info.
        <strong>Patient variables</strong> (e.g., <code v-pre>{{patient_first_name}}</code>, <code v-pre>{{apt_date}}</code>) will use sample data in the test message.
      </div>

      <b-alert v-if="testMessageSent" variant="success" show class="mt-3">
        <i class="fa fa-check-circle mr-1"></i> Test message sent successfully!
      </b-alert>

      <b-alert v-if="testError" variant="danger" show class="mt-3">
        <i class="fa fa-exclamation-circle mr-1"></i> {{ testError }}
      </b-alert>
    </b-modal>

    <!-- Wizard Footer -->
    <div v-if="!campaignSent" class="wizard-footer">
      <b-button 
        variant="outline-secondary" 
        @click="previousStep"
        :disabled="currentStep === 0"
      >
        <i class="fa fa-arrow-left mr-1"></i> Previous
      </b-button>
      
      <div class="footer-right">
        <b-button variant="outline-secondary" @click="hide" class="mr-2">
          Cancel
        </b-button>
        <b-button
          v-if="currentStep < steps.length - 1"
          variant="outline-success"
          @click="saveDraft"
          class="mr-2"
        >
          <i class="fa fa-save mr-1"></i> Save as Draft
        </b-button>
        <b-button
          v-if="currentStep < steps.length - 1"
          variant="primary"
          @click="nextStep"
          :disabled="!canProceed"
        >
          Next <i class="fa fa-arrow-right ml-1"></i>
        </b-button>
        <b-button
          v-else
          variant="success"
          @click="saveCampaign"
          :disabled="!canSave"
        >
          <i class="fa fa-check mr-1"></i>
          {{ form.scheduleType === 'now' ? 'Create & Send' : form.scheduleType === 'scheduled' ? 'Schedule Campaign' : 'Save Draft' }}
        </b-button>
      </div>
    </div>
    <!-- Eligibility warning: shown when advancing from Step 2 with ineligible recipients -->
    <b-modal
      v-model="showEligibilityWarning"
      title="Some Recipients Won't Receive This Campaign"
      hide-footer
      centered
    >
      <div class="mb-3">
        <p v-if="form.type === 'SMS'">
          <strong>{{ ineligiblePatients.length | formatNumber }}</strong> patient<span v-if="ineligiblePatients.length !== 1">s</span> in your list
          have no mobile phone number and will not receive this SMS campaign.
        </p>
        <p v-else-if="form.type === 'Email'">
          <strong>{{ ineligiblePatients.length | formatNumber }}</strong> patient<span v-if="ineligiblePatients.length !== 1">s</span> in your list
          have no email address and will not receive this email campaign.
        </p>
        <p v-else-if="form.type === 'Both'">
          <strong>{{ ineligiblePatients.length | formatNumber }}</strong> patient<span v-if="ineligiblePatients.length !== 1">s</span> in your list
          have no mobile number or email and cannot be reached by this campaign.
        </p>
        <p class="text-muted small mb-0">
          These patients will be removed from your recipient list. Your campaign will be sent to the remaining
          <strong>{{ (effectiveRecipientCount - ineligiblePatients.length) | formatNumber }}</strong> patient<span v-if="(effectiveRecipientCount - ineligiblePatients.length) !== 1">s</span>.
        </p>
      </div>
      <div class="d-flex justify-content-end" style="gap: 8px;">
        <b-button variant="outline-secondary" @click="cancelEligibilityFilter">Go Back</b-button>
        <b-button
          variant="primary"
          :disabled="effectiveRecipientCount - ineligiblePatients.length <= 0"
          @click="applyEligibilityFilterAndAdvance"
        >
          Update List &amp; Continue
        </b-button>
      </div>
    </b-modal>

    <!-- Explicit over-allowance charge approval (checkbox names the exact amount). -->
    <overage-approval-modal ref="overageModal" />
  </b-modal>
</template>

<script>
import moment from 'moment-timezone';
import { mapState } from 'vuex';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import mixin from '../../../mixin.js';
import CustomFilterBuilder from './filters/CustomFilterBuilder.vue';
import CommonOverlay from '../../../components/CommonOverlay.vue';
import { triggerAutorecallTestCall } from '../../../api/autorecall';
import { evaluateTestTargets, TEST_CALL_MAX } from '../../../utils/aiCallTestGuard';
import { analyzeSms, normalizeSms, estimateOverageCharge, OVERAGE_PRICE_PER_SEGMENT_USD, COST_PER_SEGMENT_USD, CARRIER_MAX_SEGMENTS_PER_MESSAGE } from '../../../utils/smsSegments';
import { interpretSendGate, buildVolumeConfirmMessage, buildPaymentFailedMessage } from '../../../utils/campaignGate';
import { scheduleCreditOutlook } from '../../../utils/campaignCreditOutlook';
// Aliased: the computed props below are also named isSmsEnabled/isEmailEnabled. The bare
// identifier would still resolve to the import (not the computed), but the alias removes
// any doubt for the next reader.
import { isSmsEnabled as siteHasSms, isEmailEnabled as siteHasEmail, hasSmsNumber, isSmsOutboundDisabled } from '../../../utils/siteCapability';
import OverageApprovalModal from './OverageApprovalModal.vue';
import { extractApiError } from '@/utils/apiError';
import { withoutReviewRequestTemplates } from '@/utils/wikiTags';
import { resolveSaveTarget } from '../../../utils/campaignSend';
import {
  shouldFetchRanking,
  isStaleRankingFetch,
  applyPinExclude,
  effectiveCallCount,
  buildWhy,
  rankedDisplayName,
  buildPreviewConfig,
  buildPriorityConfig,
  togglePriorityConfig,
  unionPatNums,
  availableToAdd
} from '../../../utils/aiCallRanking';

export default {
  name: 'CreateCampaignModal',
  components: { CustomFilterBuilder, Multiselect, CommonOverlay, OverageApprovalModal },
  props: {
    campaign: {
      type: Object,
      default: null
    },
    isEditing: {
      type: Boolean,
      default: false
    }
  },
  mixins: [mixin],

  data() {
    return {
      currentStep: 0,
      steps: [
        { key: 'details', label: 'Details' },
        { key: 'recipients', label: 'Recipients' },
        { key: 'content', label: 'Content' },
        { key: 'review', label: 'Review' }
      ],
      
      savingDraft: false,
      // Loading the full campaign data (TextContent/HTMLContent) when opening for edit.
      // Drives the same CommonOverlay as savingDraft so the popup shows feedback immediately.
      loadingDetails: false,
      overlayTitle: 'Saving',
      overlayMessage: 'Please wait...',

      // The campaign this wizard session already created (the create action's result).
      // A "Create & Send" whose SEND fails keeps the wizard open with isEditing still false —
      // the next click must UPDATE + re-send THAT row, never create a sibling campaign.
      // Cleared in resetForm() when the modal closes.
      createdCampaign: null,

      recipientSubStep: 0,
      customFiltersValid: false,
      previewLoading: false,
      previewError: '',

      form: {
        name: '',
        description: '',
        type: 'SMS',
        // AI Call: which follow-up list the calls count toward (3=Overdue Recall, 4=Unscheduled Treatment)
        callFollowupTaskTypeID: 3,
        // AI Call: which patients to call first (sent to backend as-is). 'standard' = no prioritization.
        priorityPreset: 'standard',
        // AI Call: operator pin/exclude edits over the ranked list. { pin:[PatNum...], exclude:[PatNum...] }.
        // Empty by default; sent (and persisted in drafts) only when non-empty.
        priorityConfig: { pin: [], exclude: [] },
        // AI Call (Phase D.2c): patients hand-added to the call list who aren't in the recall
        // audience. Minimal rows {PatNum, FName, LName, WirelessPhone}. They're unioned into the
        // effective audience (effectiveAiCallPatNums) so they flow to preview ranking AND launch.
        handAddedPatients: [],
        recipientSource: 'customFilters',
        selectedList: null,
        customFilters: {
          type: 'customFilters',
          groups: [{ id: 1, filters: [] }]
        },
        smsTemplate: null,
        smsContent: '',
        // 'optimized' (default) = auto-replace non-GSM chars (smart quotes, (R)...)
        // at send time; 'original' = customer chose to keep them and pay for the
        // extra Unicode segments. See src/utils/smsSegments.js.
        smsFormatting: 'optimized',
        // Phase 2 per-send override: include dead/disconnected numbers this send only
        includeSuppressed: false,
        emailTemplate: null,
        emailSubject: '',
        emailPreviewText: '',
        emailContent: '',
        // Tracking options
        trackClicks: true,
        // Perfect timing
        scheduleType: 'now',
        scheduledDate: null
      },

      // Saved lists for reuse
      savedLists: [],
      savedListsLoading: false,

      // Overdue Recall & Unscheduled Treatment lists
      recallLists: [],
      unscheduledLists: [],
      siteListsLoading: false,
      siteListsCollapsed: false,
      loadingListID: null,
      loadingCampaignID: null,
      campaignListsCollapsed: false,
      recallListPage: 1,
      unscheduledListPage: 1,
      listsPerPage: 5,

      // AI Call test-call state — tester picks targets (patient + number); never auto-dials the list
      testTargets: [{ patNum: null, phone: '' }],
      placingTestCalls: false,
      testCallResults: [],

      // AI Call ranked-list state (Phase D.2b) — the editable "who would be called" preview.
      // rankedPatients holds the backend-ordered rows; pin/exclude live in form.priorityConfig
      // and are applied client-side for display.
      rankedPatients: [],
      rankingLoading: false,
      rankingError: '',

      // AI Call hand-add picker (Phase D.2c) — transient v-model for the patient multiselect.
      // Selecting a patient pushes it into form.handAddedPatients then clears this back to null.
      handAddPatientSelected: null,

      // Post-send confirmation state
      campaignSent: false,
      sentRecipientCount: 0,
      sentSkippedCount: 0,

      // Legacy patient lists (kept for backwards compat)
      patientLists: [],

      smsTemplateList: [],
      smsTemplatesLoading: false,
      smsTemplateSearch: '',
      smsTemplatePage: 1,
      smsLinkMap: {},

      emailTemplateList: [],
      emailTemplatesLoading: false,
      emailTemplateSearch: '',
      emailTemplatePage: 1,
      selectedEmailTemplate: null,

      templatesPerPage: 10,

      // AI Call follow-up list type options (TaskTypeID 3=Overdue Recall, 4=Unscheduled Treatment)
      aiCallFollowupTypeOptions: [
        { value: 3, text: 'Overdue Recall' },
        { value: 4, text: 'Unscheduled Treatment' }
      ],

      // AI Call: which patients to call first. Values are sent to the backend as-is.
      aiCallPriorityPresetOptions: [
        { value: 'standard', text: 'Standard — no prioritization' },
        { value: 'max_revenue', text: 'Maximize revenue' },
        { value: 'reactivate_lapsed', text: 'Reactivate lapsed patients' },
        { value: 'balanced', text: 'Balanced' }
      ],

      smsInsertVariables: [
        { key: 'firstName', name: 'First Name',      template: '{{patient_first_name}}' },
        { key: 'lastName',  name: 'Last Name',        template: '{{patient_last_name}}' },
        { key: 'siteName',  name: 'Practice Name',    template: '{{site_name}}' },
        { key: 'sitePhone', name: 'Practice Phone',   template: '{{site_officephone}}' },
        { key: 'siteAddr',  name: 'Practice Address', template: '{{site_address1}}' },
      ],

      zipCodeOptions: [],
      insuranceOptions: { carriers: [], employers: [], groupNumbers: [] },
      recallTypeOptions: [],

      recipientCount: 0,
      smsEligibleCount: 0,
      smsSuppressedCount: 0,
      // Month-to-date texts used vs plan allowance (null = not loaded yet;
      // {available:false} = site has no Twilio subaccount → hide the block)
      smsMonthUsage: null,
      emailEligibleCount: 0,
      previewPatients: [],
      patientCurrentPage: 1,
      patientPerPage: 10,
      patientSearch: '',
      selectedPatNums: [],
      removedPatNums: [],
      removalMode: false,
      showEligibilityWarning: false,

      // Schedule for later (time picker)
      scheduledHour: '',
      scheduledMinute: '00',
      scheduledAmPm: 'AM',

      // Scheduled patients
      scheduledTimeframe: 'today',
      scheduledDateFrom: null,
      scheduledDateTo: null,
      scheduledProviders: [],

      // All Active Patients config
      allActiveTimeframeMode: 'relative',
      allActiveTimeframeValue: 18,
      allActiveTimeframeUnit: 'months',
      allActiveDateFrom: '',
      allActiveDateTo: '',
      allActiveProviders: [],
      allActiveExcludedProcCodes: [],
      showRemoveConfirm: false,
      showKeepRemovalsPrompt: false,
      pendingPreviewResult: null,

      validated: false,
      smsPreviewText: '',
      showPreviewText: false,
      showTrackingOptions: false,

      // Test message
      testEmail: '',
      testPhone: '',
      testMessageSent: false,
      sendingTest: false,
      testError: '',

      // Common emojis for email subject lines
      commonEmojis: [
        '😀', '😊', '🎉', '🎊', '✨', '⭐', '🌟', '💫',
        '❤️', '💙', '💚', '💛', '🧡', '💜', '🖤', '🤍',
        '👋', '👍', '👏', '🙌', '💪', '🤝', '✌️', '🤞',
        '🔥', '💥', '⚡', '🌈', '☀️', '🌙', '❄️', '🌸',
        '🎁', '🎂', '🎈', '🏆', '🥇', '💰', '💵', '💎',
        '📣', '📢', '🔔', '💡', '📌', '📍', '🎯', '✅',
        '⏰', '📅', '🗓️', '⏳', '🆕', '🆓', '💯', '‼️',
        '🦷', '😁', '💊', '🏥', '👨‍⚕️', '👩‍⚕️', '🩺', '💉'
      ],

    };
  },

  computed: {
    ...mapState('customer', ['settings', 'procCodes', 'patients']),
    
    // Helper to get allSettings (transformed object) or fallback to array
    allSettings() {
      const customerState = this.$store.state.customer;
      return customerState?.allSettings || null;
    },

    patientTableFields() {
      const fields = [];
      if (this.removalMode) {
        fields.push({ key: 'select', label: '', sortable: false, thStyle: { width: '40px' } });
      }
      fields.push({ key: 'name', label: 'Name', sortable: true });
      if (this.form.recipientSource === 'scheduledPatients') {
        fields.push({ key: 'ProviderName', label: 'Provider', sortable: true });
      }
      fields.push(
        { key: 'WirelessPhone', label: 'Mobile Phone', sortable: false },
        { key: 'Email', label: 'Email', sortable: false }
      );
      return fields;
    },
    activePreviewPatients() {
      return this.previewPatients.filter(p => !this.removedPatNums.includes(p.PatNum));
    },
    filteredPreviewPatients() {
      if (!this.patientSearch.trim()) return this.activePreviewPatients;
      const q = this.patientSearch.trim().toLowerCase();
      return this.activePreviewPatients.filter(p =>
        (p.LName && p.LName.toLowerCase().startsWith(q)) ||
        (p.FName && p.FName.toLowerCase().startsWith(q))
      );
    },
    paginatedRecallLists() {
      const start = (this.recallListPage - 1) * this.listsPerPage;
      return this.recallLists.slice(start, start + this.listsPerPage);
    },
    paginatedUnscheduledLists() {
      const start = (this.unscheduledListPage - 1) * this.listsPerPage;
      return this.unscheduledLists.slice(start, start + this.listsPerPage);
    },
    allVisibleSelected() {
      return this.filteredPreviewPatients.length > 0 &&
        this.filteredPreviewPatients.every(p => this.selectedPatNums.includes(p.PatNum));
    },
    effectiveRecipientCount() {
      const currentPatNums = this.previewPatients.map(p => p.PatNum);
      const activeRemovals = this.removedPatNums.filter(pn => currentPatNums.includes(pn));
      return this.recipientCount - activeRemovals.length;
    },

    // ── AI Call ranked list (Phase D.2b / D.2c) ────────────────────────────
    // PatNums of the patients hand-added to the call list (Phase D.2c).
    handAddedPatNums() {
      return (this.form.handAddedPatients || []).map(p => p.PatNum);
    },
    // SINGLE SOURCE OF TRUTH for the effective AI-Call audience: the deduped UNION of the
    // recall audience (activePreviewPatients) + the hand-added patients. This feeds BOTH the
    // preview-ranking fetch and the launch selectedList, so hand-adds rank and dial like
    // everyone else. A hand-added patient already in the audience is not double-counted.
    effectiveAiCallPatNums() {
      return unionPatNums(this.activePreviewPatients.map(p => p.PatNum), this.handAddedPatNums);
    },
    // The current chosen audience (PatNums) for the AI-Call preview/launch — now the union
    // so hand-adds flow through the existing watcher/fetch/payload plumbing unchanged.
    aiCallSelectedPatNums() {
      return this.effectiveAiCallPatNums;
    },
    // Patients offerable in the hand-add picker: customer.patients minus everyone already in
    // the effective audience (so we never offer a duplicate). Empty when the global list
    // hasn't loaded — the picker degrades gracefully (disabled) in that case.
    availableHandAddPatients() {
      return availableToAdd(this.patients || [], this.effectiveAiCallPatNums);
    },
    // The PatNums sent as the launch/draft `selectedList`. For ai_call this is the effective
    // union (audience + hand-adds); every other channel is unchanged (audience only). Null
    // when empty so the payload stays clean (mirrors the prior activePreviewPatients logic).
    campaignSendPatNums() {
      const list = this.form.type === 'ai_call'
        ? this.effectiveAiCallPatNums
        : this.activePreviewPatients.map(p => p.PatNum);
      return list.length > 0 ? list : null;
    },
    // Gates whether to FETCH the ranked list (ai_call + enabled + non-standard preset + an
    // audience). NOTE: this is NOT the section's visibility — the template v-if uses an
    // audience-only condition so the standard-preset note still renders. Do not DRY the
    // template against this or the standard note will disappear.
    shouldFetchRankedList() {
      return shouldFetchRanking(
        this.form.type,
        this.isAiCallEnabled,
        this.form.priorityPreset,
        this.aiCallSelectedPatNums.length
      );
    },
    // Backend-ordered rows decorated with pin/exclude state and a display rank.
    displayRankedPatients() {
      return applyPinExclude(this.rankedPatients, this.form.priorityConfig);
    },
    // "Will call N of M" — N excludes the operator's exclusions.
    rankedEffectiveCount() {
      return effectiveCallCount(this.rankedPatients, this.form.priorityConfig);
    },
    rankedFields() {
      return [
        { key: '_rank', label: '#', thStyle: { width: '44px' } },
        { key: 'name', label: 'Patient' },
        { key: 'why', label: 'Why' },
        { key: 'actions', label: '', thStyle: { width: '150px' } }
      ];
    },
    filteredSmsTemplates() {
      let list = this.smsTemplateList;
      if (this.smsTemplateSearch.trim()) {
        const q = this.smsTemplateSearch.toLowerCase();
        list = list.filter(t => t.Name && t.Name.toLowerCase().includes(q));
      }
      return list;
    },
    paginatedSmsTemplates() {
      const start = (this.smsTemplatePage - 1) * this.templatesPerPage;
      return this.filteredSmsTemplates.slice(start, start + this.templatesPerPage);
    },
    smsTemplateTotalPages() {
      return Math.ceil(this.filteredSmsTemplates.length / this.templatesPerPage) || 1;
    },
    filteredEmailTemplates() {
      let list = this.emailTemplateList;
      if (this.emailTemplateSearch.trim()) {
        const q = this.emailTemplateSearch.toLowerCase();
        list = list.filter(t => t.Name && t.Name.toLowerCase().includes(q));
      }
      return list;
    },
    paginatedEmailTemplates() {
      const start = (this.emailTemplatePage - 1) * this.templatesPerPage;
      return this.filteredEmailTemplates.slice(start, start + this.templatesPerPage);
    },
    emailTemplateTotalPages() {
      return Math.ceil(this.filteredEmailTemplates.length / this.templatesPerPage) || 1;
    },
    ineligiblePatients() {
      const type = this.form.type;
      return this.activePreviewPatients.filter(p => {
        const hasPhone = p.WirelessPhone && p.WirelessPhone.trim() !== '';
        const hasEmail = p.Email && p.Email.trim() !== '';
        if (type === 'SMS')   return !hasPhone;
        if (type === 'Email') return !hasEmail;
        if (type === 'Both')  return !hasPhone && !hasEmail;
        return false;
      });
    },
    scheduledTimeframeOptions() {
      return [
        { text: 'Today', value: 'today' },
        { text: 'Tomorrow', value: 'tomorrow' },
        { text: 'This Week', value: 'thisWeek' },
        { text: 'Next Week', value: 'nextWeek' },
        { text: 'This Month', value: 'thisMonth' },
        { text: 'Next Month', value: 'nextMonth' },
        { text: 'Custom', value: 'custom' }
      ];
    },
    scheduledDateRange() {
      const today = new Date();
      const fmt = (d) => d.toISOString().split('T')[0];
      switch (this.scheduledTimeframe) {
        case 'today': return { from: fmt(today), to: fmt(today) };
        case 'tomorrow': {
          const t = new Date(today); t.setDate(t.getDate() + 1);
          return { from: fmt(t), to: fmt(t) };
        }
        case 'thisWeek': {
          const dow = today.getDay();
          const start = new Date(today); start.setDate(today.getDate() - dow);
          const end = new Date(start); end.setDate(start.getDate() + 6);
          return { from: fmt(start), to: fmt(end) };
        }
        case 'nextWeek': {
          const dow = today.getDay();
          const start = new Date(today); start.setDate(today.getDate() + (7 - dow));
          const end = new Date(start); end.setDate(start.getDate() + 6);
          return { from: fmt(start), to: fmt(end) };
        }
        case 'thisMonth': {
          const start = new Date(today.getFullYear(), today.getMonth(), 1);
          const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          return { from: fmt(start), to: fmt(end) };
        }
        case 'nextMonth': {
          const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
          return { from: fmt(start), to: fmt(end) };
        }
        case 'custom':
          return { from: this.scheduledDateFrom, to: this.scheduledDateTo };
        default: return null;
      }
    },
    canPreviewScheduled() {
      if (this.scheduledTimeframe === 'custom') {
        return this.scheduledDateFrom && this.scheduledDateTo;
      }
      return true;
    },
    canPreviewAllActive() {
      if (this.allActiveTimeframeMode === 'custom') {
        return this.allActiveDateFrom && this.allActiveDateTo;
      }
      return this.allActiveTimeframeValue > 0;
    },
    missingPhoneCount() {
      return this.activePreviewPatients.filter(p => !p.WirelessPhone || p.WirelessPhone.trim() === '').length;
    },
    missingEmailCount() {
      return this.activePreviewPatients.filter(p => !p.Email || p.Email.trim() === '').length;
    },

    procCodeOptions() {
      if (!this.procCodes || !Array.isArray(this.procCodes)) return [];
      return this.procCodes.map(code => ({
        value: code.ProcCode,
        text: `${code.ProcCode} - ${code.Descript || ''}`.trim()
      }));
    },

    providerOptions() {
      const providers = this.$store.state.customer.providers;
      if (!providers || !Array.isArray(providers)) return [];
      return providers
        .filter(p => !p.IsHidden)
        .map(p => ({
          value: p.ProvNum,
          text: `${p.LName}, ${p.FName}`.trim()
        }));
    },

    filterSummaryLines() {
      if (this.form.recipientSource === 'scheduledPatients') {
        return this.buildScheduledSummary();
      }
      if (this.form.recipientSource !== 'customFilters') return [];
      const groups = this.form.customFilters?.groups;
      if (!groups || groups.length === 0) return [];
      const result = [];
      const validGroups = groups.filter(g => g.filters && g.filters.length > 0);
      for (let gi = 0; gi < validGroups.length; gi++) {
        const group = validGroups[gi];
        const lines = group.filters.map(f => this.describeFilter(f)).filter(Boolean);
        for (let fi = 0; fi < lines.length; fi++) {
          const isLastInGroup = fi === lines.length - 1;
          const isLastGroup = gi === validGroups.length - 1;
          let connector = null;
          if (!isLastInGroup) {
            connector = 'AND';
          } else if (!isLastGroup) {
            connector = 'OR';
          }
          result.push({ text: lines[fi], connector });
        }
      }
      return result;
    },

    // Max test calls per run (hard cap) — exposed to the template.
    testCallMax() {
      return TEST_CALL_MAX;
    },

    // Patients the tester can voice in a test call — the resolved campaign audience.
    testPatientOptions() {
      const opts = (this.activePreviewPatients || []).map(p => {
        const name = p.PatientName || p.Name
          || `${p.FName || p.FirstName || ''} ${p.LName || p.LastName || ''}`.trim()
          || `Patient #${p.PatNum}`;
        return { value: p.PatNum, text: name };
      });
      return [{ value: null, text: 'Select patient…' }, ...opts];
    },

    // At least one valid target (patient chosen + dialable number).
    hasTestTargets() {
      return evaluateTestTargets(this.testTargets).count > 0;
    },

    modalTitle() {
      return this.isEditing ? 'Edit Campaign' : 'Create New Campaign';
    },

    formattedSmsNumber() {
      const allSettings = this.allSettings;
      if (allSettings && allSettings.GENERAL_PHONE && allSettings.GENERAL_PHONE.PropertyValue) {
        const raw = allSettings.GENERAL_PHONE.PropertyValue.trim();
        const digits = raw.replace(/\D/g, '');
        if (digits.length === 11 && digits.startsWith('1')) {
          return `(${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
        }
        if (digits.length === 10) {
          return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
        }
        return raw;
      }
      return '';
    },
    // Per-site SMS capability — see src/utils/siteCapability.js (the single
    // implementation; backend counterpart tx-presenter-service/lib/resolveChannels.js).
    // Both settings sources are consulted because `allSettings` is not always populated
    // (only `settings` is, on some paths) — either view answering "provisioned" is enough.
    isSmsEnabled() {
      return siteHasSms(this.allSettings) || siteHasSms(this.settings);
    },

    // WhatsApp-only office (SMS_OUTBOUND_DISABLED): has a number, may not send SMS campaigns
    // (sendCampaign refuses them server-side too). Only changes the locked card's wording.
    smsOutboundOff() {
      const off = (s) => hasSmsNumber(s) && isSmsOutboundDisabled(s);
      return off(this.allSettings) || off(this.settings);
    },

    // Per-site EMAIL capability — see src/utils/siteCapability.js. Note this is NOT
    // "the string is non-empty": GENERAL_VERIFIED_EMAILS has five stored shapes, so a
    // value like "[]" is non-blank yet resolves to NO usable sender. The helper runs the
    // real parser, which is the entitlement check verifiedSender.js prescribes.
    isEmailEnabled() {
      try {
        return siteHasEmail(this.allSettings) || siteHasEmail(this.settings);
      } catch (error) {
        console.error('[Campaign Modal] Error checking isEmailEnabled:', error);
        return false;
      }
    },

    // Check if AI Call follow-ups are enabled (paid for) for this site.
    // Gated by the teamio.settings key AI_FOLLOWUP_PAID_FOR (PropertyValue === 'TRUE').
    isAiCallEnabled() {
      try {
        // First try allSettings (transformed object), then fall back to raw settings array
        const allSettings = this.allSettings;
        const settingsArray = this.settings;

        let paidValue = null;

        if (allSettings && allSettings.AI_FOLLOWUP_PAID_FOR) {
          // Use transformed allSettings object
          paidValue = allSettings.AI_FOLLOWUP_PAID_FOR.PropertyValue;
        } else if (settingsArray && Array.isArray(settingsArray)) {
          // Fallback: search in raw settings array
          const paidSetting = settingsArray.find(s => s.Property === 'AI_FOLLOWUP_PAID_FOR');
          if (paidSetting) {
            paidValue = paidSetting.PropertyValue;
          }
        }

        if (!paidValue) {
          return false;
        }

        return String(paidValue).trim().toUpperCase() === 'TRUE';
      } catch (error) {
        console.error('[Campaign Modal] Error checking isAiCallEnabled:', error);
        return false;
      }
    },

    // Determine default campaign type based on what's enabled
    defaultCampaignType() {
      if (this.isSmsEnabled) {
        return 'SMS'; // Prefer SMS if available
      } else if (this.isEmailEnabled) {
        return 'Email';
      }
      return null; // No default if neither is enabled
    },

    // Available campaign types based on site settings
    availableCampaignTypes() {
      const types = [];
      if (this.isSmsEnabled) {
        types.push('SMS');
      }
      if (this.isEmailEnabled) {
        types.push('Email');
      }
      if (this.isSmsEnabled && this.isEmailEnabled) {
        types.push('Both');
      }
      return types;
    },

    // Check if the current campaign type is valid based on enabled features
    isCampaignTypeValid() {
      if (!this.campaign) return true;
      const campaignType = this.campaign.Type || this.form.type;
      if (campaignType === 'SMS') return this.isSmsEnabled;
      if (campaignType === 'Email') return this.isEmailEnabled;
      if (campaignType === 'Both') return this.isSmsEnabled && this.isEmailEnabled;
      return true;
    },

    canProceed() {
      switch (this.currentStep) {
        case 0:
          // AI Call requires the paid feature on + a follow-up list type chosen
          if (this.form.type === 'ai_call') {
            return this.isAiCallEnabled && !!this.form.callFollowupTaskTypeID && this.form.name.trim().length > 0;
          }
          // Validate that selected type is actually enabled
          if (this.form.type === 'SMS' && !this.isSmsEnabled) return false;
          if (this.form.type === 'Email' && !this.isEmailEnabled) return false;
          if (this.form.type === 'Both' && (!this.isSmsEnabled || !this.isEmailEnabled)) return false;
          return this.form.name.trim().length > 0 && this.form.type;
        case 1:
          if (this.recipientSubStep === 0) return false;
          return this.recipientCount > 0;
        case 2:
          // AI Call has no message to compose — the agent uses its built-in script
          if (this.form.type === 'ai_call') return true;
          if (this.form.type === 'SMS') return this.form.smsContent.trim().length > 0;
          if (this.form.type === 'Email') return this.selectedEmailTemplate !== null;
          return this.form.smsContent.trim().length > 0 && this.selectedEmailTemplate !== null;
        default:
          return true;
      }
    },

    canSave() {
      // Carriers reject messages over the segment cap (error 30019) and bill for
      // the attempt — never let a send/schedule start. Draft is still allowed so
      // they don't lose their work while editing it down.
      if (this.smsCarrierBlocked && this.form.scheduleType !== 'draft') {
        return false;
      }
      if (this.form.scheduleType === 'scheduled') {
        return this.form.scheduledDate && this.scheduledHour !== '';
      }
      return true;
    },

    canSendTest() {
      if (this.form.type === 'Both') {
        return (this.testEmail.trim().length > 0 || this.testPhone.trim().length > 0);
      }
      if (this.form.type === 'Email') {
        return this.testEmail.trim().length > 0;
      }
      if (this.form.type === 'SMS') {
        return this.testPhone.trim().length > 0;
      }
      return false;
    },

    // Variable-substituted text used for all segment math. Computed directly from
    // form.smsContent (not smsPreviewText, which only refreshes on textarea @input)
    // so template prefills and programmatic changes are always measured.
    // NOTE: [Form:]/[Link:] placeholders are measured at display length; the real
    // tracking URLs differ — the server-side gate (sendCampaign) recomputes with
    // final content and is authoritative.
    smsAnalysisSource() {
      let t = this.form.smsContent || '';
      t = t.replace(/\{\{patient_first_name\}\}/g, 'John')
        .replace(/\{\{patient_last_name\}\}/g, 'Smith')
        .replace(/\{\{site_name\}\}/g, (this.user && this.user.SiteName) || 'Dental Care')
        .replace(/\{\{site_officephone\}\}/g, '(555) 123-4567')
        .replace(/\{\{site_address1\}\}/g, '123 Main St')
        .replace(/\[Form: ([^\]]+)\]/g, '[$1]')
        .replace(/\[Link: ([^\]]+)\]/g, '[$1]');
      return t;
    },

    // How Twilio would bill the message exactly as typed (incl. ®, emoji, smart quotes)
    smsAsTyped() {
      return analyzeSms(this.smsAnalysisSource);
    },

    smsNormalization() {
      return normalizeSms(this.smsAnalysisSource);
    },

    // How Twilio would bill after auto-replacing transliterable characters
    smsOptimized() {
      return analyzeSms(this.smsNormalization.text);
    },

    // True when optimizing actually changes the message (there's a choice to make)
    smsHasFormattingChoice() {
      return this.smsNormalization.changed;
    },

    // Emoji & friends: no GSM equivalent — kept in both modes, force Unicode
    smsUnreplaceableChars() {
      return this.smsNormalization.unreplaceable;
    },

    // Billing analysis for the mode the user picked
    smsActiveAnalysis() {
      return this.form.smsFormatting === 'original' ? this.smsAsTyped : this.smsOptimized;
    },

    // What removing the un-replaceable chars (emoji) would bill — powers the
    // "remove it yourself and save" hint. null when there's nothing to remove.
    smsIfUnreplaceableRemoved() {
      if (this.smsUnreplaceableChars.length === 0) return null;
      let t = this.smsNormalization.text;
      for (const ch of this.smsUnreplaceableChars) {
        t = t.split(ch).join('');
      }
      return analyzeSms(t);
    },

    smsCharCount() {
      return this.smsAnalysisSource.length;
    },

    smsSegments() {
      return this.smsActiveAnalysis.segments;
    },

    // Carrier size cap (Twilio error 30019): messages over this many segments are
    // rejected by carriers AND billed anyway. Hard stop — mirrors the server gate.
    smsCarrierMaxSegments() {
      return CARRIER_MAX_SEGMENTS_PER_MESSAGE;
    },

    // True when the send-mode message would be carrier-rejected for size.
    smsCarrierBlocked() {
      return !!this.form.smsContent &&
        (this.form.type === 'SMS' || this.form.type === 'Both') &&
        this.smsSegments > CARRIER_MAX_SEGMENTS_PER_MESSAGE;
    },

    // When over the cap, does switching to Optimize formatting bring it under?
    smsOptimizeWouldComply() {
      return this.form.smsFormatting === 'original' &&
        this.smsOptimized.segments <= CARRIER_MAX_SEGMENTS_PER_MESSAGE;
    },

    // Single-message limit for the active encoding (160 GSM-7, 70 Unicode)
    smsMaxCharsForOneSegment() {
      return this.smsActiveAnalysis.encoding === 'UCS-2' ? 70 : 160;
    },

    // --- Live counter (the visible bar under the textarea) ---
    // Billable units in the CURRENT encoding: GSM counts septets (extended chars
    // like {}[]€ = 2), Unicode counts UTF-16 code units. The segment split is
    // measured on this, so the "chars until next segment" hint is exact.
    smsBillableUnits() {
      const a = this.smsActiveAnalysis;
      return a.encoding === 'UCS-2' ? a.length : (a.septets || 0);
    },
    // Max billable units that still fit in the CURRENT number of segments.
    smsSegmentCapacity() {
      const isUcs = this.smsActiveAnalysis.encoding === 'UCS-2';
      const n = this.smsSegments;
      const single = isUcs ? 70 : 160;
      const multi = isUcs ? 67 : 153;
      return n <= 1 ? single : n * multi;
    },
    smsCharsUntilNextSegment() {
      return Math.max(0, this.smsSegmentCapacity - this.smsBillableUnits);
    },
    smsCounterTrackPct() {
      const cap = this.smsSegmentCapacity || 1;
      return Math.min(100, Math.round((this.smsBillableUnits / cap) * 100));
    },
    // Colour tone for the counter: green (1 segment) → blue (2–3) → amber (4+ or Unicode).
    smsSegmentTone() {
      if (this.smsActiveAnalysis.encoding === 'UCS-2') return 'warn';
      if (this.smsSegments >= 4) return 'warn';
      if (this.smsSegments >= 2) return 'mid';
      return 'ok';
    },
    // Customer $ saved by dropping ONE segment/text on this campaign — counts
    // only credits over the plan, so a covered campaign correctly saves $0.
    smsTrimSavingsUsd() {
      if (this.smsSegments <= 1) return 0;
      return +Math.max(0, this.smsCostForSegments(this.smsSegments) - this.smsCostForSegments(this.smsSegments - 1)).toFixed(2);
    },

    smsRecipientsForEstimate() {
      return this.smsEligibleCount || this.effectiveRecipientCount || this.recipientCount || 0;
    },

    smsCampaignTotalSegments() {
      return this.smsSegments * this.smsRecipientsForEstimate;
    },

    // Schedule-time credit advisory. A campaign sent NOW gets the server's 409/consent flow with
    // the sender right there; one sent LATER fires unattended and just stops when credits run out,
    // half-delivering in silence. This says so while a human can still act on it.
    //
    // ADVISORY ONLY — never gates the button. Enforcement lives server-side at the send boundary.
    // Reads per-channel balances from the wallet store (`channels.sms` / `channels.email`) and the
    // per-product auto-reload configs, passing `balanceLoaded` and a possibly-null
    // `autoReloadEnabled` through honestly so the helper can answer "unknown" instead of guessing.
    scheduleCreditWarning() {
      const w = this.$store.state.wallet || {};
      const chans = w.channels || {};
      const type = this.form.type;
      const wantsSms = type === 'SMS' || type === 'Both';
      const wantsEmail = type === 'Email' || type === 'Both';

      // A per-channel pool figure when the breakdown has loaded; else the flat total, which is the
      // SMS pool (the pill's legacy shape). Never invent a number for a pool we have not seen.
      const poolCredits = (key) => {
        const c = chans[key];
        if (c && c.creditsAvailable != null) return c.creditsAvailable;
        return key === 'sms' ? w.creditsAvailable : null;
      };
      const loaded = (key) => !!w.hasBalance && poolCredits(key) != null;
      const reloadOn = (cfg) => (cfg && typeof cfg.enabled === 'boolean' ? cfg.enabled : null);

      const channels = [];
      if (wantsSms) {
        channels.push({
          product: 'sms',
          creditsNeeded: this.smsCampaignTotalSegments,   // segments, matching how SMS bills
          creditsAvailable: poolCredits('sms'),
          autoReloadEnabled: reloadOn(w.reloadConfig),
          balanceLoaded: loaded('sms'),
        });
      }
      if (wantsEmail) {
        channels.push({
          product: 'email',
          creditsNeeded: this.emailEligibleCount,          // 1 credit per email recipient
          creditsAvailable: poolCredits('email'),
          autoReloadEnabled: reloadOn(w.emailReloadConfig),
          balanceLoaded: loaded('email'),
        });
      }
      return scheduleCreditOutlook({ scheduleType: this.form.scheduleType, channels });
    },

    // Complete pre-send picture: texts already used this month (conversations,
    // reminders, forms...) + what this campaign adds vs the plan allowance,
    // priced via the shared estimator so this preview, the server gate, and the
    // confirm dialog can never disagree on the dollars.
    smsUsageBreakdown() {
      const u = this.smsMonthUsage;
      if (!u || !u.available || !this.smsRecipientsForEstimate) return null;
      // Allowance is counted in SEGMENTS ("message credits"): Twilio bills Teamio
      // per segment, so a 5-segment text spends 5 credits, not 1. This is the fix
      // that stops long campaigns from being "free" while Teamio eats the cost.
      const campaignSegments = this.smsCampaignTotalSegments;
      const charge = estimateOverageCharge({
        segmentsUsed: u.segmentsUsed,
        allowance: u.allowance,
        campaignSegments
      });
      return {
        used: u.segmentsUsed,          // credits already used this month
        allowance: u.allowance,        // credits included per month
        campaignSegments,              // credits this campaign will send
        projected: charge.projectedSegments,
        overage: Math.max(0, charge.projectedSegments - u.allowance),
        chargeableSegments: charge.chargeableSegments,
        overageCostUsd: charge.overageCostUsd,     // honest per-segment math (breakdown)
        chargeUsd: charge.chargeUsd,               // what we actually bill (Stripe $0.50 floor)
        minimumApplied: charge.minimumApplied
      };
    },

    smsOverageRatePerSegment() {
      return OVERAGE_PRICE_PER_SEGMENT_USD;
    },

    // Geometry for the monthly-usage meter: used / campaign-included / over,
    // as percentages of max(projected, allowance), with the plan cap marked
    // as a vertical line at the allowance position.
    smsUsageMeter() {
      const b = this.smsUsageBreakdown;
      if (!b) return null;
      const included = Math.max(0, Math.min(b.campaignSegments, Math.max(0, b.allowance - b.used)));
      const base = Math.max(b.projected, b.allowance) || 1;
      return {
        included,
        usedPct: (Math.min(b.used, base) / base) * 100,
        campaignPct: (included / base) * 100,
        overPct: (b.chargeableSegments / base) * 100,
        capPct: (b.allowance / base) * 100
      };
    },

    // The campaign's full dollar value at the customer rate — ALWAYS shown so
    // the practice sees a real dollar figure even when their plan covers it all.
    smsCampaignValueUsd() {
      return +(this.smsCampaignTotalSegments * OVERAGE_PRICE_PER_SEGMENT_USD).toFixed(2);
    },

    // Portion of the campaign's value the monthly allowance absorbs
    smsCoveredValueUsd() {
      const charge = (this.smsUsageBreakdown && this.smsUsageBreakdown.overageCostUsd) || 0;
      return +Math.max(0, this.smsCampaignValueUsd - charge).toFixed(2);
    },

    // Every actionable way to lower this campaign's cost, each priced at the
    // customer rate for the CURRENT recipient count so the practice can compare
    // real dollar figures, not abstractions.
    smsSavingsSuggestions() {
      if (!this.smsRecipientsForEstimate) return [];
      const suggestions = [];
      const current = this.smsSegments;
      const currentCost = this.smsCostForSegments(current);

      // 1. Remove characters we can't auto-fix (emoji) — they hold the whole
      //    message at Unicode rates (67 chars/segment instead of 153)
      if (this.smsIfUnreplaceableRemoved && this.smsIfUnreplaceableRemoved.segments < current) {
        const s = this.smsIfUnreplaceableRemoved.segments;
        suggestions.push({
          key: 'remove-unreplaceable',
          icon: 'fa-eraser',
          label: `Remove ${this.smsUnreplaceableChars.join(' ')} (no plain-text equivalent — keeps the message in Unicode)`,
          detail: `${current} → ${s} credit${s === 1 ? '' : 's'} per text`,
          costUsd: this.smsCostForSegments(s),
          saveUsd: +(currentCost - this.smsCostForSegments(s)).toFixed(2)
        });
      }

      // 2. Shorten to concrete segment targets, with the exact character budget
      //    for each (GSM budgets when nothing non-fixable remains, Unicode otherwise)
      const gsmAfterFixes = this.smsUnreplaceableChars.length === 0;
      for (const target of [3, 2, 1]) {
        if (target >= current) continue;
        const charBudget = gsmAfterFixes
          ? (target === 1 ? 160 : 153 * target)
          : (target === 1 ? 70 : 67 * target);
        suggestions.push({
          key: `trim-${target}`,
          icon: 'fa-scissors',
          label: `Shorten to ${target} credit${target === 1 ? '' : 's'} per text`,
          detail: `keep it under ${charBudget.toLocaleString()} characters${gsmAfterFixes ? '' : ' (Unicode, due to emoji)'}`,
          costUsd: this.smsCostForSegments(target),
          saveUsd: +(currentCost - this.smsCostForSegments(target)).toFixed(2)
        });
      }

      return suggestions.filter(s => s.saveUsd > 0);
    },

    // Teamio-internal economics row — DEV-ONLY (localhost / dev.teamio.app) AND
    // super admin only. Never rendered on prod hosts even for super admins.
    // (Display gating only: the rate constant ships in the JS bundle either way,
    // so this hides it from the UI, not from someone reading the source.)
    smsInternalCostVisible() {
      const host = window.location.hostname;
      const isDevHost = host === 'localhost' || host === '127.0.0.1' || host === 'dev.teamio.app';
      return isDevHost && this.isSuperAdmin;
    },

    // Complete internal picture: what all these segments cost Teamio (we pay
    // Twilio for EVERY segment, allowance or not) vs what the customer would
    // be charged (overage only), and the resulting net.
    smsInternalCost() {
      const costUsd = +(this.smsCampaignTotalSegments * COST_PER_SEGMENT_USD).toFixed(2);
      // what Teamio actually collects = the billed amount (overage floored to the $0.50
      // Stripe minimum), so net-to-Teamio reflects reality on a rounded-up small charge
      const chargeUsd = (this.smsUsageBreakdown && this.smsUsageBreakdown.chargeUsd) || 0;
      const netUsd = +(chargeUsd - costUsd).toFixed(2);
      return {
        costUsd,
        chargeUsd,
        netUsd,
        netNegative: netUsd < 0,
        netDisplay: `${netUsd < 0 ? '−' : '+'}$${Math.abs(netUsd).toFixed(2)}`,
        ratePerSegment: COST_PER_SEGMENT_USD
      };
    },

    smsSplitPreview() {
      // Preview the text we would actually send (normalized when optimized)
      const analysis = this.smsActiveAnalysis;
      const text = this.form.smsFormatting === 'original'
        ? this.smsAnalysisSource
        : this.smsNormalization.text;
      if (!text || analysis.segments <= 1) {
        return null;
      }

      const segmentSize = analysis.encoding === 'UCS-2' ? 67 : 153;
      const segments = [];

      for (let i = 0; i < text.length; i += segmentSize) {
        segments.push(text.substring(i, i + segmentSize));
      }

      return segments;
    },

    smsCounterClass() {
      if (this.smsSegments >= 4) return 'counter-danger';
      if (this.smsSegments >= 2) return 'counter-split';
      if (this.smsCharCount > 140) return 'counter-caution';
      return 'counter-ok';
    },

    currentTime() {
      const now = new Date();
      return now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    },

    minScheduleDate() {
      return new Date().toISOString().split('T')[0];
    },

    formatScheduleDate() {
      if (!this.form.scheduledDate || this.scheduledHour === '') return '-';
      let hour24 = parseInt(this.scheduledHour);
      if (this.scheduledAmPm === 'PM' && hour24 !== 12) hour24 += 12;
      if (this.scheduledAmPm === 'AM' && hour24 === 12) hour24 = 0;
      return moment(`${this.form.scheduledDate} ${String(hour24).padStart(2, '0')}:${this.scheduledMinute}`).format('MMM D, YYYY [at] h:mm A');
    }
  },

  mounted() {
    // Initialize form type based on enabled features
    if (!this.campaign) {
      // Wait a tick for settings to be available
      this.$nextTick(() => {
        const defaultType = this.defaultCampaignType;
        this.form.type = defaultType || (this.isSmsEnabled ? 'SMS' : this.isEmailEnabled ? 'Email' : null);
      });
    }
    // Load procedure codes if not already loaded
    if (!this.procCodes || this.procCodes.length === 0) {
      this.$store.dispatch('customer/getProcCodes').catch(() => {});
    }
    // Load zip codes for demographic filter
    this.fetchZipCodes();
    // Load insurance options for insurance filter
    this.fetchInsuranceOptions();
    // Load recall types for the recall due filter
    this.fetchRecallTypes();
    // Debug: Log settings to help diagnose
    if (process.env.NODE_ENV === 'development') {
      console.log('[Campaign Modal] mounted - Settings:', this.settings);
      console.log('[Campaign Modal] mounted - isSmsEnabled:', this.isSmsEnabled);
      console.log('[Campaign Modal] mounted - isEmailEnabled:', this.isEmailEnabled);
      if (this.settings && this.settings.GENERAL_PHONE) {
        console.log('[Campaign Modal] mounted - GENERAL_PHONE:', this.settings.GENERAL_PHONE);
      }
    }
  },

  watch: {
    campaign: {
      immediate: true,
      handler(val) {
        if (val) {
          this.populateForm(val);
        } else {
          // Reset to default type when creating new campaign
          const defaultType = this.defaultCampaignType;
          this.form.type = defaultType || (this.isSmsEnabled ? 'SMS' : this.isEmailEnabled ? 'Email' : null);
        }
      }
    },

    // Lazy-load month-to-date SMS usage the first time the user reaches the
    // compose step with an SMS campaign — powers the "you've already used X of
    // your monthly texts" line in the pre-send breakdown.
    currentStep(step) {
      if (step === 2 && (this.form.type === 'SMS' || this.form.type === 'Both') && this.smsMonthUsage === null) {
        this.loadSmsMonthUsage();
      }
    },
    'form.smsContent': {
      handler() {
        this.updateSmsPreview();
      },
      immediate: true
    },
    // AI Call ranked list (Phase D.2b): re-fetch when the preset changes. Switching to
    // 'standard' (or anything that fails the gate) clears the list without an API call.
    'form.priorityPreset'() {
      this.fetchAiCallRanking();
    },
    // Re-fetch when the chosen audience changes (size or membership) while on AI Call.
    // immediate:true so a draft that REOPENS with a non-standard preset + an audience
    // already set (no later change event) still populates the table on mount — the gate
    // inside fetchAiCallRanking no-ops for standard/empty/non-ai_call, so immediate is safe,
    // and the stale-fetch guard dedups any overlap with a subsequent change fire.
    aiCallSelectedPatNums: {
      immediate: true,
      handler(newVal, oldVal) {
        if (this.form.type !== 'ai_call') return;
        if (oldVal !== undefined && JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
        this.fetchAiCallRanking();
      }
    },
    // Watch settings to update when they become available
    settings: {
      handler(newSettings) {
        if (newSettings && !this.campaign) {
          // Settings loaded, update form type if needed
          const defaultType = this.defaultCampaignType;
          if (defaultType && !this.form.type) {
            this.form.type = defaultType;
          }
        }
      },
      immediate: true,
      deep: true
    },
    // Ensure form type is valid based on enabled features
    isSmsEnabled: {
      handler(newVal) {
        if (!newVal && (this.form.type === 'SMS' || this.form.type === 'Both')) {
          // If SMS is disabled, switch to Email if available
          this.form.type = this.isEmailEnabled ? 'Email' : null;
        }
      }
    },
    isEmailEnabled: {
      handler(newVal) {
        if (!newVal && (this.form.type === 'Email' || this.form.type === 'Both')) {
          // If Email is disabled, switch to SMS if available
          this.form.type = this.isSmsEnabled ? 'SMS' : null;
        }
      }
    }
  },

  methods: {
    show() {
      // Ensure form type is valid when modal opens
      if (!this.campaign) {
        const defaultType = this.defaultCampaignType;
        this.form.type = defaultType || (this.isSmsEnabled ? 'SMS' : this.isEmailEnabled ? 'Email' : null);
      }
      this.$refs.modal.show();
    },

    hide() {
      this.$refs.modal.hide();
    },

    // Show/hide the loading overlay while the parent fetches the campaign's full
    // data for editing. Lets the modal pop instantly with feedback, then populate.
    showLoading(message = 'Loading campaign…') {
      this.overlayTitle = 'Loading';
      this.overlayMessage = message;
      this.loadingDetails = true;
    },

    hideLoading() {
      this.loadingDetails = false;
    },

    // ── AI Call test calls ──────────────────────────────────────────────────
    addTestTarget() {
      if (this.testTargets.length < this.testCallMax) {
        this.testTargets.push({ patNum: null, phone: '' });
      }
    },

    removeTestTarget(i) {
      this.testTargets.splice(i, 1);
      if (this.testTargets.length === 0) this.testTargets.push({ patNum: null, phone: '' });
    },

    async placeTestCalls() {
      const evalRes = evaluateTestTargets(this.testTargets);
      if (!evalRes.ok) {
        const msg = evalRes.reason === 'too_many'
          ? `You can place at most ${this.testCallMax} test calls at once.`
          : 'Add at least one row with a selected patient and a valid phone number.';
        this.$bvModal.msgBoxOk(msg, { title: 'Can’t place test calls', centered: true });
        return;
      }
      if (evalRes.needsConfirm) {
        const ok = await this.$bvModal.msgBoxConfirm(
          `You're about to place ${evalRes.count} real AI calls to the numbers you entered. Continue?`,
          { title: 'Place test calls?', okTitle: `Yes, call ${evalRes.count}`, cancelTitle: 'Cancel', okVariant: 'warning', centered: true }
        );
        if (!ok) return;
      }

      this.placingTestCalls = true;
      this.testCallResults = [];
      const siteID = this.$store.state.customer.siteID;
      const callReason = this.form.callFollowupTaskTypeID === 4 ? 'treatment' : 'recall';

      for (const t of evalRes.valid) {
        try {
          const res = await triggerAutorecallTestCall({ siteID, patNum: t.patNum, toNumber: t.phone, callReason });
          this.testCallResults.push({
            phone: t.phone,
            success: !!(res && res.success),
            message: (res && res.success) ? 'Calling now — you should ring shortly.' : ((res && (res.message || res.reason)) || 'Failed to place call.')
          });
        } catch (err) {
          this.testCallResults.push({
            phone: t.phone,
            success: false,
            message: (err.response && err.response.data && err.response.data.message) || err.message || 'Network error.'
          });
        }
      }
      this.placingTestCalls = false;
    },

    // ── AI Call ranked list (Phase D.2b) ────────────────────────────────────
    rankedNameFor(row) {
      return rankedDisplayName(row);
    },
    rankedWhyFor(row) {
      return buildWhy(row);
    },
    isPinned(patNum) {
      return (this.form.priorityConfig.pin || []).map(Number).includes(Number(patNum));
    },
    isExcluded(patNum) {
      return (this.form.priorityConfig.exclude || []).map(Number).includes(Number(patNum));
    },
    togglePin(patNum) {
      this.form.priorityConfig = togglePriorityConfig(this.form.priorityConfig, 'pin', patNum);
    },
    toggleExclude(patNum) {
      this.form.priorityConfig = togglePriorityConfig(this.form.priorityConfig, 'exclude', patNum);
    },

    // ── AI Call hand-add specific patients (Phase D.2c) ────────────────────
    // Whether a ranked-table row was hand-added (drives the "Added" badge + remove action).
    isHandAdded(patNum) {
      return this.handAddedPatNums.map(Number).includes(Number(patNum));
    },
    // multiselect label for a customer.patients row (mirrors FormsHome patientCustomLabel —
    // `Patient` ("LName, FName") only; the DOB is rendered separately in the option slot).
    handAddPatientLabel(option) {
      if (!option) return '';
      return option.Patient || `Patient ${option.PatNum}`;
    },
    handAddPatientDOB(option) {
      if (!option || !option.Birthdate) return '';
      try { return this.formatDate(option.Birthdate); } catch (e) { return ''; }
    },
    // Push the selected patient into the hand-added list (minimal row) then clear the picker.
    // availableHandAddPatients already excludes everyone in the effective audience, so a
    // re-add can't duplicate; guard defensively anyway.
    onHandAddPatient(option) {
      if (!option || option.PatNum == null) return;
      if (this.isHandAdded(option.PatNum)) { this.handAddPatientSelected = null; return; }
      // Don't re-add someone already in the recall audience (the union would dedup, but keep
      // the hand-added array clean of audience members).
      const inAudience = this.activePreviewPatients.some(p => Number(p.PatNum) === Number(option.PatNum));
      if (!inAudience) {
        this.form.handAddedPatients = [
          ...this.form.handAddedPatients,
          { PatNum: option.PatNum, FName: option.FName, LName: option.LName, WirelessPhone: option.WirelessPhone }
        ];
      }
      this.handAddPatientSelected = null;
    },
    // Remove a hand-added patient (from the picker chips or the ranked-table row). Also scrub
    // any pin/exclude on that PatNum: launch already intersects with the audience, but
    // buildPreviewConfig does NOT, so a stale pin could otherwise be sent to the preview — and
    // a leftover entry would resurface if the patient were hand-added again. Reassign the
    // arrays (and priorityConfig) so Vue 2 picks up the change reactively.
    removeHandPatient(patNum) {
      const pn = Number(patNum);
      this.form.handAddedPatients = this.form.handAddedPatients.filter(p => Number(p.PatNum) !== pn);
      const cfg = this.form.priorityConfig || { pin: [], exclude: [] };
      this.form.priorityConfig = {
        pin: (cfg.pin || []).filter(x => Number(x) !== pn),
        exclude: (cfg.exclude || []).filter(x => Number(x) !== pn)
      };
    },

    /**
     * Fetch the ranked AI-Call audience for the current preset + selected list.
     * No-op (and clears) for standard/absent preset or empty audience — never calls the
     * endpoint in those cases. Re-callable; safe to invoke from watchers.
     *
     * Guarded against a stale-fetch race: rapid preset toggles / fast audience edits fire
     * overlapping requests, and without sequencing whichever resolves LAST would win (could
     * show a list for a superseded preset, then launch against it). A monotonic token
     * (this._rankingSeq, a NON-reactive instance field) lets a newer fetch supersede an older
     * one — the stale call bails before touching rankedPatients / loading / error.
     */
    async fetchAiCallRanking() {
      const seq = (this._rankingSeq = (this._rankingSeq || 0) + 1);
      if (!this.shouldFetchRankedList) {
        this.rankedPatients = [];
        this.rankingError = '';
        this.rankingLoading = false;
        return;
      }
      this.rankingLoading = true;
      this.rankingError = '';
      try {
        const ranked = await this.$store.dispatch('campaigns/previewAiCallRanking', {
          selectedList: this.aiCallSelectedPatNums,
          priorityPreset: this.form.priorityPreset,
          // PIN-ONLY: pins influence backend ordering, but excludes must NOT be sent here.
          // The backend drops excluded patients from the result, which would make them
          // invisible/un-excludable on any re-fetch. Excludes stay client-side (applyPinExclude)
          // and are sent authoritatively only in the launch payload.
          priorityConfig: buildPreviewConfig(this.form.priorityConfig)
        });
        // A newer fetch superseded this one — drop its result silently.
        if (isStaleRankingFetch(seq, this._rankingSeq)) return;
        this.rankedPatients = Array.isArray(ranked) ? ranked : [];
      } catch (error) {
        if (isStaleRankingFetch(seq, this._rankingSeq)) return;
        this.rankedPatients = [];
        const status = error.response?.status;
        this.rankingError = status === 403
          ? "AI Call isn't enabled for this site."
          : extractApiError(error, 'Could not load the ranked list. Please try again.');
      } finally {
        // Only the latest fetch may clear the loading flag, so a stale resolve can't
        // hide the spinner of a still-in-flight newer fetch.
        if (!isStaleRankingFetch(seq, this._rankingSeq)) this.rankingLoading = false;
      }
    },

    resetForm() {
      this.currentStep = 0;
      // The modal is closing: the next session starts fresh and must create its own campaign.
      this.createdCampaign = null;
      this.testTargets = [{ patNum: null, phone: '' }];
      this.testCallResults = [];
      this.placingTestCalls = false;
      this.rankedPatients = [];
      this.rankingLoading = false;
      this.rankingError = '';
      const defaultType = this.defaultCampaignType;
      this.form = {
        name: '',
        description: '',
        type: defaultType || (this.isSmsEnabled ? 'SMS' : this.isEmailEnabled ? 'Email' : null),
        // AI Call: default follow-up list type (3=Overdue Recall, 4=Unscheduled Treatment)
        callFollowupTaskTypeID: 3,
        // AI Call: default prioritization preset (sent to backend as-is)
        priorityPreset: 'standard',
        // AI Call: pin/exclude edits over the ranked list (empty by default)
        priorityConfig: { pin: [], exclude: [] },
        // AI Call (Phase D.2c): hand-added patients (empty by default)
        handAddedPatients: [],
        recipientSource: 'customFilters',
        selectedList: null,
        customFilters: {
          type: 'customFilters',
          groups: [{ id: 1, filters: [] }]
        },
        smsTemplate: null,
        smsContent: '',
        // 'optimized' (default) = auto-replace non-GSM chars (smart quotes, (R)...)
        // at send time; 'original' = customer chose to keep them and pay for the
        // extra Unicode segments. See src/utils/smsSegments.js.
        smsFormatting: 'optimized',
        // Phase 2 per-send override: include dead/disconnected numbers this send only
        includeSuppressed: false,
        emailTemplate: null,
        emailSubject: '',
        emailPreviewText: '',
        emailContent: '',
        // Tracking options
        trackClicks: true,
        // Perfect timing
        scheduleType: 'now',
        scheduledDate: null
      };
      this.scheduledHour = '';
      this.scheduledMinute = '00';
      this.scheduledAmPm = 'AM';
      this.recipientSubStep = 0;
      this.recipientCount = 0;
      this.previewPatients = [];
      this.selectedPatNums = [];
      this.removedPatNums = [];
      this.removalMode = false;
      this.pendingPreviewResult = null;
      this.scheduledTimeframe = 'today';
      this.scheduledDateFrom = null;
      this.scheduledDateTo = null;
      this.scheduledProviders = [];
      this.allActiveTimeframeMode = 'relative';
      this.allActiveTimeframeValue = 18;
      this.allActiveTimeframeUnit = 'months';
      this.allActiveDateFrom = '';
      this.allActiveDateTo = '';
      this.allActiveProviders = [];
      this.allActiveExcludedProcCodes = [];
      this.customFiltersValid = false;
      this.previewLoading = false;
      this.previewError = '';
      this.savedLists = [];
      this.savedListsLoading = false;
      this.recallLists = [];
      this.unscheduledLists = [];
      this.siteListsLoading = false;
      this.siteListsCollapsed = false;
      this.loadingCampaignID = null;
      this.campaignListsCollapsed = false;
      this.recallListPage = 1;
      this.unscheduledListPage = 1;
      this.smsPreviewText = '';
      this.showPreviewText = false;
      this.showTrackingOptions = false;
      this.testEmail = '';
      this.testPhone = '';
      this.testMessageSent = false;
      this.sendingTest = false;
      this.testError = '';
      this.validated = false;
      this.campaignSent = false;
      this.sentRecipientCount = 0;
      this.smsTemplateList = [];
      this.smsTemplateSearch = '';
      this.smsTemplatePage = 1;
      this.smsLinkMap = {};
      this.emailTemplateList = [];
      this.emailTemplateSearch = '';
      this.emailTemplatePage = 1;
      this.selectedEmailTemplate = null;
    },

    populateForm(campaign) {
      // Basic form fields
      this.form.name = campaign.Name || '';
      this.form.description = campaign.Description || '';
      this.form.type = campaign.Type || 'SMS';
      // AI Call: restore chosen follow-up list type when editing (default Overdue Recall)
      this.form.callFollowupTaskTypeID = campaign.CallFollowupTaskTypeID || 3;
      this.form.recipientSource = campaign.RecipientSource || 'customFilters';
      this.form.smsContent = campaign.TextContent || '';
      this.form.emailSubject = campaign.Subject || '';
      this.form.emailContent = campaign.HTMLContent || '';

      // Schedule type based on status
      if (campaign.Status === 'draft' || campaign.Status === 'Draft') {
        this.form.scheduleType = 'draft';
      } else if (campaign.ScheduledAt) {
        this.form.scheduleType = 'scheduled';
        const dt = moment(campaign.ScheduledAt);
        this.form.scheduledDate = dt.format('YYYY-MM-DD');
        let h = dt.hour();
        this.scheduledMinute = dt.format('mm');
        this.scheduledAmPm = h >= 12 ? 'PM' : 'AM';
        if (h === 0) h = 12;
        else if (h > 12) h -= 12;
        this.scheduledHour = h;
      } else {
        this.form.scheduleType = 'now';
      }

      // Restore filter config
      if (campaign.FilterConfig) {
        const fc = typeof campaign.FilterConfig === 'string' ? JSON.parse(campaign.FilterConfig) : campaign.FilterConfig;
        if (fc.type === 'scheduledPatients') {
          // Scheduled patients filter config
          this.scheduledTimeframe = fc.timeframe || 'today';
          this.scheduledDateFrom = fc.dateFrom || '';
          this.scheduledDateTo = fc.dateTo || '';
          this.scheduledProviders = fc.providers || [];
        } else {
          // Custom filters
          this.form.customFilters = fc;
        }
      }

      // Restore wizard state for draft resumption
      if (campaign.WizardState) {
        const ws = typeof campaign.WizardState === 'string' ? JSON.parse(campaign.WizardState) : campaign.WizardState;
        if (ws.currentStep !== undefined) this.currentStep = ws.currentStep;
        if (ws.scheduledTimeframe) this.scheduledTimeframe = ws.scheduledTimeframe;
        if (ws.scheduledDateFrom) this.scheduledDateFrom = ws.scheduledDateFrom;
        if (ws.scheduledDateTo) this.scheduledDateTo = ws.scheduledDateTo;
        if (ws.scheduledProviders) this.scheduledProviders = ws.scheduledProviders;
        if (ws.removedPatNums) this.removedPatNums = ws.removedPatNums;
        // AI Call: restore prioritization choices saved with the draft.
        if (ws.priorityPreset) this.form.priorityPreset = ws.priorityPreset;
        if (ws.priorityConfig) {
          this.form.priorityConfig = {
            pin: Array.isArray(ws.priorityConfig.pin) ? ws.priorityConfig.pin : [],
            exclude: Array.isArray(ws.priorityConfig.exclude) ? ws.priorityConfig.exclude : []
          };
        }
        // AI Call (Phase D.2c): restore hand-added patients saved with the draft.
        if (Array.isArray(ws.handAddedPatients)) {
          this.form.handAddedPatients = ws.handAddedPatients;
        }
      }

      // Re-populate patient preview for draft resumption
      this.$nextTick(async () => {
        // If we have PatientList (exact PatNums from when draft was saved)
        if (campaign.PatientList) {
          const patNums = typeof campaign.PatientList === 'string'
            ? JSON.parse(campaign.PatientList)
            : campaign.PatientList;
          if (patNums && patNums.length > 0) {
            try {
              const result = await this.$store.dispatch('campaigns/fetchPatientsByPatNums', patNums);
              this.recipientCount = result.count || patNums.length;
              this.previewPatients = result.patients || [];
            } catch (e) {
              console.warn('Failed to reload patient list:', e);
              this.recipientCount = patNums.length;
            }
            return;
          }
        }

        // If no PatientList but we have filters, re-run the preview query
        if (this.form.recipientSource === 'scheduledPatients') {
          this.handleScheduledPatientsPreview();
        } else if (this.form.recipientSource === 'customFilters' && this.form.customFilters) {
          const hasFilters = this.form.customFilters.groups &&
            this.form.customFilters.groups.some(g => g.filters && g.filters.length > 0);
          if (hasFilters) {
            this.previewRecipients();
          }
        } else if (this.form.recipientSource === 'all') {
          this.handleAllActivePatientsPreview();
        }
      });
    },

    nextStep() {
      if (!this.canProceed || this.currentStep >= this.steps.length - 1) return;

      // Intercept Step 2 → Step 3: warn if any active patients are ineligible for campaign type
      if (this.currentStep === 1 && this.ineligiblePatients.length > 0) {
        this.showEligibilityWarning = true;
        return;
      }

      this.currentStep++;

      // Reset sub-step when entering step 2
      if (this.currentStep === 1) {
        this.recipientSubStep = 0;
      }

      // Load wiki templates when entering Step 3
      if (this.currentStep === 2) {
        if ((this.form.type === 'SMS' || this.form.type === 'Both') && this.smsTemplateList.length === 0)
          this.loadSmsTemplates();
        if ((this.form.type === 'Email' || this.form.type === 'Both') && this.emailTemplateList.length === 0)
          this.loadEmailTemplates();
      }
    },

    applyEligibilityFilterAndAdvance() {
      const ineligiblePatNums = this.ineligiblePatients.map(p => p.PatNum);
      this.removedPatNums = [...new Set([...this.removedPatNums, ...ineligiblePatNums])];
      this.showEligibilityWarning = false;
      this.currentStep++;
      if (this.currentStep === 2) {
        if ((this.form.type === 'SMS' || this.form.type === 'Both') && this.smsTemplateList.length === 0)
          this.loadSmsTemplates();
        if ((this.form.type === 'Email' || this.form.type === 'Both') && this.emailTemplateList.length === 0)
          this.loadEmailTemplates();
      }
    },

    cancelEligibilityFilter() {
      this.showEligibilityWarning = false;
    },

    previousStep() {
      if (this.currentStep === 1 && this.recipientSubStep === 1) {
        this.recipientSubStep = 0;
      } else if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    goToStep(index) {
      if (index < this.currentStep) {
        this.currentStep = index;
      }
    },

    validateField(field) {
      if (!this.validated) return null;
      switch (field) {
        case 'name':
          return this.form.name.trim().length > 0;
        default:
          return null;
      }
    },

    async fetchZipCodes() {
      try {
        const result = await this.$store.dispatch('campaigns/fetchPatientZipCodes');
        this.zipCodeOptions = (result || []).map(z => {
          // Support both new { zip, city } format and legacy plain string format
          if (typeof z === 'object' && z.zip) {
            const label = z.city ? `${z.zip} - ${z.city}` : z.zip;
            return { value: z.zip, text: label };
          }
          return { value: z, text: z };
        });
      } catch (err) {
        console.error('Error loading zip codes:', err);
        this.zipCodeOptions = [];
      }
    },

    async fetchInsuranceOptions() {
      try {
        const result = await this.$store.dispatch('campaigns/fetchInsuranceOptions');
        this.insuranceOptions = result || { carriers: [], employers: [], groupNumbers: [] };
      } catch (err) {
        console.error('Error loading insurance options:', err);
        this.insuranceOptions = { carriers: [], employers: [], groupNumbers: [] };
      }
    },

    async fetchRecallTypes() {
      try {
        const result = await this.$store.dispatch('campaigns/fetchRecallTypes');
        this.recallTypeOptions = (result || []).map(t => ({ value: t.id, text: t.name }));
      } catch (err) {
        console.error('Error loading recall types:', err);
        this.recallTypeOptions = [];
      }
    },

    onModalShown() {
      // Fetch zip codes if not already loaded (handles hot-reload & lazy load)
      if (this.zipCodeOptions.length === 0) {
        this.fetchZipCodes();
      }
      // Fetch insurance options if not already loaded
      if (!this.insuranceOptions.carriers || this.insuranceOptions.carriers.length === 0) {
        this.fetchInsuranceOptions();
      }
      // Fetch recall types if not already loaded
      if (this.recallTypeOptions.length === 0) {
        this.fetchRecallTypes();
      }
    },

    selectAudienceMethod(method) {
      this.form.recipientSource = method;
      this.recipientSubStep = 1;
      if (method === 'list') {
        this.loadSavedLists();
      }
    },

    async loadSavedLists() {
      this.savedListsLoading = true;
      this.siteListsLoading = true;
      try {
        const [savedLists, siteLists] = await Promise.all([
          this.$store.dispatch('campaigns/fetchSavedLists'),
          this.$store.dispatch('campaigns/fetchSiteLists')
        ]);
        this.savedLists = savedLists;
        this.recallLists = siteLists.recallLists || [];
        this.unscheduledLists = siteLists.unscheduledLists || [];
      } catch (error) {
        console.error('Error loading saved lists:', error);
        this.savedLists = [];
        this.recallLists = [];
        this.unscheduledLists = [];
      } finally {
        this.savedListsLoading = false;
        this.siteListsLoading = false;
      }
    },

    async useRecallOrUnscheduledList(list) {
      this.loadingListID = list.ListID;
      this.previewLoading = true;
      try {
        const result = await this.$store.dispatch('campaigns/fetchListMembers', list.ListID);
        if (!result.patNums || result.patNums.length === 0) {
          this.$toast?.error?.('This list has no patients');
          this.previewLoading = false;
          return;
        }
        // Set PatNums as the selected list and populate preview table
        this.form.selectedList = result.patNums;
        this.recipientCount = result.count || result.patNums.length;
        this.smsEligibleCount = result.smsEligible || 0;
        this.smsSuppressedCount = result.suppressedCount || 0;
        this.emailEligibleCount = result.emailEligible || 0;
        this.previewPatients = result.patients || [];
        this.patientCurrentPage = 1;
        this.patientSearch = '';
        this.removedPatNums = [];
        this.removalMode = false;

        const listType = list.TaskTypeID === 3 ? 'Overdue Recall' : 'Unscheduled Treatment';
        this.$toast?.success?.(`Loaded ${result.patNums.length} patients from ${listType} list: ${list.Name}`);

        // Collapse both sections so recipient preview is visible
        this.siteListsCollapsed = true;
        this.campaignListsCollapsed = true;
      } catch (error) {
        console.error('Error loading list members:', error);
        this.$toast?.error?.('Failed to load list members');
      } finally {
        this.previewLoading = false;
        this.loadingListID = null;
      }
    },

    async rerunSavedFilters(item) {
      const fc = typeof item.FilterConfig === 'string' ? JSON.parse(item.FilterConfig) : item.FilterConfig;
      if (fc.type === 'scheduledPatients') {
        // Switch to scheduled patients mode and re-run
        this.form.recipientSource = 'scheduledPatients';
        this.scheduledTimeframe = fc.timeframe || 'today';
        this.scheduledDateFrom = fc.dateFrom || '';
        this.scheduledDateTo = fc.dateTo || '';
        this.scheduledProviders = fc.providers || [];
        this.handleScheduledPreview();
      } else {
        // Switch to custom filters mode and re-run
        this.form.recipientSource = 'customFilters';
        this.form.customFilters = fc;
        this.handleFilterPreview(fc);
      }
    },

    async useExactList(item) {
      const patNums = typeof item.PatientList === 'string' ? JSON.parse(item.PatientList) : item.PatientList;
      if (!patNums || patNums.length === 0) {
        this.$toast?.error?.('This saved list has no patients');
        return;
      }
      this.loadingCampaignID = item.CampaignID;
      this.previewLoading = true;
      try {
        const result = await this.$store.dispatch('campaigns/fetchPatientsByPatNums', patNums);
        this.form.selectedList = patNums;
        this.recipientCount = result.count || patNums.length;
        this.smsEligibleCount = result.smsEligible || 0;
        this.smsSuppressedCount = result.suppressedCount || 0;
        this.emailEligibleCount = result.emailEligible || 0;
        this.previewPatients = result.patients || [];
        this.patientCurrentPage = 1;
        this.patientSearch = '';
        this.removedPatNums = [];
        this.removalMode = false;
        this.$toast?.success?.(`Loaded ${patNums.length} patients from saved list`);
        this.campaignListsCollapsed = true;
        this.siteListsCollapsed = true;
      } catch (error) {
        console.error('Error loading exact list patients:', error);
        this.$toast?.error?.('Failed to load patient details');
      } finally {
        this.previewLoading = false;
        this.loadingCampaignID = null;
      }
    },

    loadRecipientPreview() {
      // Simulate API call to get recipient count
      setTimeout(() => {
        this.recipientCount = Math.floor(Math.random() * 3000) + 500;
        this.smsEligibleCount = Math.floor(this.recipientCount * 0.85);
        this.smsSuppressedCount = 0;
        this.emailEligibleCount = Math.floor(this.recipientCount * 0.92);
      }, 300);
    },

    async handleFilterPreview(filterConfig) {
      this.previewLoading = true;
      this.previewError = '';
      try {
        const country = localStorage.getItem('country') || 'USA';
        const result = await this.$store.dispatch('campaigns/previewRecipients', { ...filterConfig, country });

        // Check if we have previous removals that overlap with new results
        if (this.removedPatNums.length > 0) {
          const newPatNums = (result.patients || []).map(p => p.PatNum);
          const overlapping = this.removedPatNums.filter(pn => newPatNums.includes(pn));
          if (overlapping.length > 0) {
            // Store result and show prompt
            this.pendingPreviewResult = result;
            this.showKeepRemovalsPrompt = true;
            this.previewLoading = false;
            return;
          }
          // No overlap — previous removals are irrelevant to new results, clear them
          this.removedPatNums = [];
        }

        this.applyPreviewResult(result);
      } catch (err) {
        console.error('Filter preview error:', err);
        this.previewError = 'Failed to preview recipients. Please try again.';
        this.recipientCount = 0;
        this.smsEligibleCount = 0;
        this.smsSuppressedCount = 0;
        this.emailEligibleCount = 0;
        this.previewPatients = [];
      } finally {
        this.previewLoading = false;
      }
    },

    applyPreviewResult(result) {
      this.recipientCount = result.count || 0;
      this.smsEligibleCount = result.smsEligible || 0;
        this.smsSuppressedCount = result.suppressedCount || 0;
      this.emailEligibleCount = result.emailEligible || 0;
      this.previewPatients = result.patients || [];
      this.patientCurrentPage = 1;
      this.patientSearch = '';
      this.selectedPatNums = [];
      if (this.recipientCount === 0) {
        this.previewError = 'No patients found matching these filters. Try adjusting your criteria.';
      }
    },

    handleKeepRemovalsChoice(keep) {
      this.showKeepRemovalsPrompt = false;
      if (!keep) {
        this.removedPatNums = [];
      }
      if (this.pendingPreviewResult) {
        this.applyPreviewResult(this.pendingPreviewResult);
        this.pendingPreviewResult = null;
      }
    },

    togglePatientSelection(patNum) {
      const idx = this.selectedPatNums.indexOf(patNum);
      if (idx === -1) {
        this.selectedPatNums.push(patNum);
      } else {
        this.selectedPatNums.splice(idx, 1);
      }
    },

    toggleSelectAll(checked) {
      if (checked) {
        const visiblePatNums = this.filteredPreviewPatients.map(p => p.PatNum);
        const combined = new Set([...this.selectedPatNums, ...visiblePatNums]);
        this.selectedPatNums = Array.from(combined);
      } else {
        const visiblePatNums = this.filteredPreviewPatients.map(p => p.PatNum);
        this.selectedPatNums = this.selectedPatNums.filter(pn => !visiblePatNums.includes(pn));
      }
    },

    // --- Filter summary helpers ---
    describeFilter(f) {
      const procStatusMap = { 2: 'completed', 1: 'treatment planned', 3: 'existing/current', 6: 'referred' };
      const aptStatusMap = { 1: 'scheduled', 2: 'completed', 3: 'unscheduled', 5: 'broken' };

      if (f.filterType === 'procedure') {
        const statuses = (f.statuses || []).map(s => procStatusMap[s] || s).join(' or ');
        let codes = '';
        if (f.selectionMode === 'specific' && f.procCodes?.length) {
          codes = ' procedure' + (f.procCodes.length > 1 ? 's ' : ' ') + f.procCodes.join(', ');
        } else {
          codes = ' 1 or more procedures';
        }
        const verb = f.mode === 'none' ? 'have NOT had' : 'have';
        const tf = f.timeframe ? this.describeTimeframe(f.timeframe) : 'at any time';
        let line = `Patients that ${verb} ${statuses}${codes} ${tf}`;
        if (f.fee && f.fee.operator && f.fee.amount != null) {
          line += ` with fee ${f.fee.operator === 'gt' ? 'greater than' : 'less than'} $${f.fee.amount}`;
        }
        return line;
      }

      if (f.filterType === 'appointment') {
        const statuses = (f.statuses || []).map(s => aptStatusMap[s] || s).join(' or ');
        const dir = f.timeframe?.direction === 'next' ? 'in the next' : 'in the last';
        const val = f.timeframe?.value || '';
        const unit = f.timeframe?.unit || '';
        const keyword = f.mode === 'none' ? 'with no' : 'with';
        return `Patients ${keyword} ${statuses} appointments ${dir} ${val} ${unit}`;
      }

      if (f.filterType === 'recall') {
        const tf = this.describeTimeframe(f.timeframe);
        const provs = this.describeProviders(f.providers);
        const types = this.describeRecallTypes(f.recallTypes);
        return `Patients who are due for recall ${tf}${types}${provs}`;
      }

      if (f.filterType === 'demographic') {
        const parts = [];
        if (f.ageMin != null && f.ageMin !== '' && f.ageMax != null && f.ageMax !== '') {
          parts.push(`aged ${f.ageMin}-${f.ageMax}`);
        } else if (f.ageMin != null && f.ageMin !== '') {
          parts.push(`aged ${f.ageMin}+`);
        }
        if (f.gender && f.gender !== 'all') {
          parts.push(f.gender.charAt(0).toUpperCase() + f.gender.slice(1));
        }
        if (f.zipCodes?.length) {
          parts.push(`in zip code${f.zipCodes.length > 1 ? 's' : ''} ${f.zipCodes.join(', ')}`);
        }
        return parts.length ? `Patients ${parts.join(', ')}` : null;
      }

      if (f.filterType === 'insurance') {
        const parts = [];
        if (f.carriers?.length) {
          const names = f.carriers.map(id => {
            const opt = (this.insuranceOptions.carriers || []).find(c => c.id === id || c.id === String(id));
            return opt ? opt.name : `#${id}`;
          });
          parts.push(`the following plan(s): ${names.join(', ')}`);
        }
        if (f.employers?.length) {
          const names = f.employers.map(id => {
            const opt = (this.insuranceOptions.employers || []).find(e => e.id === id || e.id === String(id));
            return opt ? opt.name : `#${id}`;
          });
          parts.push(`employer(s): ${names.join(', ')}`);
        }
        if (f.groupNumbers?.length) {
          parts.push(`group number(s): ${f.groupNumbers.join(', ')}`);
        }
        return parts.length ? `Patients with ${parts.join(' and ')}` : null;
      }

      return null;
    },

    formatDateReadable(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const day = d.getDate();
      const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
      return `${months[d.getMonth()]} ${day}${suffix}, ${d.getFullYear()}`;
    },

    describeTimeframe(tf) {
      if (!tf) return '';
      if (tf.mode === 'custom' && tf.from && tf.to) {
        return `from ${this.formatDateReadable(tf.from)} to ${this.formatDateReadable(tf.to)}`;
      }
      return `in the last ${tf.value || ''} ${tf.unit || ''}`;
    },

    describeProviders(provIds) {
      if (!provIds || provIds.length === 0) return ' for all providers';
      const names = provIds.map(id => {
        const opt = this.providerOptions.find(p => p.value === id);
        return opt ? opt.text : `#${id}`;
      });
      return ` for ${names.join(', ')}`;
    },

    describeRecallTypes(typeIds) {
      // Silent when nothing is picked — "all recall types" is the default and saying so on every
      // recall campaign just makes the summary longer.
      if (!typeIds || typeIds.length === 0) return '';
      const names = typeIds.map(id => {
        const opt = this.recallTypeOptions.find(t => t.value === id);
        return opt ? opt.text : `#${id}`;
      });
      return ` of type ${names.join(', ')}`;
    },

    buildScheduledSummary() {
      let tf = '';
      if (this.scheduledTimeframe === 'custom' && this.scheduledDateFrom && this.scheduledDateTo) {
        tf = `from ${this.formatDateReadable(this.scheduledDateFrom)} to ${this.formatDateReadable(this.scheduledDateTo)}`;
      } else {
        const labels = { today: 'today', tomorrow: 'tomorrow', thisWeek: 'this week', nextWeek: 'next week', thisMonth: 'this month', nextMonth: 'next month' };
        tf = labels[this.scheduledTimeframe] || this.scheduledTimeframe;
      }
      const provs = this.scheduledProviders.length > 0
        ? ` for ${this.scheduledProviders.map(p => p.text).join(', ')}`
        : ' for all providers';
      return [{ text: `Patients with scheduled appointments ${tf}${provs}`, connector: null }];
    },

    enterRemovalMode() {
      this.removalMode = true;
      this.selectedPatNums = [];
    },
    exitRemovalMode() {
      this.removalMode = false;
      this.selectedPatNums = [];
    },
    confirmRemovePatients() {
      this.removedPatNums = [...this.removedPatNums, ...this.selectedPatNums];
      this.selectedPatNums = [];
      this.showRemoveConfirm = false;
      this.removalMode = false;
      this.patientCurrentPage = 1;
    },

    async saveListOnly() {
      const name = `Patient List - ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      const patientList = this.activePreviewPatients.map(p => p.PatNum);
      const filterSummary = (this.filterSummaryLines || []).map(l => l.text + (l.connector ? ` ${l.connector}` : '')).join(' ').trim() || null;

      let filterConfig = null;
      if (this.form.recipientSource === 'customFilters' && this.form.customFilters) {
        filterConfig = this.form.customFilters;
      } else if (this.form.recipientSource === 'scheduledPatients') {
        filterConfig = {
          type: 'scheduledPatients',
          timeframe: this.scheduledTimeframe,
          dateFrom: this.scheduledDateFrom,
          dateTo: this.scheduledDateTo,
          providers: this.scheduledProviders
        };
      }

      try {
        await this.$store.dispatch('campaigns/createCampaign', {
          name,
          type: this.form.type,
          recipientSource: this.form.recipientSource,
          filterConfig,
          filterSummary,
          patientList,
          status: 'list'
        });
        this.$toast?.success?.('Patient list saved successfully');
      } catch (error) {
        console.error('Error saving patient list:', error);
        this.$toast?.error?.('Failed to save patient list');
      }
    },

    onScheduledTimeframeChange() {
      if (this.scheduledTimeframe !== 'custom') {
        this.scheduledDateFrom = null;
        this.scheduledDateTo = null;
      }
      this.recipientCount = 0;
      this.smsEligibleCount = 0;
        this.smsSuppressedCount = 0;
      this.emailEligibleCount = 0;
      this.previewPatients = [];
      this.previewError = '';
    },

    async handleScheduledPreview() {
      this.previewLoading = true;
      this.previewError = '';
      try {
        const dateRange = this.scheduledDateRange;
        if (!dateRange || !dateRange.from || !dateRange.to) {
          this.previewError = 'Please select a valid date range.';
          this.previewLoading = false;
          return;
        }

        const providers = this.scheduledProviders.map(p => p.value);
        const result = await this.$store.dispatch('campaigns/previewScheduledPatients', { dateRange, providers });

        // Handle previous removals (same pattern as handleFilterPreview)
        if (this.removedPatNums.length > 0) {
          const newPatNums = (result.patients || []).map(p => p.PatNum);
          const overlapping = this.removedPatNums.filter(pn => newPatNums.includes(pn));
          if (overlapping.length > 0) {
            this.pendingPreviewResult = result;
            this.showKeepRemovalsPrompt = true;
            this.previewLoading = false;
            return;
          }
          this.removedPatNums = [];
        }

        this.applyPreviewResult(result);
      } catch (err) {
        console.error('Scheduled preview error:', err);
        this.previewError = 'Failed to preview scheduled patients. Please try again.';
        this.recipientCount = 0;
        this.smsEligibleCount = 0;
        this.smsSuppressedCount = 0;
        this.emailEligibleCount = 0;
        this.previewPatients = [];
      } finally {
        this.previewLoading = false;
      }
    },

    async handleAllActivePatientsPreview() {
      this.previewLoading = true;
      this.previewError = '';
      try {
        const allActiveConfig = {
          timeframe: {
            mode: this.allActiveTimeframeMode,
            value: this.allActiveTimeframeValue,
            unit: this.allActiveTimeframeUnit,
            from: this.allActiveDateFrom,
            to: this.allActiveDateTo
          },
          providers: this.allActiveProviders.map(p => p.value),
          excludedProcCodes: ['D9986', 'D9987', ...this.allActiveExcludedProcCodes.map(c => c.value)]
        };

        const result = await this.$store.dispatch('campaigns/previewRecipients', {
          recipientSource: 'all',
          allActiveConfig
        });

        // Handle previous removals
        if (this.removedPatNums.length > 0) {
          const newPatNums = (result.patients || []).map(p => p.PatNum);
          const overlapping = this.removedPatNums.filter(pn => newPatNums.includes(pn));
          if (overlapping.length > 0) {
            this.pendingPreviewResult = result;
            this.showKeepRemovalsPrompt = true;
            this.previewLoading = false;
            return;
          }
          this.removedPatNums = [];
        }

        this.applyPreviewResult(result);
      } catch (err) {
        console.error('All active patients preview error:', err);
        this.previewError = 'Failed to preview active patients. Please try again.';
        this.recipientCount = 0;
        this.smsEligibleCount = 0;
        this.smsSuppressedCount = 0;
        this.emailEligibleCount = 0;
        this.previewPatients = [];
      } finally {
        this.previewLoading = false;
      }
    },

    onCustomFiltersValid(isValid) {
      this.customFiltersValid = isValid;
    },

    // Load SMS wiki templates when entering Step 3
    async loadSmsTemplates() {
      this.smsTemplatesLoading = true;
      try {
        const siteID = this.$store.state.customer.siteID;
        const result = await this.$store.dispatch('wiki/getWikiPages', { siteID, tags: 'sms-template' });
        // Reviews templates stay out: only the Reviews send path can fill their {{review_link}}.
        const pages = withoutReviewRequestTemplates((result && result[0]) || []);
        this.smsTemplateList = pages;
      } catch (e) {
        console.warn('Failed to load SMS templates:', e);
      } finally {
        this.smsTemplatesLoading = false;
      }
    },

    // Load email wiki templates when entering Step 3
    async loadEmailTemplates() {
      this.emailTemplatesLoading = true;
      try {
        const siteID = this.$store.state.customer.siteID;
        const result = await this.$store.dispatch('wiki/getWikiPages', { siteID, tags: 'email-template' });
        const pages = withoutReviewRequestTemplates((result && result[0]) || []);
        this.emailTemplateList = pages.filter(p => p.IsEmailTemplate === 1 || p.IsEmailTemplate === '1');
      } catch (e) {
        console.warn('Failed to load email templates:', e);
      } finally {
        this.emailTemplatesLoading = false;
      }
    },

    // Convert EditorJS JSON blocks to an HTML string (for email template preview and content)
    parseEditorJsToHtml(content) {
      if (!content) return '';
      try {
        const parsed = typeof content === 'string' ? JSON.parse(content) : content;
        return (parsed.blocks || []).map(block => {
          switch (block.type) {
            case 'header': {
              const level = block.data.level || 2;
              return `<h${level}>${block.data.text || ''}</h${level}>`;
            }
            case 'paragraph':
              return `<p>${block.data.text || ''}</p>`;
            case 'image': {
              const url = (block.data.file && block.data.file.url) || block.data.url || '';
              const caption = block.data.caption || '';
              return `<p><img src="${url}" style="max-width:100%;height:auto;" alt="${caption}"></p>${caption ? `<p><em>${caption}</em></p>` : ''}`;
            }
            case 'list': {
              const tag = block.data.style === 'ordered' ? 'ol' : 'ul';
              const items = (block.data.items || []).map(i => `<li>${i}</li>`).join('');
              return `<${tag}>${items}</${tag}>`;
            }
            case 'checklist': {
              const items = (block.data.items || []).map(i =>
                `<li><input type="checkbox" disabled ${i.checked ? 'checked' : ''}> ${i.text || ''}</li>`
              ).join('');
              return `<ul style="list-style:none;padding-left:0;">${items}</ul>`;
            }
            case 'table': {
              const rows = (block.data.content || []).map((row, idx) => {
                const cells = row.map(cell => idx === 0 ? `<th>${cell}</th>` : `<td>${cell}</td>`).join('');
                return `<tr>${cells}</tr>`;
              }).join('');
              return `<table border="1" style="border-collapse:collapse;width:100%;">${rows}</table>`;
            }
            case 'delimiter':
              return '<hr>';
            default:
              return block.data && block.data.text ? `<p>${block.data.text}</p>` : '';
          }
        }).join('\n');
      } catch (e) {
        return '';
      }
    },

    // Apply an email wiki template: render EditorJS → HTML, set emailContent
    applyEmailTemplate(template) {
      if (!template) return;
      this.form.emailContent = this.parseEditorJsToHtml(template.Content);
      this.selectedEmailTemplate = template;
      this.form.emailSubject = template.EmailSubject || template.Name;
    },

    // Clear the selected email template and reset email content
    clearEmailTemplate() {
      this.form.emailContent = '';
      this.selectedEmailTemplate = null;
    },

    // Apply a wiki template: parse EditorJS content, set textarea text + linkMap
    applySmsTemplate(template) {
      if (!template) return;
      const parsed = this.parseEditorJsContent(template.Content);
      this.form.smsContent = parsed.text;
      this.smsLinkMap = parsed.linkMap;
    },

    // Return plain-text preview for a template row (first ~100 chars)
    parsedTemplatePreview(template) {
      if (!template || !template.Content) return '';
      const parsed = this.parseEditorJsContent(template.Content);
      return parsed.text ? parsed.text.substring(0, 120) : '';
    },

    /**
     * Parse EditorJS JSON content to readable SMS text.
     * Converts <a> tags to [Form: name] / [Link: name] placeholders
     * and builds a linkMap for restoration before sending.
     * Copied from SmsTemplateSelectionModal.vue.
     */
    parseEditorJsContent(content) {
      const emptyResult = { text: '', linkMap: {} };
      if (!content) return emptyResult;
      try {
        if (typeof content === 'string' && !content.startsWith('{')) {
          return { text: content, linkMap: {} };
        }
        const parsed = typeof content === 'string' ? JSON.parse(content) : content;
        if (!parsed.blocks || !Array.isArray(parsed.blocks)) {
          return { text: String(content), linkMap: {} };
        }
        const textParts = [];
        const linkMap = {};
        const convertLinksToPlaceholders = (html) => {
          return html.replace(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (_match, href, displayText) => {
            if (!displayText || displayText.trim() === '') return '';
            const cleanText = displayText.replace(/<[^>]*>/g, '').trim();
            const placeholder = href.indexOf('patient_link_for_form') >= 0
              ? `[Form: ${cleanText}]`
              : `[Link: ${cleanText}]`;
            linkMap[placeholder] = _match;
            return placeholder;
          });
        };
        for (const block of parsed.blocks) {
          if (block.type === 'header') continue;
          if (block.data) {
            if (block.data.text) {
              let text = convertLinksToPlaceholders(block.data.text);
              text = text
                .replace(/<[^>]*>/g, '')
                .replace(/&nbsp;/g, ' ')
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .trim();
              if (text) textParts.push(text);
            }
            if (block.data.items && Array.isArray(block.data.items)) {
              for (const item of block.data.items) {
                const itemText = typeof item === 'string' ? item : item.text;
                if (itemText) {
                  let text = convertLinksToPlaceholders(itemText);
                  text = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                  if (text) textParts.push(`• ${text}`);
                }
              }
            }
          }
        }
        return { text: textParts.join('\n'), linkMap };
      } catch (e) {
        console.warn('Failed to parse EditorJS content:', e);
        return typeof content === 'string'
          ? { text: content.replace(/[{}[\]"]/g, '').substring(0, 200), linkMap: {} }
          : emptyResult;
      }
    },

    // Restore [Form: name] / [Link: name] placeholders back to HTML anchors before submitting
    restoreSmsLinks(text) {
      if (!text || !this.smsLinkMap || Object.keys(this.smsLinkMap).length === 0) return text;
      let resolved = text;
      for (const [placeholder, html] of Object.entries(this.smsLinkMap)) {
        resolved = resolved.split(placeholder).join(html);
      }
      return resolved;
    },

    // Insert a wiki-standard variable at the end of the SMS textarea
    insertSmsVariable(variable) {
      this.form.smsContent += variable;
      this.updateSmsPreview();
      if (this.$refs.smsInsertDropdown) this.$refs.smsInsertDropdown.hide();
    },

    // Insert an emoji at the end of the SMS textarea
    insertSmsEmoji(emoji) {
      this.form.smsContent += emoji;
      this.updateSmsPreview();
      if (this.$refs.smsInsertDropdown) this.$refs.smsInsertDropdown.hide();
    },

    buildScheduledAt() {
      if (!this.form.scheduledDate || this.scheduledHour === '') return null;
      let hour24 = parseInt(this.scheduledHour);
      if (this.scheduledAmPm === 'PM' && hour24 !== 12) hour24 += 12;
      if (this.scheduledAmPm === 'AM' && hour24 === 12) hour24 = 0;
      return `${this.form.scheduledDate} ${String(hour24).padStart(2, '0')}:${this.scheduledMinute}:00`;
    },

    insertVariable(type, variable) {
      if (type === 'sms') {
        this.form.smsContent += variable;
        this.updateSmsPreview();
      } else {
        this.form.emailContent += variable;
      }
    },

    insertEmoji(target, emoji) {
      if (target === 'subject') {
        this.form.emailSubject += emoji;
      } else if (target === 'content') {
        this.form.emailContent += emoji;
      }
    },

    insertSubjectVariable(variable) {
      this.form.emailSubject += variable;
    },

    openSendTestModal() {
      this.testEmail = '';
      this.testPhone = '';
      this.testMessageSent = false;
      this.sendingTest = false;
      this.testError = '';
      this.$refs.sendTestModal.show();
    },

    async sendTestMessage(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.sendingTest = true;
      this.testError = '';
      this.testMessageSent = false;

      const campaignId = this.campaign?.CampaignIDForWeb || null;

      try {
        const promises = [];

        // Send test SMS if applicable
        if ((this.form.type === 'SMS' || this.form.type === 'Both') && this.testPhone.trim()) {
          if (!campaignId) {
            this.testError = 'Please save the campaign as a draft before sending a test SMS.';
            this.sendingTest = false;
            return;
          }
          promises.push(
            this.$store.dispatch('campaigns/sendTestSMS', {
              campaignId,
              testPhone: this.testPhone.trim(),
              smsContent: this.form.smsContent
            })
          );
        }

        // Send test email if applicable (pass content directly — no draft required)
        if ((this.form.type === 'Email' || this.form.type === 'Both') && this.testEmail.trim()) {
          promises.push(
            this.$store.dispatch('campaigns/sendTestMessage', {
              campaignId,
              email: this.testEmail.trim(),
              emailSubject: this.form.emailSubject,
              emailContent: this.form.emailContent
            })
          );
        }

        await Promise.all(promises);
        this.testMessageSent = true;
      } catch (error) {
        console.error('Error sending test message:', error);
        this.testError = error.response?.data?.message || 'Failed to send test message. Please try again.';
      } finally {
        this.sendingTest = false;
      }
    },

    updateSmsPreview() {
      let preview = this.form.smsContent;
      // Replace wiki-standard variables with sample data for preview
      preview = preview.replace(/\{\{patient_first_name\}\}/g, 'John');
      preview = preview.replace(/\{\{patient_last_name\}\}/g, 'Smith');
      preview = preview.replace(/\{\{site_name\}\}/g, 'Dental Care');
      preview = preview.replace(/\{\{site_officephone\}\}/g, '(555) 123-4567');
      preview = preview.replace(/\{\{site_address1\}\}/g, '123 Main St');
      // Replace [Form: ...] and [Link: ...] placeholders with display text for preview
      preview = preview.replace(/\[Form: ([^\]]+)\]/g, '[$1]');
      preview = preview.replace(/\[Link: ([^\]]+)\]/g, '[$1]');
      this.smsPreviewText = preview;
    },

    async loadSmsMonthUsage() {
      this.smsMonthUsage = await this.$store.dispatch('campaigns/fetchSmsUsage') || { available: false };
    },

    // Customer's INCURRED charge if each text were `segmentsPerText` segments —
    // only the message credits that spill past the monthly plan are billed
    // (covered credits are $0 to the customer). Falls back to the gross value
    // when plan usage is unavailable so a potential charge is never understated.
    // Used by the formatting choice cards and the savings advisor so every dollar
    // the practice sees is one they would actually pay.
    smsCostForSegments(segmentsPerText) {
      const u = this.smsMonthUsage;
      const campaignSegments = Math.max(0, segmentsPerText) * this.smsRecipientsForEstimate;
      if (!u || !u.available || !Number.isFinite(u.segmentsUsed)) {
        return +(campaignSegments * OVERAGE_PRICE_PER_SEGMENT_USD).toFixed(2);
      }
      return estimateOverageCharge({
        segmentsUsed: u.segmentsUsed,
        allowance: u.allowance,
        campaignSegments
      }).overageCostUsd;
    },

    /**
     * Dispatch campaigns/sendCampaign, handling the server's segment-count
     * confirmation gate: large SMS campaigns get a 409 + estimate back on the
     * first attempt; we show the exact footprint and resend with the
     * confirmation token if the user accepts. Returns the send result, or
     * null if the user declined (campaign stays a draft).
     */
    // Reinforcement line for a send confirmation: "Balance after: ~X credits",
    // drawn from the live wallet store. Empty string when no wallet balance is
    // loaded, so surfaces without a wallet are unchanged.
    balanceAfterNote(creditsThisSend) {
      const w = this.$store.state.wallet;
      if (!w || !w.hasBalance) return '';
      const after = Math.max(0, (Number(w.creditsAvailable) || 0) - (Number(creditsThisSend) || 0));
      return `\n\nBalance after: ~${after.toLocaleString()} credits.`;
    },

    async sendCampaignWithSegmentGate(payload) {
      try {
        return await this.$store.dispatch('campaigns/sendCampaign', payload);
      } catch (err) {
        const status = err.response && err.response.status;
        // Carrier size hard block (422) → the message is too large for carriers to
        // deliver; there is no "confirm" — they must shorten it. Explain + stop.
        if (status === 422 && err.response?.data?.carrierBlocked) {
          await this.$bvModal.msgBoxOk(
            err.response.data.reason || 'This message is too large for mobile carriers to deliver. Please shorten it.',
            { title: 'Message too large to send', okVariant: 'danger', centered: true }
          );
          return null;
        }
        // Overage charge failed server-side → send was blocked; explain + stop.
        if (status === 402 && err.response?.data?.paymentFailed) {
          await this.$bvModal.msgBoxOk(buildPaymentFailedMessage(err.response.data), {
            title: 'Payment needed to send', okVariant: 'warning', centered: true,
          });
          return null;
        }
        const estimate = err.response && err.response.data && err.response.data.estimate;
        if (status !== 409 || !estimate) throw err;

        // Two server gates can fire (large volume, over-allowance charge). The charge
        // needs explicit dollar consent; interpretSendGate tells us which to show and
        // gives the echoes that clear BOTH on resend.
        // P4c: the server may send a `wallet` object (credit-wallet consent path) alongside the
        // estimate; interpretSendGate prefers the wallet shortfall as the amount to consent to.
        // E3: `walletChannels` (multi-pool) supersedes the single `wallet` when present — the
        // consent then covers a COMBINED top-up across pools (SMS + Email).
        const g = interpretSendGate(estimate, err.response?.data?.wallet, err.response?.data?.walletChannels);
        let approved;
        if (g.hasOverage) {
          approved = await this.$refs.overageModal.open({
            // the amount actually billed = overage floored to Stripe's $0.50 minimum;
            // rawOverage + minimumApplied let the dialog explain a rounded-up small charge
            amount: g.chargeAmount,
            rawOverage: g.overageAmount,
            minimumApplied: g.minimumApplied,
            chargeableSegments: g.chargeableSegments,
            ratePerSegment: OVERAGE_PRICE_PER_SEGMENT_USD,
            // Wallet consent context (null on the legacy allowance path) — free credits still
            // remaining + the existing paid balance both already cover part of the campaign.
            wallet: g.wallet,
            // Multi-pool: per-channel top-ups + combined total (empty/false → single-pool path).
            multiChannel: g.multiChannel,
            channels: g.channels,
            totalTopUpUsd: g.totalTopUpUsd,
          });
        } else {
          approved = await this.$bvModal.msgBoxConfirm(
            buildVolumeConfirmMessage(g) + this.balanceAfterNote(g.totalSegments),
            { title: 'Confirm large campaign', okTitle: 'Yes, send campaign', cancelTitle: 'Go back', okVariant: 'success', centered: true }
          );
        }
        if (!approved) return null;

        return await this.$store.dispatch('campaigns/sendCampaign', {
          ...payload,
          ...g.confirmEcho,
        });
      }
    },

    getTypeVariant(type) {
      const variants = {
        'SMS': 'success',
        'Email': 'primary',
        'Both': 'info'
      };
      return variants[type] || 'secondary';
    },

    async saveDraft() {
      // Save current wizard progress as a draft — works from any step
      const name = this.form.name.trim() || `Draft - ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      const filterSummary = (this.filterSummaryLines || []).map(l => l.text + (l.connector ? ` ${l.connector}` : '')).join(' ').trim() || null;

      const wizardState = {
        currentStep: this.currentStep,
        scheduledTimeframe: this.scheduledTimeframe,
        scheduledDateFrom: this.scheduledDateFrom,
        scheduledDateTo: this.scheduledDateTo,
        scheduledProviders: this.scheduledProviders,
        removedPatNums: this.removedPatNums || [],
        // AI Call: persist prioritization choices through the draft so they restore on reopen.
        priorityPreset: this.form.priorityPreset || 'standard',
        priorityConfig: this.form.priorityConfig || { pin: [], exclude: [] },
        // AI Call (Phase D.2c): persist hand-added patients so they restore on reopen.
        handAddedPatients: this.form.handAddedPatients || []
      };

      // ai_call sends the effective union (audience + hand-adds); other channels unchanged.
      const patientList = this.campaignSendPatNums;

      let filterConfig = null;
      if (this.form.recipientSource === 'customFilters' && this.form.customFilters) {
        filterConfig = this.form.customFilters;
      } else if (this.form.recipientSource === 'scheduledPatients') {
        filterConfig = {
          type: 'scheduledPatients',
          timeframe: this.scheduledTimeframe,
          dateFrom: this.scheduledDateFrom,
          dateTo: this.scheduledDateTo,
          providers: this.scheduledProviders
        };
      }

      const campaignData = {
        name,
        description: this.form.description || null,
        type: this.form.type || 'SMS',
        recipientSource: this.form.recipientSource,
        subject: this.form.emailSubject || '',
        htmlContent: this.form.emailContent || '',
        textContent: this.restoreSmsLinks(this.form.smsContent) || '',
        filterConfig,
        filterSummary,
        wizardState,
        patientList,
        status: 'draft',
        // AI Call drafts must carry the follow-up type (default 3) — mirrors the send path
        // so the campaign-service validateAiCallCampaign gate doesn't 400 a draft save.
        // Non-ai_call channels send null (the backend ignores it for SMS/Email).
        callFollowupTaskTypeID: this.form.type === 'ai_call' ? this.form.callFollowupTaskTypeID : null
      };

      this.overlayTitle = 'Saving Draft';
      this.overlayMessage = 'Please wait while we save your draft...';
      this.savingDraft = true;

      try {
        // Update the campaign being edited, or the one this wizard session already created;
        // only POST a new one when neither exists.
        const targetId = resolveSaveTarget({
          isEditing: this.isEditing,
          campaign: this.campaign,
          createdCampaign: this.createdCampaign
        });

        let result;
        if (targetId) {
          result = await this.$store.dispatch('campaigns/updateCampaign', {
            campaignId: targetId,
            campaignData
          });
        } else {
          result = await this.$store.dispatch('campaigns/createCampaign', campaignData);
          this.createdCampaign = result;
        }

        this.savingDraft = false;
        this.$emit('saved', result);
        this.hide();
        this.$toast?.success?.('Draft saved successfully');
      } catch (error) {
        this.savingDraft = false;
        console.error('Error saving draft:', error);
        this.$toast?.error?.(extractApiError(error, 'Failed to save draft'));
      }
    },

    async saveCampaign() {
      this.validated = true;

      // Validate that selected campaign type is enabled
      if (this.form.type === 'SMS' && !this.isSmsEnabled) {
        this.$toast?.error?.('SMS is not enabled for this site. Please select a different campaign type.') || alert('SMS is not enabled for this site.');
        return;
      }
      if (this.form.type === 'Email' && !this.isEmailEnabled) {
        this.$toast?.error?.('Email is not enabled for this site. Please select a different campaign type.') || alert('Email is not enabled for this site.');
        return;
      }
      if (this.form.type === 'Both' && (!this.isSmsEnabled || !this.isEmailEnabled)) {
        this.$toast?.error?.('Both SMS and Email must be enabled to use this campaign type.') || alert('Both SMS and Email must be enabled.');
        return;
      }

      // Auto-generate name for drafts if blank
      const name = this.form.name.trim() || `Draft - ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

      // Build filter summary string from computed lines
      const filterSummary = (this.filterSummaryLines || []).map(l => l.text + (l.connector ? ` ${l.connector}` : '')).join(' ').trim() || null;

      // Build wizard state for draft resumption
      const wizardState = {
        currentStep: this.currentStep,
        scheduledTimeframe: this.scheduledTimeframe,
        scheduledDateFrom: this.scheduledDateFrom,
        scheduledDateTo: this.scheduledDateTo,
        scheduledProviders: this.scheduledProviders,
        removedPatNums: this.removedPatNums || [],
        // AI Call: persist prioritization choices through the draft so they restore on reopen.
        priorityPreset: this.form.priorityPreset || 'standard',
        priorityConfig: this.form.priorityConfig || { pin: [], exclude: [] },
        // AI Call (Phase D.2c): persist hand-added patients so they restore on reopen.
        handAddedPatients: this.form.handAddedPatients || []
      };

      // Build patient list from active preview. For ai_call this is the effective union
      // (audience + hand-added patients); every other channel is the audience only.
      const patientList = this.campaignSendPatNums;

      // Build filter config based on recipient source
      let filterConfig = null;
      if (this.form.recipientSource === 'customFilters' && this.form.customFilters) {
        filterConfig = this.form.customFilters;
      } else if (this.form.recipientSource === 'scheduledPatients') {
        filterConfig = {
          type: 'scheduledPatients',
          timeframe: this.scheduledTimeframe,
          dateFrom: this.scheduledDateFrom,
          dateTo: this.scheduledDateTo,
          providers: this.scheduledProviders
        };
      }

      const status = this.form.scheduleType === 'draft' ? 'draft' : this.form.scheduleType === 'scheduled' ? 'scheduled' : 'draft';

      const overlayLabel = this.form.scheduleType === 'now' ? 'Sending Campaign' : this.form.scheduleType === 'scheduled' ? 'Scheduling Campaign' : 'Saving Draft';
      this.overlayTitle = overlayLabel;
      this.overlayMessage = 'Please wait...';
      this.savingDraft = true;

      const campaignData = {
        name,
        description: this.form.description || null,
        type: this.form.type,
        recipientSource: this.form.recipientSource,
        subject: this.form.emailSubject || '',
        htmlContent: this.form.emailContent || '',
        textContent: this.restoreSmsLinks(this.form.smsContent) || this.form.emailContent || '',
        filterConfig,
        filterSummary,
        wizardState,
        patientList,
        status,
        scheduledAt: this.form.scheduleType === 'scheduled' ? this.buildScheduledAt() : null
      };

      // AI Call campaigns carry the chosen follow-up list type through create
      if (this.form.type === 'ai_call') {
        campaignData.callFollowupTaskTypeID = this.form.callFollowupTaskTypeID;
      }

      try {
        // Update the campaign being edited, or the one this wizard session already created;
        // only POST a new one when neither exists. Without this, a "Create & Send" whose SEND
        // failed (the row exists, isEditing is still false, the wizard stayed open) would create
        // a sibling campaign on the next click instead of re-sending the one just created.
        const targetId = resolveSaveTarget({
          isEditing: this.isEditing,
          campaign: this.campaign,
          createdCampaign: this.createdCampaign
        });

        let result;
        if (targetId) {
          result = await this.$store.dispatch('campaigns/updateCampaign', {
            campaignId: targetId,
            campaignData
          });
        } else {
          result = await this.$store.dispatch('campaigns/createCampaign', campaignData);
          this.createdCampaign = result;
        }

        // If "Send Now", also trigger the send
        if (this.form.scheduleType === 'now' && result) {
          // targetId first: the PUT response doesn't necessarily echo the web id, and on the
          // re-send path this.campaign is null (isEditing false) — targetId is the row we wrote.
          const campaignId = targetId || result.campaignIDForWeb || result.CampaignIDForWeb || this.campaign?.CampaignIDForWeb;
          if (campaignId) {
            const sendResult = await this.sendCampaignWithSegmentGate({
              campaignId,
              campaignType: this.form.type,
              customFilters: this.form.recipientSource === 'customFilters' ? this.form.customFilters : null,
              selectedList: patientList,
              smsContent: this.restoreSmsLinks(this.form.smsContent) || null,
              smsFormatting: this.form.smsFormatting || 'optimized',
              includeSuppressed: this.form.includeSuppressed || false,
              filterSummary,
              recipientSource: this.form.recipientSource,
              removedPatNums: this.removedPatNums || [],
              trackClicks: this.form.trackClicks,
              // For ai_call the server branches on campaignType + needs the follow-up list type
              callFollowupTaskTypeID: this.form.type === 'ai_call' ? this.form.callFollowupTaskTypeID : null,
              // For ai_call only: which patients to call first ('standard' = no prioritization)
              // plus the operator's pin/exclude edits (backend applies them at launch).
              ...(this.form.type === 'ai_call'
                ? {
                    priorityPreset: this.form.priorityPreset || 'standard',
                    priorityConfig: buildPriorityConfig(this.form.priorityConfig, patientList)
                  }
                : {})
            });

            // null = user reviewed the segment estimate and chose not to send.
            // Campaign stays as a draft; keep the wizard open on the review step.
            if (sendResult === null) {
              this.savingDraft = false;
              this.$toast?.info?.('Send canceled — campaign saved as a draft');
              return;
            }

            // Optimistic local decrement so the header credit pill reflects this
            // send instantly; the next siteWallet snapshot reconciles the number.
            if ((this.form.type === 'SMS' || this.form.type === 'Both') && this.smsCampaignTotalSegments > 0) {
              this.$store.commit('wallet/applyOptimisticDraw', this.smsCampaignTotalSegments);
            }

            this.savingDraft = false;
            this.$emit('saved', result);
            this.sentRecipientCount = (sendResult && (sendResult.uniquePatientCount || sendResult.totalRecipients)) || this.effectiveRecipientCount || 0;
            this.sentSkippedCount = (sendResult && sendResult.suppressedCount) || 0;
            this.campaignSent = true;
            this.$store.dispatch('campaigns/startCampaignPolling', campaignId);
            return;
          }
        }

        this.savingDraft = false;
        this.$emit('saved', result);
        this.hide();

        const message = this.form.scheduleType === 'scheduled'
          ? 'Campaign scheduled successfully!'
          : 'Campaign saved as draft';

        this.$toast?.success?.(message);
      } catch (error) {
        this.savingDraft = false;
        console.error('Error saving campaign:', error);
        this.$toast?.error?.(extractApiError(error, 'Failed to save campaign'));
      }
    }
  },

  filters: {
    formatNumber(value) {
      if (!value) return '0';
      return value.toLocaleString();
    },
    formatListDate(value) {
      if (!value) return '';
      const d = new Date(value);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
};
</script>

<style lang="scss" scoped>
// ── Campaign cost card — "receipt" treatment (mockup A, 2026-07-03) ─────────
// Bigger type than the rest of the composer chrome on purpose: money must be
// readable. 14px base, 22px hero total, tabular numerals throughout.
// Live segment/credit counter under the textarea — prominent + colour-coded
// (2026-07-03: the old grey "X/160 characters" line was too easy to miss).
.sms-counter-live {
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-left: 4px solid var(--scl, #22c55e);
  border-radius: 10px;
  background: #f8fafc;
  padding: 10px 12px;

  &.tone-ok   { --scl: #22c55e; }
  &.tone-mid  { --scl: #2563eb; }
  &.tone-warn { --scl: #f59e0b; }

  .scl-top {
    display: flex; align-items: baseline; justify-content: space-between;
    gap: 10px; flex-wrap: wrap;
  }
  .scl-pill {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--scl, #22c55e); color: #fff;
    font-weight: 700; font-size: 0.82rem; border-radius: 999px; padding: 4px 11px;
    white-space: nowrap;
    i { font-size: 0.78rem; }
  }
  .scl-read { text-align: right; font-variant-numeric: tabular-nums; line-height: 1.1; }
  .scl-count { font-size: 1.35rem; font-weight: 800; color: #0f172a; letter-spacing: -0.01em; }
  .scl-unit { font-size: 0.8rem; color: #64748b; font-weight: 600; margin-left: 3px; }

  .scl-track {
    height: 7px; border-radius: 6px; background: #e2e8f0; overflow: hidden; margin: 9px 0 7px;
  }
  .scl-fill { height: 100%; border-radius: 6px; background: var(--scl, #22c55e); transition: width 0.15s ease; }

  .scl-foot {
    display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap;
    font-size: 0.78rem; color: #64748b;
    b { color: #0f172a; font-variant-numeric: tabular-nums; }
  }
  .scl-uni {
    display: inline-flex; align-items: center; font-weight: 600; color: #b45309;
    background: #fffbeb; border: 1px solid #fde68a; border-radius: 999px; padding: 2px 9px; font-size: 0.74rem;
  }
}

// Disconnected-number skip notice (Phase 2) — informational, with a per-send override.
.dead-number-notice {
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 10px;
  padding: 10px 12px;

  .dnn-head {
    display: flex; align-items: center;
    font-weight: 700; color: #374151; font-size: 0.9rem;

    strong { color: #b45309; }
  }

  .dnn-body {
    font-size: 0.82rem; color: #6b7280; line-height: 1.45; margin: 3px 0 6px;
  }

  .dnn-override {
    font-size: 0.82rem; color: #374151;
  }
}

// Carrier size hard stop — the one red, blocking state in this panel.
.sms-carrier-block {
  margin-top: 10px;
  border: 1.5px solid #fca5a5;
  background: #fef2f2;
  border-radius: 10px;
  padding: 12px 14px;

  .scb-head {
    display: flex; align-items: center;
    font-weight: 800; font-size: 0.95rem; color: #b91c1c;
    margin-bottom: 4px;
  }

  .scb-body {
    font-size: 0.85rem; color: #7f1d1d; line-height: 1.45;

    strong { color: #991b1b; }
  }
}

// Plain-English money-saving tips (collapsible) — concepts only, Teamio rate.
.sms-tips {
  margin-top: 10px;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  background: #f0fdf4;
  overflow: hidden;

  .sms-tips-head {
    display: flex; align-items: center; gap: 10px; cursor: pointer;
    padding: 11px 13px; list-style: none;
    &::-webkit-details-marker { display: none; }
  }
  .sms-tips-bulb {
    flex: none; width: 26px; height: 26px; border-radius: 8px; display: grid; place-items: center;
    background: #fff; border: 1px solid #bbf7d0; font-size: 0.95rem;
  }
  .sms-tips-title { flex: 1; font-weight: 700; font-size: 0.88rem; color: #15803d; }
  .sms-tips-chev { color: #15803d; transition: transform 0.15s ease; }
  &[open] .sms-tips-chev { transform: rotate(90deg); }

  .sms-tips-body { padding: 0 14px 6px; }
  .sms-tip {
    display: flex; gap: 11px; padding: 11px 0; border-top: 1px solid #bbf7d0;
    &:first-child { border-top: 0; padding-top: 4px; }
  }
  .sms-tip-num {
    flex: none; width: 21px; height: 21px; border-radius: 50%; display: grid; place-items: center;
    background: #15803d; color: #fff; font-size: 0.7rem; font-weight: 700; margin-top: 1px;
  }
  .sms-tip-text {
    font-size: 0.83rem; color: #14532d; line-height: 1.5;
    strong { color: #052e16; }
    code {
      background: #fff; border: 1px solid #bbf7d0; border-radius: 4px; padding: 0 4px;
      font-size: 0.82em; color: #15803d;
    }
  }
  .sms-tip-chip {
    display: inline-block; font-weight: 700; color: #15803d;
    background: #fff; border: 1px solid #bbf7d0; border-radius: 7px; padding: 1px 8px;
    font-variant-numeric: tabular-nums;
  }
  .sms-tips-foot {
    font-size: 0.74rem; color: #3f6212; background: #fff; border-top: 1px solid #bbf7d0; padding: 8px 14px;
  }
}

.sms-cost-card {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-size: 14px;
  color: #1f2937;

  .sms-line-val, .sms-choice-price, .sms-meter-head, .sms-sugg-price {
    font-variant-numeric: tabular-nums;
  }
}

// formatting choice as selectable priced cards
.sms-choice-row {
  display: flex;
  gap: 10px;

  @media (max-width: 575px) { flex-direction: column; }
}

.sms-choice {
  flex: 1;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  background: #fff;
  margin-bottom: 0;

  &.is-selected {
    border-color: #15803d;
    background: #f0fdf4;

    .sms-choice-price { color: #15803d; }
  }

  &.sms-choice--costly .sms-choice-price { color: #b91c1c; }
  &.sms-choice--costly.is-selected {
    border-color: #b45309;
    background: #fffbeb;
    .sms-choice-price { color: #b45309; }
  }

  .sms-choice-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .sms-choice-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .sms-choice-title {
    font-weight: 700;
    font-size: 14px;
  }

  .sms-choice-badge {
    font-size: 11px;
    font-weight: 700;
    color: #15803d;
    background: #dcfce7;
    padding: 1px 8px;
    border-radius: 10px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    vertical-align: 1px;
  }

  .sms-choice-price {
    font-size: 18px;
    font-weight: 800;
    white-space: nowrap;
  }

  .sms-choice-sub {
    display: block;
    color: #6b7280;
    font-size: 12.5px;
    margin-top: 2px;

    code {
      background: #fef3c7;
      color: #b45309;
      padding: 0 4px;
      border-radius: 3px;
      font-size: 12px;
    }
  }
}

// monthly usage meter
.sms-meter-block {
  display: flex;
  flex-direction: column;
  gap: 6px;

  .sms-meter-head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    color: #6b7280;

    strong { color: #1f2937; }
    .sms-meter-over { color: #b45309; font-weight: 700; }
    .sms-meter-ok { color: #15803d; font-weight: 700; }
  }

  .sms-meter {
    display: flex;
    height: 14px;
    border-radius: 7px;
    overflow: hidden;
    background: #f3f4f6;
    position: relative;

    .sms-meter-used { background: #94a3b8; }
    .sms-meter-campaign { background: #22c55e; }
    .sms-meter-overbar { background: #f59e0b; }

    .sms-meter-cap {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 2px;
      background: #1f2937;
    }
  }

  .sms-meter-legend {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    font-size: 12.5px;
    color: #6b7280;

    strong { color: #1f2937; }

    .sms-meter-dot {
      display: inline-block;
      width: 9px;
      height: 9px;
      border-radius: 2px;
      margin-right: 5px;
    }
  }
}

// itemized receipt
.sms-lines {
  display: flex;
  flex-direction: column;

  .sms-line {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 7px 0;

    & + .sms-line { border-top: 1px solid #f3f4f6; }

    .sms-line-label {
      color: #6b7280;

      small {
        display: block;
        color: #9ca3af;
        font-size: 12px;
      }
    }

    .sms-line-val {
      font-weight: 600;
      white-space: nowrap;

      &--covered { color: #15803d; }
    }

    &.sms-line--total {
      border-top: 2px solid #e5e7eb;
      margin-top: 2px;
      padding-top: 10px;

      .sms-line-label { color: #1f2937; font-weight: 700; font-size: 15px; }

      .sms-line-val {
        font-size: 22px;
        font-weight: 800;

        &--due { color: #b45309; }
        &--zero { color: #15803d; }
      }
    }
  }

  .sms-line-note {
    color: #6b7280;
    font-size: 12.5px;
    padding-top: 4px;
  }
}

// savings advisor
.sms-advisor {
  border-top: 1px dashed #fde68a;
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .sms-advisor-title {
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #b45309;
  }

  .sms-sugg {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 8px;
    padding: 8px 12px;

    .sms-sugg-label {
      font-size: 13.5px;
      color: #78350f;

      small {
        display: block;
        color: #6b7280;
        font-size: 12px;
      }
    }

    .sms-sugg-price {
      text-align: right;
      white-space: nowrap;
      font-weight: 800;
      font-size: 15px;
      color: #92400e;

      small {
        display: block;
        font-size: 12px;
        font-weight: 700;
        color: #15803d;
      }
    }
  }

  .sms-advisor-lastresort {
    color: #9ca3af;
    font-size: 12.5px;
  }
}

// Teamio-internal box (dev + super admin only — see smsInternalCostVisible)
.sms-internal {
  border: 1.5px dashed #c7d2fe;
  background: #eef2ff;
  border-radius: 8px;
  padding: 10px 12px;
  color: #4338ca;

  .sms-internal-title {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .sms-line {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 3px 0;
    font-size: 13.5px;
    font-variant-numeric: tabular-nums;

    & + .sms-line { border-top: 1px solid #c7d2fe; }

    .sms-line-label { color: #4338ca; opacity: 0.85; }
    .sms-line-val { font-weight: 700; }
  }
}

.create-campaign-modal {
  .wizard-progress {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;
    padding: 0 1rem;

    .progress-step {
      display: flex;
      align-items: center;
      flex: 1;
      position: relative;

      &:not(:last-child)::after {
        content: '';
        flex: 1;
        height: 2px;
        background: #e2e8f0;
        margin: 0 1rem;
      }

      &.completed::after {
        background: #3abf94;
      }

      &.clickable {
        cursor: pointer;
      }

      .step-indicator {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        color: #718096;
        flex-shrink: 0;
      }

      .step-label {
        margin-left: 0.75rem;
        font-size: 0.875rem;
        color: #718096;
        white-space: nowrap;
      }

      &.active {
        .step-indicator {
          background: #547fff;
          color: white;
        }
        .step-label {
          color: #547fff;
          font-weight: 600;
        }
      }

      &.completed {
        .step-indicator {
          background: #3abf94;
          color: white;
        }
        .step-label {
          color: #3abf94;
        }
      }
    }
  }

  .wizard-content {
    min-height: 400px;
    max-height: 65vh;
    overflow-y: auto;
    padding: 0 1rem;

    .step-title {
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 0.5rem;
    }

    .step-subtitle {
      color: #718096;
      margin-bottom: 1.5rem;
    }

    .section-label {
      font-weight: 600;
      color: #4a5568;
    }
  }

  .campaign-type-selector {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;

    .type-option {
      flex: 1;
      min-width: 200px;
      padding: 1.25rem;
      border: 2px solid #e2e8f0;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: flex-start;
      gap: 1rem;

      &:hover:not(.disabled) {
        border-color: #cbd5e0;
        background: #f8f9fa;
      }
      
      &:not(.disabled) {
        cursor: pointer;
      }

      &.selected {
        border-color: #3abf94;
        background: rgba(58, 191, 148, 0.05);
      }

      &.disabled {
        opacity: 0.7;
        cursor: not-allowed;
        background: #f8f9fa;
        border-color: #e2e8f0;
        
        .type-icon.disabled {
          background: #cbd5e0 !important;
          opacity: 0.6;
        }
        
        .type-info {
          h6 {
            color: #a0aec0;
          }
          p {
            color: #cbd5e0;
          }
        }
        
        &:hover {
          border-color: #e2e8f0;
          background: #f8f9fa;
          transform: none;
        }
      }

      .type-icon {
        width: 48px;
        height: 48px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        i {
          font-size: 1.25rem;
          color: white;
        }

        &.sms-icon {
          background: linear-gradient(135deg, #3abf94 0%, #2d9474 100%);
        }
        &.email-icon {
          background: linear-gradient(135deg, #547fff 0%, #3d5dff 100%);
        }
        &.both-icon {
          background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
        }
        &.ai-call-icon {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
        }

        &.disabled {
          background: #cbd5e0 !important;
          opacity: 0.6;
        }
      }

      .type-info {
        flex: 1;

        h6 {
          margin-bottom: 4px;
          font-weight: 600;
          color: #2d3748;
        }

        p {
          margin-bottom: 4px;
          font-size: 0.85rem;
          color: #718096;
        }
        
        small {
          display: block;
          font-size: 0.75rem;
          margin-top: 4px;
          font-style: italic;
        }
      }

      .type-check {
        font-size: 1.25rem;
      }
    }

    .no-features-warning {
      width: 100%;
      margin-top: 1rem;
      
      .alert {
        border-radius: 8px;
        padding: 1rem;
      }
    }
  }

  .audience-method-selector {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 0.5rem;

    .method-card {
      display: flex;
      align-items: center;
      gap: 1.25rem;
      padding: 1.5rem;
      border: 2px solid #e2e8f0;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: #cbd5e0;
        background: #f8f9fa;
        transform: translateX(4px);

        .method-arrow {
          color: #547fff;
        }
      }

      .method-icon {
        width: 52px;
        height: 52px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        i {
          font-size: 1.25rem;
          color: white;
        }
      }

      .method-info {
        flex: 1;

        h6 {
          margin-bottom: 4px;
          font-weight: 600;
          color: #2d3748;
          font-size: 1rem;
        }

        p {
          margin-bottom: 0;
          font-size: 0.875rem;
          color: #718096;
        }
      }

      .method-arrow {
        color: #cbd5e0;
        font-size: 1.25rem;
        transition: color 0.2s ease;
      }
    }
  }

  .audience-back-link {
    a {
      font-size: 0.875rem;
      color: #547fff;
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .filter-multiselect ::v-deep {
    .multiselect__tags {
      min-height: 34px;
      padding: 4px 40px 0 8px;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      font-size: 0.8125rem;
    }
    .multiselect__tag {
      background: #d1fae5;
      color: #065f46;
      border-radius: 4px;
      font-size: 0.75rem;
      padding: 2px 22px 2px 8px;
      margin-bottom: 2px;
    }
    .multiselect__tag-icon {
      &:after { color: #10b981; }
      &:hover { background: #a7f3d0; }
    }
    .multiselect__option--highlight {
      background: #10b981;
    }
    .multiselect__content-wrapper {
      z-index: 1060;
      border-radius: 6px;
      border-color: #e2e8f0;
      max-height: 200px;
    }
    .multiselect__placeholder {
      font-size: 0.8125rem;
      color: #94a3b8;
      padding-top: 0;
      margin-bottom: 0;
    }
  }

  .custom-filters-section {
    ::v-deep .multiselect__content-wrapper {
      z-index: 1060;
    }
  }

  .patient-table-section {

    .patient-preview-table {
      font-size: 0.8125rem;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;

      ::v-deep th {
        background: #f8fafc;
        color: #64748b;
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        border-bottom: 2px solid #e2e8f0;
        padding: 0.5rem 0.75rem;
      }

      ::v-deep td {
        padding: 0.4rem 0.75rem;
        vertical-align: middle;
      }
    }
  }

  // AI Call editable ranked list (Phase D.2b)
  .ai-ranked-table {
    font-size: 0.8125rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;

    ::v-deep th {
      background: #f8fafc;
      color: #64748b;
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      border-bottom: 2px solid #e2e8f0;
      padding: 0.5rem 0.75rem;
    }

    ::v-deep td {
      padding: 0.4rem 0.75rem;
      vertical-align: middle;
    }
  }

  .ai-ranked-excluded {
    text-decoration: line-through;
    color: #94a3b8;
  }

  .recipient-preview {
    margin-top: 1.5rem;

    .preview-card {
      display: flex;
      align-items: center;
      padding: 1.25rem;
      background: linear-gradient(135deg, rgba(58, 191, 148, 0.1) 0%, rgba(84, 127, 255, 0.1) 100%);
      border-radius: 12px;
      border: 1px solid rgba(58, 191, 148, 0.2);

      .preview-icon {
        width: 56px;
        height: 56px;
        background: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

        i {
          font-size: 1.5rem;
          color: #3abf94;
        }
      }

      .preview-content {
        h5 {
          margin-bottom: 4px;
          font-weight: 700;
          color: #2d3748;
        }

        p {
          color: #718096;
          font-size: 0.9rem;
        }
      }
    }
  }

  .message-section {
    padding: 1.25rem;
    background: #f8f9fa;
    border-radius: 12px;
    border: 1px solid #e9ecef;

    .message-editor {
      .message-textarea {
        border-radius: 8px;
        border: 1px solid #e2e8f0;
        resize: none;

        &:focus {
          border-color: #547fff;
          box-shadow: 0 0 0 3px rgba(84, 127, 255, 0.1);
        }
      }

      .message-tools {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 0.5rem;

        .character-count {
          font-size: 0.8rem;
          color: #718096;

          &.warning {
            color: #f59e0b;
          }

          .segments-info {
            color: #ef4444;
            font-weight: 500;
          }
        }
      }
    }

    .variables-panel {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 1rem;
      height: 100%;

      .variables-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #4a5568;
        margin-bottom: 0.75rem;
        display: block;
      }

      .variables-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        max-height: 200px;
        overflow-y: auto;

        .variable-item {
          padding: 0.625rem 0.75rem;
          background: #f8f9fa;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: #eef2ff;
            border-color: #547fff;
            transform: translateX(2px);
          }

          .variable-name {
            font-size: 0.875rem;
            font-weight: 500;
            color: #2d3748;
            margin-bottom: 2px;
          }

          .variable-code {
            font-size: 0.75rem;
            color: #718096;
            font-family: 'Courier New', monospace;
          }
        }
      }
    }
  }

  .sms-preview-section {
    .preview-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #4a5568;
      display: block;
    }

    .phone-preview-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
    }

    .phone-mockup-campaign {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .phone-frame-campaign {
      width: 220px;
      height: 380px;
      background: #1a1a1a;
      border-radius: 32px;
      padding: 10px;
      box-shadow: 
        0 20px 40px -12px rgba(0, 0, 0, 0.4),
        inset 0 0 0 2px #333,
        inset 0 0 0 4px #1a1a1a;
      position: relative;
    }

    .phone-notch-campaign {
      width: 90px;
      height: 20px;
      background: #1a1a1a;
      border-radius: 0 0 16px 16px;
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10;
    }

    .phone-screen-campaign {
      width: 100%;
      height: 100%;
      background: #f5f5f5;
      border-radius: 24px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .phone-header-campaign {
      background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
      color: white;
      padding: 28px 12px 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 11px;
      font-weight: 500;
    }

    .phone-time-campaign {
      font-weight: 600;
    }

    .phone-carrier-campaign {
      font-size: 10px;
      opacity: 0.9;
    }

    .phone-content-campaign {
      flex: 1;
      padding: 12px;
      overflow-y: auto;
      background: #e5ddd5;
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4ccc4' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }

    .message-bubble-campaign {
      max-width: 90%;
      padding: 8px 12px;
      border-radius: 14px;
      margin-bottom: 6px;
      position: relative;
      word-wrap: break-word;

      &.outgoing {
        background: linear-gradient(135deg, #dcf8c6 0%, #c5e8a5 100%);
        margin-left: auto;
        border-bottom-right-radius: 4px;
        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
      }

      &.split-bubble {
        position: relative;
        margin-bottom: 10px;
        border: 2px dashed rgba(58, 191, 148, 0.3);
        background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%) !important;
      }
    }

    .split-indicator-campaign {
      position: absolute;
      top: -8px;
      right: 8px;
      background: linear-gradient(135deg, #3abf94 0%, #2d9474 100%);
      color: white;
      font-size: 9px;
      font-weight: 700;
      padding: 2px 6px;
      border-radius: 10px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }

    .message-text-campaign {
      font-size: 14px;
      color: #1a1a1a;
      line-height: 1.4;
      word-wrap: break-word;
    }

    .message-time-campaign {
      font-size: 10px;
      color: #718096;
      margin-top: 4px;
      text-align: right;
    }

    .phone-label-campaign {
      margin-top: 8px;
      font-size: 0.75rem;
      color: #718096;
      font-weight: 500;
    }

    .sms-counter-campaign {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.25rem;
      background: #f8f9fa;
      border-radius: 8px;
      border: 1px solid #e2e8f0;

      .counter-main-campaign {
        display: flex;
        align-items: baseline;
        gap: 0.25rem;
        font-size: 0.875rem;

        .char-count-campaign {
          font-weight: 600;
          color: #2d3748;
        }

        .char-limit-campaign {
          color: #718096;
        }
      }

      .segment-info-campaign {
        .segment-badge-campaign {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.25rem 0.625rem;
          border-radius: 12px;
          font-size: 0.75rem;
          background: #e2e8f0;
          color: #4a5568;

          &.multi-segment {
            background: #fef3c7;
            color: #92400e;
          }

          i {
            font-size: 0.7rem;
          }
        }
      }

      &.counter-ok {
        .char-count-campaign {
          color: #3abf94;
        }
      }

      &.counter-caution {
        .char-count-campaign {
          color: #f59e0b;
        }
      }

      &.counter-split {
        .char-count-campaign {
          color: #f59e0b;
        }
      }

      &.counter-danger {
        .char-count-campaign {
          color: #ef4444;
        }
      }
    }
  }

  // Schedule-time credit advisory. Amber, not red: nothing is broken and nothing is blocked —
  // the practice is being told a later send may stop partway, so they can act now. Lives inside
  // the scoped block so it cannot leak to any other page.
  .schedule-credit-warning {
    display: flex;
    gap: 0.65rem;
    align-items: flex-start;
    padding: 0.75rem 0.9rem;
    border: 1px solid #f5c26b;
    border-left: 4px solid #e8a33d;
    border-radius: 6px;
    background: #fff8ec;
    color: #7a4b10;
    font-size: 0.82rem;
    line-height: 1.45;

    .fa-exclamation-triangle { color: #e8a33d; margin-top: 0.15rem; flex: 0 0 auto; }

    .scw-title { font-weight: 700; margin-bottom: 0.2rem; }

    .scw-line {
      display: block;
      em { font-style: normal; opacity: 0.85; }
    }

    .scw-action { margin-top: 0.35rem; opacity: 0.95; }
  }

  .schedule-options {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;

    .schedule-option {
      flex: 1;
      min-width: 180px;
      padding: 1rem;
      border: 2px solid #e2e8f0;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: center;

      &:hover {
        border-color: #cbd5e0;
      }

      &.selected {
        border-color: #547fff;
        background: rgba(84, 127, 255, 0.05);
      }

      .option-icon {
        font-size: 1.5rem;
        color: #547fff;
        margin-bottom: 0.5rem;
      }

      strong {
        display: block;
        color: #2d3748;
        margin-bottom: 4px;
      }

      p {
        font-size: 0.8rem;
        color: #718096;
      }
    }
  }

  .summary-card {
    background: #f8f9fa;
    border: 1px solid #e9ecef;

    .summary-item {
      label {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #718096;
        margin-bottom: 4px;
      }

      p {
        margin-bottom: 0;
        font-weight: 500;
        color: #2d3748;
      }

      .content-preview {
        padding: 0.75rem;
        background: white;
        border-radius: 6px;
        border: 1px solid #e2e8f0;
        font-size: 0.9rem;
        color: #4a5568;
        white-space: pre-wrap;
        max-height: 100px;
        overflow-y: auto;
      }
    }
  }

  // Email subject line enhancements
  .subject-line-wrapper {
    .subject-input {
      border-radius: 6px 0 0 6px;
    }
  }

  .emoji-dropdown {
    ::v-deep .dropdown-menu {
      padding: 0.5rem;
      min-width: 280px;
    }
  }

  .emoji-picker-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 4px;
    padding: 0.5rem;

    .emoji-item {
      font-size: 1.25rem;
      padding: 4px;
      cursor: pointer;
      text-align: center;
      border-radius: 4px;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: #e2e8f0;
      }
    }
  }

  .ai-subject-generator {
    .btn {
      border-radius: 6px;
    }
  }

  // Preview text section
  .preview-text-section {
    .preview-text-header {
      display: flex;
      align-items: center;
      font-weight: 500;
      color: #4a5568;
      padding: 0.5rem 0;

      &:hover {
        color: #2d3748;
      }
    }

    .preview-text-input {
      border-radius: 6px;
    }
  }

  // Tracking options section
  .tracking-section {
    .tracking-header {
      display: flex;
      align-items: center;
      font-weight: 500;
      color: #4a5568;
      padding: 0.5rem 0;

      &:hover {
        color: #2d3748;
      }
    }

    .tracking-options-card {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 8px;

      .tracking-option {
        .custom-switch {
          padding-left: 2.5rem;
        }
      }
    }
  }

  // Perfect timing section
  .perfect-timing-section {
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e2e8f0;

    .custom-switch {
      padding-left: 2.5rem;
    }
  }

  // Send test section
  .send-test-section {
    display: flex;
    align-items: center;
    padding: 1rem;
    background: #eef2ff;
    border-radius: 8px;
    border: 1px solid #c7d2fe;
  }

  .wizard-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 1.5rem;
    margin-top: 1.5rem;
    border-top: 1px solid #e9ecef;

    .footer-right {
      display: flex;
      align-items: center;
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .create-campaign-modal {
    .wizard-progress {
      flex-wrap: wrap;
      gap: 0.5rem;

      .progress-step {
        flex: none;
        
        &:not(:last-child)::after {
          display: none;
        }

        .step-label {
          display: none;
        }
      }
    }

    .campaign-type-selector,
    .schedule-options {
      flex-direction: column;

      .type-option,
      .schedule-option {
        min-width: 100%;
      }
    }
  }

  .all-active-filter-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    margin-bottom: 0.375rem;
    display: block;
  }

  .all-active-radio-group {
    display: flex;
    gap: 16px;

    > label + label {
      margin-left: 16px;
    }
  }

  .all-active-radio-option {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 0.875rem;
    color: #374151;
    margin-bottom: 0;

    input[type="radio"] {
      width: 16px;
      height: 16px;
      margin: 0;
      cursor: pointer;
      accent-color: #10b981;
    }
  }

  // SMS Insert dropdown options
  .sms-insert-option {
    padding: 4px 0;
    cursor: pointer;
    font-size: 0.85rem;
    color: #374151;
    border-radius: 4px;
    &:hover {
      color: #10b981;
    }
  }


  // Email template HTML preview
  .email-template-preview {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px;
    background: #fafafa;
  }

  .email-html-preview {
    max-height: 400px;
    overflow-y: auto;
    font-size: 0.9rem;
    line-height: 1.6;
    img {
      max-width: 100%;
      height: auto;
    }
  }

  .review-email-preview {
    padding: 0.75rem;
    background: white;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    max-height: 500px;
    overflow-y: auto;
  }

  // Queued confirmation screen
  .sent-confirmation {
    max-width: 480px;
    margin: 0 auto;
  }
}
</style>

<!-- Unscoped styles for wiki template cards (b-modal portals outside component scope) -->
<style lang="scss">
.wiki-template-card {
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    border-left-color: #3b82f6;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
    transform: translateY(-1px);
  }

  &:hover .wiki-template-card-name {
    color: #1d4ed8;
  }

  &:hover .wiki-template-card-hint {
    opacity: 1;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(59, 130, 246, 0.2);
    background: #dbeafe;
  }
}

.wiki-template-card-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1e293b;
  transition: color 0.2s ease;
}

.wiki-template-card-hint {
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-size: 0.7rem;
  color: #3b82f6;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.wiki-tag-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.7rem;
  font-weight: 500;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  white-space: nowrap;
}
</style>
